// Tasklio AI — bilingual UI support (English + Spanish).
//
// Design choices (deliberately scoped so we don't break anything):
//   - This dictionary covers ONLY the labels listed in the bilingual spec —
//     navbar, dashboard cards, settings, lead detail key labels, common
//     buttons, empty states. Long-form content (Terms body, Privacy body,
//     Pricing marketing copy) stays in English on purpose.
//   - Brand names ("Tasklio AI", "Stripe") are NEVER translated.
//   - User content (lead names, addresses, messages, business names,
//     AI-generated replies/quotes) is NEVER translated by this layer.
//   - If a key is missing in `es`, we fall back to the English string —
//     so adding a new label without a Spanish translation can never crash.

export const STRINGS = {
  en: {
    // Navbar
    "nav.dashboard": "Dashboard",
    "nav.billing": "Billing",
    "nav.pricing": "Pricing",
    "nav.settings": "Settings",
    "nav.admin": "Admin",
    "nav.logout": "Logout",
    "nav.login": "Log in",
    "nav.startTrial": "Start free 30-day trial",
    "nav.goDashboard": "Go to dashboard",

    // Landing page
    "landing.heroEyebrow": "AI for cleaning businesses",
    "landing.heroTitle": "Run your cleaning business — ",
    "landing.heroTitleAccent": "all in one place.",
    "landing.heroBody": "Tasklio replies to leads with AI, sends quotes in seconds, tracks every job, and keeps your revenue and expenses organized — from first message to year-end summary.",
    "landing.ctaSeePricing": "See pricing",
    "landing.ctaWatchDemo": "Watch demo",
    "landing.trust14Days": "30-Day Free Trial",
    "landing.trust2Min": "No Contract Required",
    "landing.trustSms": "English & Spanish",
    "landing.heroLangBadge": "English & Spanish Supported",
    // New demo-first hero
    "landing.heroSubhead": "Reply faster, send quotes in seconds, automate follow-ups, request Google reviews, and book more jobs.",
    "landing.heroPositioning": "Built specifically for cleaning businesses.",
    "landing.ctaScheduleDemo": "Book a Free Demo",
    "landing.ctaStartTrial": "Start Free 30-Day Trial",
    "landing.demoCardEyebrow": "Live Demo",
    "landing.demoCardTitle": "Book a Free Demo",
    "landing.demoCardBody": "See exactly how Tasklio works for your cleaning business.",
    "landing.demoCard.aiReplies": "AI Replies",
    "landing.demoCard.aiQuotes": "AI Quotes",
    "landing.demoCard.bookingPage": "Customer Booking Page",
    "landing.demoCard.followUps": "Follow-Ups & Reviews",
    "landing.demoCard.crm": "CRM & Pipeline",
    "landing.demoCard.liveQa": "Live Q&A",
    "landing.demoCard.bilingual": "English & Spanish Supported",
    "landing.demoCardSocialContext": "Designed for solo cleaners and growing cleaning teams.",
    "landing.demoCardCta": "Book My Free Demo",
    "landing.demoCardFinePrint": "15-minute live demo · no credit card required",
    "landing.showcaseEyebrow": "Product",
    "landing.showcaseTitle": "See Tasklio in Action",
    "landing.showcaseSubtitle": "Everything your cleaning business needs in one place.",
    "landing.showcase.dashboardTitle": "Dashboard",
    "landing.showcase.dashboardBody": "Track leads, jobs, revenue, and business performance from one place.",
    "landing.showcase.calendarTitle": "Calendar",
    "landing.showcase.calendarBody": "Every booked job automatically appears on your schedule.",
    "landing.showcase.financialsTitle": "Financials",
    "landing.showcase.financialsBody": "Monitor revenue, expenses, and profit without spreadsheets.",
    "landing.showcase.quotesTitle": "AI Quotes",
    "landing.showcase.quotesBody": "Generate professional quotes in seconds and send them online.",
    "landing.showcase.growTitle": "Grow Your Business",
    "landing.showcase.growBody": "Create review responses, referral campaigns, Google posts, and slow-season marketing ideas with AI.",
    "landing.heroTag.dashboard": "All Leads & Jobs",
    "landing.heroTag.calendar": "Auto Scheduling",
    "landing.heroTag.financials": "Revenue Tracking",
    "landing.heroTag.quotes": "AI Quotes",
    "landing.heroTag.grow": "Business Growth",
    "landing.socialEyebrow": "Built for cleaning owners who do it all",
    "landing.socialQuote": "“Replies, quotes, follow-ups, and my books — finally one place instead of five.”",
    "landing.socialAttribution": "— Cleaning business owner",
    "landing.socialTrust": "No contracts • Cancel anytime • Free 30-day trial",
    "landing.howItWorks": "How it works",
    "landing.howItWorksTitle": "From first lead to year-end summary — without juggling tabs.",
    "landing.step1": "A customer message lands in Tasklio",
    "landing.step2": "AI drafts a reply or quote in your voice",
    "landing.step3": "You book the job — and the numbers track themselves",
    "landing.testimonialsTitle": "Trusted by cleaning pros running smarter, not harder",
    "landing.testimonialsBody": "From solo cleaners to growing teams — Tasklio helps you reply faster, quote smarter, follow up automatically, and see exactly where the money's going.",
    "landing.testimonialsStat": "Most owners reply in under 2 minutes and start booking more jobs within the first week.",
    "landing.trustNoContracts": "No contracts",
    "landing.trustCancel": "Cancel anytime",
    "landing.trustStripe": "Secure payments via Stripe",
    "landing.featureCaptureTitle": "Every lead, every channel — one inbox.",
    "landing.featureCaptureBody": "Share your booking link on Instagram, Google, or your website. Inquiries from DMs, calls, and forms all land in one place — with status tracking from new to closed.",
    "landing.featureReplyTitle": "AI replies and quotes — in your voice.",
    "landing.featureReplyBody": "One click drafts a warm reply or a polished quote in English or Spanish. Sound professional in seconds and beat slower competitors every time.",
    "landing.featureWinTitle": "Follow-ups, reviews & rebookings — handled.",
    "landing.featureWinBody": "Tasklio nudges quiet leads, asks happy customers for reviews, and brings past clients back — so jobs keep flowing without manual chasing.",
    "landing.featureQuoteTitle": "Revenue & expenses, organized.",
    "landing.featureQuoteBody": "Track earned revenue from closed jobs, log expenses by category, and export a clean year-end summary for your bookkeeper — all built in.",
    "landing.statResponseTitle": "Reply in under 60 seconds",
    "landing.statResponseBody": "From inquiry to drafted reply.",
    "landing.statBookingsTitle": "3× more bookings",
    "landing.statBookingsBody": "When you respond in under 5 minutes.",
    "landing.statSaveTitle": "One app instead of five",
    "landing.statSaveBody": "Replies, quotes, follow-ups, reviews, and books — together.",

    // FAQ section on the landing page. Question keys mirror those in
    // /components/app/LandingFAQ.jsx — keep in sync when adding/removing.
    "faq.title": "Everything You Need to Know",
    "faq.subtitle": "Answers to the most common questions about Tasklio AI, pricing, scheduling, quotes, and business growth.",
    "faq.q.what": "What is Tasklio AI?",
    "faq.a.what": "Tasklio AI is an all-in-one platform built for cleaning business owners. Manage leads, create AI-assisted quotes and replies, share your booking page, organize jobs, follow up with customers, request reviews, and track your business—all in one place.",
    "faq.q.cost": "How much does Tasklio AI cost?",
    "faq.a.cost": "Tasklio AI costs $39 per month after your free trial. Your subscription includes access to the platform's available features, including Find a Cleaner listing eligibility. No annual contract is required.",
    "faq.q.trial": "Is the 30-day free trial really free?",
    "faq.a.trial": "Yes! You can try Tasklio AI for 30 days with no credit card required. Explore the platform and its features before deciding whether to subscribe.",
    "faq.q.noWebsite": "Do I need a website to receive cleaning requests?",
    "faq.a.noWebsite": "No. Tasklio gives your business its own shareable Customer Booking Page, where potential customers can request cleaning quotes. Share the link on social media, your Google Business Profile, or directly with customers.",
    "faq.q.existingLeads": "Can I use Tasklio with leads I already receive?",
    "faq.a.existingLeads": "Absolutely. Add and manage leads from Instagram, Facebook, phone calls, text messages, WhatsApp, email, referrals, and other sources in one place.",
    "faq.q.quotes": "Can Tasklio generate cleaning quotes for me?",
    "faq.a.quotes": "Yes. Tasklio's AI Quote feature helps prepare cleaning price estimates based on job details. You can review and edit each quote before sending it to your customer.",
    "faq.q.acceptOnline": "Can customers accept quotes online?",
    "faq.a.acceptOnline": "Yes. Share a professional quote link that customers can open and accept from their phone or computer.",
    "faq.q.afterAccept": "What happens after a customer accepts a quote?",
    "faq.a.afterAccept": "Tasklio automatically marks the lead as booked and adds the job to your built-in calendar.",
    "faq.q.aiReply": "How does AI Reply work?",
    "faq.a.aiReply": "AI Reply helps you draft professional responses to customer inquiries in seconds. You review the suggested response and decide what to send. Tasklio does not automatically reply to customer messages on your behalf.",
    "faq.q.notifications": "How will I know when a new customer requests a quote?",
    "faq.a.notifications": "Tasklio can notify you by email when a new request arrives. Optional SMS alerts are available for business owners who provide the required consent. SMS notifications are sent to the business owner, not automatically to customers.",
    "faq.q.findCleaner": "What is Find a Cleaner?",
    "faq.a.findCleaner": "Find a Cleaner is Tasklio's public search directory that helps potential customers discover participating cleaning businesses by ZIP code. Eligible businesses can choose to appear in search results by enabling their listing and selecting a service radius. Listing is included with an active trial or subscription; customer inquiries are not guaranteed.",
    "faq.q.recurring": "Can Tasklio manage recurring cleaning appointments?",
    "faq.a.recurring": "Yes. Manage one-time, weekly, biweekly, and monthly cleaning jobs using Tasklio's built-in scheduling and calendar tools.",
    "faq.q.googleReviews": "Can I request Google reviews from customers?",
    "faq.a.googleReviews": "Yes. Tasklio helps you send review requests with a direct link to your Google Business Profile review page.",
    "faq.q.pastCustomers": "Can Tasklio help me reconnect with past customers?",
    "faq.a.pastCustomers": "Yes. Use rebooking reminders and follow-up tools to stay in touch with previous customers and encourage repeat business.",
    "faq.q.finance": "Can I track expenses, revenue, and profits?",
    "faq.a.finance": "Yes. Tasklio includes financial tracking tools to help you record expenses, monitor revenue, and understand your business performance.",
    "faq.q.growSection": "What is the Grow Your Business section?",
    "faq.a.growSection": "It's a collection of AI-powered tools for writing review replies, Google Business Profile posts, before-and-after content, referral campaigns, and slow-season promotions.",
    "faq.q.spanish": "Does Tasklio support Spanish?",
    "faq.a.spanish": "Yes. Tasklio supports English and Spanish, including tools for creating customer-facing content in Spanish when needed.",
    "faq.q.noApp": "Do I need to install an app or special software?",
    "faq.a.noApp": "No. Tasklio works in your web browser on your phone, tablet, or computer. No app download or special software is required.",
    "faq.q.solo": "Is Tasklio suitable for solo cleaners and small teams?",
    "faq.a.solo": "Yes. Tasklio is designed for independent cleaners, family-run businesses, and small cleaning teams that want to stay organized and grow.",
    "faq.q.trialEnd": "What happens when my free trial ends? Can I cancel anytime?",
    "faq.a.trialEnd": "If you don't subscribe after your 30-day trial, you won't be automatically charged. Some features become restricted, including access to new lead contact details, until you upgrade. If you subscribe, you can manage or cancel your subscription through the billing portal.",
    "landing.ctaTitle": "Run your cleaning business smarter — starting today.",
    "landing.ctaBody": "Join cleaning owners using Tasklio to reply faster, quote in seconds, follow up automatically, and keep their numbers organized.",
    "landing.ctaCreate": "Start free",
    "landing.ctaViewPricing": "View pricing",

    // Footer
    "footer.home": "Home",
    "footer.pricing": "Pricing",
    "footer.login": "Login",
    "footer.signup": "Sign up",
    "footer.privacy": "Privacy",
    "footer.terms": "Terms",
    "footer.smsTerms": "SMS Terms",
    "footer.support": "Contact support",
    "footer.copyright": (year) => `© ${year} Tasklio AI`,

    // Pricing page
    "pricing.eyebrow": "Pricing",
    "pricing.title": "One simple plan. Run your whole cleaning business.",
    "pricing.subtitle": "Built for solo cleaners and growing teams. Try free for 30 days — no credit card required.",
    "pricing.tierName": "Tasklio Pro",
    "pricing.earlyAccess": "Early access pricing",
    "pricing.active": "Active",
    "pricing.perMonth": "/ month",
    "pricing.trialLine": "30-day free trial — no credit card required",
    "pricing.feature1": "AI-written replies in your voice — in seconds",
    "pricing.feature2": "AI-generated quotes with price ranges & line items",
    "pricing.feature3": "Bilingual replies and quotes (English + Spanish)",
    "pricing.feature4": "Unlimited leads in one organized inbox",
    "pricing.feature5": "Full CRM: pipeline tracking from new → closed",
    "pricing.feature6": "Automatic follow-ups so quiet leads come back",
    "pricing.feature7": "One-tap Google review requests",
    "pricing.feature8": "Rebooking reminders for past customers",
    "pricing.feature10": "Business Insights dashboard with revenue tracking",
    "pricing.feature11": "Expense tracking by category (gas, supplies, ads & more)",
    "pricing.feature12": "Year-end summary and CSV exports for your bookkeeper",
    "pricing.feature13": "Your own Customer Booking Page — share anywhere",
    "pricing.feature14": "Find a Cleaner directory listing — included at no extra cost",
    "pricing.feature9": "Cancel anytime",
    "pricing.ctaManage": "Manage billing",
    "pricing.ctaUpgradeNow": "Run your business smarter",
    "pricing.ctaUpgradeUnlock": "Run your business smarter",
    "pricing.ctaStartTrial": "Start free 30-day trial",
    "pricing.trustHeading": "Built for solo cleaners and growing teams.",
    "pricing.trustLine1": "Replies, quotes, follow-ups, reviews, revenue, and expenses — together.",
    "pricing.trustLine2": "One booked cleaning job can cover months of Tasklio.",
    "pricing.portalNote": "Cancel, update card, or view invoices in your secure Stripe portal.",
    "pricing.staleBilling": "Your old test billing record is no longer valid. Please subscribe again on live Stripe.",
    "pricing.trialActiveNote": "You're currently on your 30-day free trial.",
    "pricing.subText": "No credit card required • Cancel anytime",
    "pricing.secureLine": "Secure payment via Stripe • 30-day free trial • Cancel anytime",
    "pricing.noRefunds": "All payments are final and non-refundable.",
    "pricing.cancelPolicy": "You can cancel anytime. Access continues until the end of your billing period.",
    "pricing.needHelp": "Need help?",
    "pricing.contactLink": "Contact support@tasklio.co",
    "pricing.benefitCancelTitle": "Cancel anytime",
    "pricing.benefitCancelBody": "No long-term contracts. Cancel from your dashboard.",
    "pricing.benefitTrialTitle": "30-day free trial",
    "pricing.benefitTrialBody": "Try every feature free — no credit card required.",
    "pricing.benefitBuiltTitle": "All-in-one workflow",
    "pricing.benefitBuiltBody": "Replies, quotes, CRM, follow-ups, revenue & expenses — together.",

    // Legal pages (Terms + Privacy)
    "legal.eyebrow": "Legal",
    "legal.lastUpdated": "Last updated:",
    "terms.title": "Terms and Conditions",
    "terms.intro": "Welcome to Tasklio AI (\"Tasklio\", \"we\", \"our\"). By creating an account or using the service, you agree to these Terms and Conditions.",
    "terms.subTitle": "Subscription and billing",
    "terms.subBody1": "Tasklio is offered as a monthly subscription. Your card is charged on the same day each month for the upcoming service period.",
    "terms.subBody2": "You can cancel your subscription at any time from your account. After cancellation you will continue to have access until the end of your current billing period; no further charges will be made.",
    "terms.subBody3": "We may offer an introductory free trial. If you do not subscribe before the trial ends, your account will move to a limited state — no charge is made.",
    "terms.refundsTitle": "Refunds",
    "terms.refundsHeadline": "All payments are final and non-refundable.",
    "terms.refundsBody": "We do not provide refunds, prorated refunds, or credit for partial billing periods, unused features, or downgrades. If you cancel mid-cycle you keep access until the end of the period you've already paid for.",
    "terms.guaranteeTitle": "No guarantee of results",
    "terms.guaranteeBody1": "Tasklio provides AI-assisted suggestions including draft replies, price estimates, and lead scoring. These outputs are guidance only and are not guaranteed to be accurate, optimal, or to win you the job.",
    "terms.guaranteeBody2": "Outcomes depend on many factors outside our control — your pricing, your customer's decision, market conditions, and your follow-up. We make no warranty or representation about earnings, conversion rate, or business results.",
    "terms.respTitle": "Your responsibility",
    "terms.respBody1": "You are solely responsible for the use of any AI-generated content. Before sending a reply or quote to a customer, you should review it for accuracy, tone, and pricing, and edit it as needed. You are responsible for honoring any prices, commitments, or promises you communicate to your customers.",
    "terms.respBody2": "You agree to use Tasklio only for lawful business purposes and not to send spam, harass customers, or violate applicable telecommunications and email regulations (including TCPA and CAN-SPAM in the United States).",
    "terms.smsTitle": "SMS notifications",
    "terms.smsBody1": "Tasklio AI operates a single transactional SMS program for business owners (Tasklio account holders). Business owners who save a phone number under Settings → SMS Notification Phone may receive transactional SMS notifications when a new lead is submitted or created in their Tasklio account. Customers who submit a request through a Tasklio public lead form do not receive SMS messages from Tasklio AI.",
    "terms.smsBody2": "Message frequency varies based on lead volume. Message and data rates may apply. No marketing or promotional SMS messages are sent through this program. SMS opt-in data is never sold, rented, or shared with third parties for marketing purposes.",
    "terms.smsBodyNotRequired1": "Consent to receive SMS notifications is not required as a condition of purchase, subscription, or use of Tasklio AI.",
    "terms.smsBodyNotRequired2": "Users may use Tasklio AI without providing a phone number.",
    "terms.smsBodyNotRequired3": "SMS notifications are optional and can be disabled at any time by removing the phone number from Settings or by replying STOP.",
    "terms.smsPhoneOptional": "Providing a phone number is optional.",
    "terms.smsMobileNotShared": "Mobile information will not be shared with third parties or affiliates for marketing or promotional purposes.",
    "terms.smsProgramUrlsTitle": "SMS Program URLs",
    "terms.smsProgramDetailsLabel": "SMS Program Details:",
    "terms.smsPolicyLinkLabel": "Privacy Policy:",
    "terms.smsTermsLinkLabel": "Terms and Conditions:",
    "terms.smsBody3Pre": "To get help, reply",
    "terms.smsBody3Mid": "To stop receiving messages at any time, reply",
    // Customer-facing SMS consent — A2P 10DLC compliant. Keep wording in
    // lockstep with the lead form's SMS disclosure copy so reviewers
    // see the same workflow described everywhere.
    "terms.smsConsentTitle": "Owner SMS Notifications",
    "terms.smsConsentBody": "Business owners opt in by entering and saving a mobile number under Settings → SMS Notification Phone inside their Tasklio account. Saving the number is the opt-in event. Messages are limited to a single transactional notification sent when a new lead is submitted or created in the Tasklio account. Message frequency varies based on lead volume. Message and data rates may apply. Reply STOP to opt out at any time, or HELP for help. No marketing or promotional messages are sent through this program. SMS opt-in data is never sold, rented, or shared with third parties for marketing purposes.",
    "terms.changesTitle": "Changes to these terms",
    "terms.changesBody": "We may update these Terms from time to time. Continued use of Tasklio after we publish an update means you accept the revised Terms.",
    "terms.contactTitle": "Contact",
    "terms.contactBody": "Questions about these Terms? Email us at",
    "privacy.title": "Privacy Policy",
    "privacy.intro": "Tasklio AI (\"Tasklio\", \"we\", \"our\") respects your privacy. This policy explains what data we collect, how we use it, and what we never do with it.",
    "privacy.collectTitle": "What we collect",
    "privacy.collectIntro": "When you sign up and use Tasklio we collect the following:",
    "privacy.collect1": "Your name and business name",
    "privacy.collect2": "Your email address (used to log in and receive notifications)",
    "privacy.collect3": "Your phone number (optional, used for SMS notifications)",
    "privacy.collect4": "Lead information your customers submit through your public form — name, email, phone, service type, message, and any other details they provide",
    "privacy.collect5": "Basic billing metadata from Stripe when you subscribe",
    "privacy.cardNote": "We do not store your card number. Payments are processed by Stripe.",
    "privacy.useTitle": "How we use your data",
    "privacy.useBody1": "We use the information above solely to provide the service. That means: showing you your leads, sending you new-lead notifications, generating AI draft replies and price estimates, processing your subscription, and providing customer support.",
    "privacy.useBody2": "Aggregate, anonymized usage data may be used to improve Tasklio (e.g. which features are most used). This data cannot be used to identify you.",
    // SMS notifications disclosure — body string must remain verbatim (regulatory).
    "privacy.smsNotificationsTitle": "SMS Communications",
    "privacy.smsNotificationsBody": "Tasklio AI sends only one category of SMS message, strictly transactional. BUSINESS OWNER NEW-LEAD NOTIFICATIONS: If you are a Tasklio account holder (cleaning business owner) and you enter and save a phone number under Settings → SMS Notification Phone, you may receive a transactional SMS notification when a new lead is submitted or created in your Tasklio account. Customers who submit requests through a Tasklio public lead form do not receive SMS messages from Tasklio AI. Message frequency varies based on lead volume. Message and data rates may apply. You can reply STOP to any message to opt out at any time, or HELP for help. We do not send marketing or promotional SMS messages.",
    "privacy.smsUnifiedNotShared": "Mobile information and SMS consent data will not be shared, sold, rented, transferred, or disclosed to third parties or affiliates for marketing or promotional purposes.",
    "privacy.smsMobileNotShared": "Mobile information will not be shared with third parties or affiliates for marketing or promotional purposes.",
    "privacy.smsConsentDataUsage": "All SMS consent information, opt-in records, and phone numbers collected for SMS notifications are used solely to provide the requested transactional SMS service.",
    "privacy.smsConsentNeverSold": "SMS consent data is never sold, rented, shared, transferred, or disclosed to third parties for marketing purposes.",
    "privacy.smsConsentSectionTitle": "SMS Consent",
    "privacy.smsConsentIntro": "SMS consent is collected only when a Tasklio account holder voluntarily enters and saves their own mobile phone number under Settings → SMS Notification Phone.",
    "privacy.smsConsentSavingIs": "Saving the phone number is the opt-in event.",
    "privacy.smsConsentUsage": "Consent is used solely to deliver requested transactional SMS notifications related to the Tasklio account.",
    "privacy.smsConsentFrequency": "Message frequency varies based on lead volume.",
    "privacy.smsConsentRates": "Message and data rates may apply.",
    "privacy.smsConsentStop": "Reply STOP to opt out.",
    "privacy.smsConsentHelp": "Reply HELP for help.",
    "privacy.smsConsentNotShared": "SMS consent is not shared with third parties or affiliates for marketing purposes.",
    "privacy.smsConsentDataUsageAlt": "SMS consent data, opt-in records, and phone numbers collected for SMS notifications are used solely for delivery of requested transactional SMS notifications.",
    "privacy.smsConsentNeverSoldAlt": "SMS consent data is never sold, rented, transferred, disclosed, or shared for marketing purposes.",
    "privacy.smsProgramUrlsTitle": "SMS Program URLs",
    "privacy.smsProgramDetailsLabel": "SMS Program Details:",
    "privacy.smsPolicyLinkLabel": "Privacy Policy:",
    "privacy.smsTermsLinkLabel": "Terms and Conditions:",
    "privacy.dontTitle": "What we don't do",
    "privacy.dontBody1": "We do not sell, rent, or share your personal data — or your customers' lead information — with third parties for marketing purposes. Ever.",
    "privacy.dontBody2": "We share data with our subprocessors only to deliver the service: Stripe (payments), Resend (email delivery), Twilio (SMS delivery), MongoDB Atlas (database hosting), and the AI provider that powers reply/quote generation. Each is bound by their own privacy terms.",
    "privacy.choicesTitle": "Your choices",
    "privacy.choicesBody1Pre": "You can edit your profile and remove your phone number at any time from",
    "privacy.choicesBody1Post": ". To delete your account entirely, email us — see below.",
    "privacy.choicesBody2": "To stop SMS notifications, reply STOP to any message you receive from Tasklio.",
    "privacy.contactTitle": "Contact",
    "privacy.contactBody": "Questions, deletion requests, or concerns? Email us at",
    "privacy.settingsWord": "Settings",

    // Auth pages (Register / Login)
    "auth.registerSidebarTitle": "Run your cleaning business in one place.",
    "auth.registerSidebarPoint1": "AI replies, quotes & follow-ups — in seconds",
    "auth.registerSidebarPoint2": "Track leads, revenue & expenses together",
    "auth.registerSidebarPoint3": "Free 30-day trial — no credit card required",
    "auth.registerTitle": "Start organizing your cleaning business today",
    "auth.registerSubtitle": "No complicated setup · Works on desktop & mobile · Takes under 60 seconds.",
    "auth.businessName": "Business name",
    "auth.businessPlaceholder": "Sparkle Cleaning Co.",
    "auth.email": "Email",
    "auth.emailPlaceholder": "you@business.com",
    "auth.password": "Password",
    "auth.passwordPlaceholder": "At least 6 characters",
    "auth.createAccount": "Create account",
    "auth.accountCreatedToast": "Account created!",
    "auth.registrationFailed": "Registration failed",
    "auth.alreadyHaveAccount": "Already have an account?",
    "auth.logInLink": "Log in",
    "auth.loginSidebarTitle": "Welcome back. Your leads are waiting.",
    "auth.loginSidebarBody": "Pick up right where you left off — respond to new enquiries, send quotes, and book jobs.",
    "auth.loginTitle": "Log in",
    "auth.loginSubtitle": "Enter your email to access your dashboard.",
    "auth.forgotPassword": "Forgot password?",
    "auth.loginButton": "Log in",
    "auth.welcomeBackToast": "Welcome back!",
    "auth.loginFailed": "Login failed",
    "auth.noAccount": "Don't have an account?",
    "auth.signUpLink": "Sign up free",

    // Forgot password
    "forgot.sidebarTitle": "Forgot your password? No problem.",
    "forgot.sidebarBody": "Enter your email and we'll send you a secure link to set a new one. The link expires in 1 hour.",
    "forgot.title": "Reset your password",
    "forgot.subtitle": "Enter the email associated with your account and we'll send a reset link.",
    "forgot.submit": "Send reset link",
    "forgot.backToLogin": "Back to log in",
    "forgot.successTitle": "Check your inbox",
    "forgot.successBody": "If an account exists, we sent a reset link. The link expires in 1 hour.",
    "forgot.spamHint": "Check your spam folder if you don't see it within a few minutes.",
    "forgot.tryAnother": "Try a different email",

    // Reset password
    "reset.sidebarTitle": "Set a new password",
    "reset.sidebarBody": "Choose a strong password you'll remember. Once it's saved, log back in and pick up right where you left off.",
    "reset.successTitle": "Password updated",
    "reset.successBody": "Your password has been changed. You can now log in with your new password.",
    "reset.goToLogin": "Go to log in",
    "reset.missingTitle": "Reset link missing",
    "reset.missingBody": "This page needs a valid reset link from the email we sent. Request a new link to continue.",
    "reset.requestNew": "Request a new reset link",
    "reset.title": "Reset password",
    "reset.subtitle": "Choose a new password for your account.",
    "reset.newPassword": "New password",
    "reset.confirmPassword": "Confirm password",
    "reset.confirmPlaceholder": "Re-enter password",
    "reset.errorTooShort": "Password must be at least 6 characters.",
    "reset.errorMismatch": "Passwords don't match.",
    "reset.errorGeneric": "Could not reset password. The link may be invalid or expired.",
    "reset.submit": "Reset password",

    // Common buttons
    "common.save": "Save",
    "common.saveChanges": "Save changes",
    "common.cancel": "Cancel",
    "common.copy": "Copy",
    "common.regenerate": "Regenerate",
    "common.loading": "Loading…",
    "common.english": "English",
    "common.spanish": "Spanish",

    // Language toggles
    "lang.appLanguage": "App language",
    "lang.customerMessageLanguage": "Customer message language",
    "lang.replyLanguage": "Reply language",
    "lang.quoteLanguage": "Quote language",

    // Dashboard
    "dash.greeting": "Hi,",
    "dash.subtitle": "Here's what's happening with your leads. Need help?",
    "dash.contactSupport": "Contact support",
    "dash.addLead": "Add lead",
    "dash.potentialRevenue": "Potential revenue",
    "dash.fromActiveLeads": (count) =>
      `From ${count} active ${count === 1 ? "lead" : "leads"} — respond fast →`,
    "dash.noActiveLeadsYet": "No active leads yet — share your Customer Booking Page to get started",
    "dash.statTotalLeads": "Total leads",
    "dash.statNew": "New",
    "dash.statBooked": "Booked",
    "dash.statLast7": "Last 7 days",
    "dash.leadTemperature": "Lead temperature",
    "dash.leadTemperatureHelp": "Prioritise your hottest leads first.",
    "dash.scoreHot": "Hot",
    "dash.scoreWarm": "Warm",
    "dash.scoreCold": "Cold",
    "dash.returningCustomerBadge": "Returning Customer",
    "dash.smsPhoneSaved": "SMS phone saved",
    "dash.smsPhoneCleared": "SMS phone cleared",
    "dash.smsPhoneSaveError": "Could not save phone",
    "dash.hotReady": "Ready to book now",
    "dash.leadProgress": "Lead progress",
    "dash.leadProgressEmpty": "Your conversion funnel will appear here.",
    "dash.percentBooked": (p) => `${p}% of leads booked`,
    "dash.progressTotal": "Total leads",
    "dash.progressContacted": "Contacted",
    "dash.progressQuoted": "Quoted",
    "dash.progressBooked": "Booked",
    "dash.missedBannerOne": "You have 1 lead waiting — don't miss them",
    "dash.missedBannerMany": (n) => `You have ${n} leads waiting — don't miss them`,
    "dash.missedHelper": "Hot leads cool fast. Respond now to win the booking.",
    "dash.respondNow": "Respond now",
    "dash.filterAll": "All",
    "dash.filterNew": "New",
    "dash.filterContacted": "Contacted",
    "dash.filterQuoted": "Quoted",
    "dash.filterBooked": "Booked",
    "dash.filterClosed": "Closed",
    "dash.trialActiveTitle": (days) => `30-day free trial active — ${days} ${days === 1 ? "day" : "days"} left`,
    "dash.trialActiveBody": "You have full access to every feature. Upgrade anytime to keep going after the trial.",
    "dash.upgradeNow": "Upgrade now",
    "dash.trialExpiredTitle": "Your trial ended — upgrade to unlock new lead details.",
    "dash.trialExpiredBody": "You're still receiving SMS and email alerts. Upgrade to view contact info and keep winning jobs.",
    "dash.trialExpiredCta": "Upgrade to unlock leads",
    "dash.publicLinkTitle": "Your public lead form link",
    "dash.publicLinkPreview": "Preview",
    "dash.publicLinkCopy": "Copy link",
    "dash.publicLinkCopied": "Copied",
    // Customer Booking Page card (top-of-dashboard onboarding) — primary
    // onboarding surface for new owners. Keep the wording focused on
    // "customers submit through this page", NOT "owner adds a lead".
    "dash.bookingPageEyebrow": "Customer Booking Page",
    "dash.bookingPageTitle": "Share Your Customer Booking Page",
    "dash.bookingPageDescription": "This is the page your customers use to request quotes and book cleaning services.",
    "dash.bookingPageCopy": "Copy Page Link",
    "dash.bookingPageCopied": "Page link copied",
    "dash.bookingPageOpen": "Open Page",
    "dash.bookingPageHelper": "Share it on your Instagram bio, Google Business Profile, Facebook page, website, text messages, or anywhere customers contact you.",
    "dash.firstBookingTitle": "Get Your First Booking",
    "dash.onbProgress": (n) => `${n} of 4 completed`,
    "dash.onbStep1": "Prepare your Customer Booking Page",
    "dash.onbStep1Action": "Set up page",
    "dash.onbStep2": "Get your first lead — share your page or add an existing customer",
    "dash.onbStep2Action": "Add a lead",
    "dash.onbStep3": "Create and send your first quote",
    "dash.onbStep3Hint": "Open a lead to prepare a quote — you review it before sending.",
    "dash.onbStep4": "Get your first booking",
    "dash.onbStep4Hint": "When a customer accepts your quote, the job is booked automatically.",
    "dash.onbAllDone": "All set — you booked your first job! Keep sharing your page to reach more customers.",
    // Manual Lead Entry — secondary surface at the bottom of the dashboard
    // for exception flows (phone calls, referrals, walk-ins, repeat
    // customers, leads received outside the Customer Booking Page).
    "dash.manualLeadEntryTitle": "Manual Lead Entry",
    "dash.manualLeadEntryDescription": "Use this when a customer contacts you by phone, referral, walk-in, or any method outside your Customer Booking Page.",
    "dash.manualLeadTitle": "Manually Add a Customer",
    "dash.manualLeadHelper": "Use this form for phone calls, referrals, walk-ins, repeat customers, or anyone who did not use your Customer Booking Page.",
    "dash.smsPhoneTitle": "SMS notification phone",
    "dash.smsPhoneHelp": "We'll text you when a new lead comes in. E.164 format, e.g. +14049177181.",
    "dash.smsPhoneSave": "Save",
    "dash.searchPlaceholder": "Search by name, email, phone...",
    "dash.tableHeaderName": "Name",
    "dash.tableHeaderContact": "Contact",
    "dash.tableHeaderService": "Service",
    "dash.tableHeaderStatus": "Status",
    "dash.tableHeaderScore": "Score",
    "dash.tableHeaderAdded": "Added",
    "dash.noLeadsYet": "Ready when your first lead lands",
    "dash.noLeadsHint": "Share your public booking link or add a lead manually — Tasklio drafts the reply, you book the job.",
    "dash.missedBadge": "Missed",

    // Lead detail
    "lead.replySection": "AI reply draft",
    "lead.quoteSection": "AI price estimate",
    "lead.generateReply": "Generate reply",
    "lead.generateQuote": "Generate quote",
    "lead.copyReply": "Copy reply",
    "lead.copyQuote": "Copy quote",
    "lead.regenerate": "Regenerate",
    "lead.step1": "AI reply",
    "lead.step2": "Price estimate",
    "lead.step3": "Send quote",
    "lead.backToDashboard": "Back to dashboard",
    "lead.statusHelper": "Mark as booked once the customer confirms.",
    "lead.closeIn3Steps": "Close this lead in 3 steps",
    "lead.closerStep1": "Reply to the customer",
    "lead.closerStep2": "Generate price estimate",
    "lead.closerStep3": "Send quote link",
    "lead.closeIn3StepsHelp": "Tasklio prepares the messages, but you stay in control. Nothing is sent automatically unless you copy or share it.",
    "lead.markContacted": "Mark as contacted",
    "lead.markQuoted": "Mark as quoted",
    "lead.copyReplyQuote": "Copy reply + quote",
    "lead.addToCalendar": "Add to calendar",
    "lead.scoreTitle": "Lead score",
    "lead.scoreSubtitle": "AI-assessed buyer intent",
    "lead.scoreNotAnalyzed": "Not analyzed yet.",
    "lead.scoreThisLead": "Score this lead",
    "lead.winProbability": "win probability",
    "lead.detailsTitle": "Lead details",
    "lead.notProvided": "Not provided",
    "lead.fieldSource": "Source",
    "lead.fieldSubmittedAt": "Date submitted",
    "lead.fieldEmail": "Email",
    "lead.fieldPhone": "Phone",
    "lead.fieldAddress": "Address",
    "lead.fieldHomeSize": "Home size",
    "lead.fieldService": "Service",
    "lead.fieldPreferredDate": "Preferred date",
    "lead.fieldFrequency": "Frequency",
    "lead.fieldFrequencyValue.one_time": "One-Time",
    "lead.fieldFrequencyValue.weekly": "Weekly",
    "lead.fieldFrequencyValue.biweekly": "Bi-Weekly",
    "lead.fieldFrequencyValue.monthly": "Monthly",
    "lead.fieldFrequencyValue.daily": "Daily",
    "lead.theirMessage": "Their message",
    "lead.serviceDetailsTitle": "Service details",
    "lead.sd.bedrooms": "Bedrooms",
    "lead.sd.bathrooms": "Bathrooms",
    "lead.sd.square_footage": "Square footage",
    "lead.sd.num_kitchens": "Kitchens",
    "lead.sd.restrooms": "Restrooms",
    "lead.sd.frequency": "Frequency",
    "lead.sd.pets": "Pets",
    "lead.sd.home_condition": "Home condition",
    "lead.sd.property_empty": "Property empty",
    "lead.sd.inside_appliances": "Inside appliances",
    "lead.sd.business_type": "Business type",
    "lead.sd.laundry_needed": "Laundry needed",
    "lead.sd.restocking_needed": "Restocking needed",
    "lead.sd.shower_tub": "Shower/tub cleaning",
    "lead.sd.hard_water_grout": "Hard water / grout",
    "lead.sd.oven_inside": "Inside oven",
    "lead.sd.fridge_inside": "Inside refrigerator",
    "lead.sd.cabinets_inside": "Inside cabinets",
    "lead.sd.construction_type": "Construction type",
    "lead.sd.heavy_dust": "Heavy dust",
    "lead.sd.debris_removal": "Debris removal",
    // Enum value labels rendered inside the Service Details panel.
    "lead.sd.val.one_time": "One-time",
    "lead.sd.val.daily": "Daily",
    "lead.sd.val.weekly": "Weekly",
    "lead.sd.val.biweekly": "Bi-weekly",
    "lead.sd.val.monthly": "Monthly",
    "lead.sd.val.light": "Light",
    "lead.sd.val.average": "Average",
    "lead.sd.val.heavy": "Heavy",
    "lead.sd.val.office": "Office",
    "lead.sd.val.retail": "Retail",
    "lead.sd.val.medical": "Medical",
    "lead.sd.val.restaurant": "Restaurant",
    "lead.sd.val.other": "Other",
    "lead.sd.val.renovation": "Renovation",
    "lead.sd.val.new_build": "New build",
    "lead.sd.val.remodel": "Remodel",
    "lead.translateToSpanish": "Translate to Spanish",
    "lead.translateToEnglish": "Translate to English",
    "lead.showOriginal": "Show original",
    "lead.translatedNote": "Translated by AI — original preserved",
    "lead.notesTitle": "Internal notes",
    "lead.notesPlaceholder": "Private notes about this lead…",
    "lead.jobValueTitle": "Estimated job value (USD)",
    "lead.jobValueHelper": "Used for revenue insights only. Estimates aren't accounting figures.",
    "lead.jobValueBookedAt": "Booked on",
    // AI v2 — reply tone selector + customer context + option cards
    "lead.replyToneLabel": "Tone",
    "lead.toneFriendly": "Friendly",
    "lead.toneProfessional": "Professional",
    "lead.tonePremium": "Premium",
    "lead.toneFastClosing": "Fast-closing",
    "lead.customerContextLabel": "Customer's latest reply (optional)",
    "lead.customerContextPlaceholder": "Paste the customer's most recent message here…",
    "lead.customerContextHelper": "Paste the customer's latest reply, then click Regenerate to draft a new response. Nothing is sent automatically.",
    "lead.customerContextExamplesLabel": "Quick examples:",
    "lead.customerContextEx1": "That's too expensive",
    "lead.customerContextEx2": "Can you do Friday?",
    "lead.customerContextEx3": "What's included?",
    "lead.customerContextEx4": "Can you lower the price?",
    "lead.replyOptionsLabel": "Choose a reply",
    // AI v2 — structured quote
    "lead.quoteSuggestedPrice": "Suggested price",
    "lead.quoteRangeLabel": "Range:",
    "lead.quoteCustomerOnlyHelper": "Only the suggested price is shown to the customer. The range, confidence, reasoning, and assumptions stay private.",
    "lead.quoteEstimated": "estimated",
    "lead.quoteConfidence": "Confidence",
    "lead.quoteWhyThisPrice": "Why this price",
    // Visibility system — small chips that tell the owner at a glance
    // which parts of the AI quote are shown to customers and which stay
    // internal. Used inside the StructuredQuoteView card.
    "lead.vis.summaryTitle": "Visibility guide",
    "lead.vis.summaryCustomer": "Customer Visible — appears on the public quote",
    "lead.vis.summaryOwner": "Owner Only — internal information only",
    "lead.vis.customer": "Customer Visible",
    "lead.vis.owner": "Owner Only",
    "lead.vis.customerHelper": "Shown to the customer.",
    "lead.vis.ownerHelper": "Visible only to you. Never shown to customers.",
    "lead.upsellTitle": "Upsell Opportunities",
    "lead.upsellHelper": "Suggested opportunities to increase quote value. Never shown to customers.",
    "lead.quoteIncluded": "What's included",
    "lead.quoteAddOns": "Optional add-ons",
    "lead.quoteAssumptions": "Assumptions",
    "lead.quoteCustomerMessage": "Customer-facing message",
    "lead.quoteCustomerHelper": "Copy / edit before sending. The owner stays in control.",
    "lead.quoteNextBestAction": "Next best action",
    "lead.saveNotes": "Save notes",
    "lead.saved": "Saved",
    "lead.aiReplyTitle": "Step 1 — Reply to the customer",
    "lead.aiReplySubtitle": "Use this message to respond quickly. Nothing is sent automatically.",
    "lead.aiReplyHelper": "Copy this reply and send it through Instagram, SMS, WhatsApp, Facebook, email, or wherever the customer contacted you.",
    "lead.aiReplyHelperSub": "Claude Sonnet 4.5 — edit before sending.",
    "lead.replyPlaceholderBusy": "Drafting reply…",
    "lead.replyPlaceholderIdle": "Click \"Generate reply\" to draft an email response, or type your own.",
    "lead.saveEdits": "Save edits",
    "lead.aiQuoteTitle": "Step 2 — Generate a price estimate",
    "lead.aiQuoteSubtitle": "Create a suggested price and customer-ready quote details.",
    "lead.aiQuoteHelper": "This is not sent to the customer until you create and share a quote link.",
    "lead.aiQuoteHelperSub": "Generate a quick estimate in seconds.",
    "lead.quotePlaceholderBusy": "Calculating estimate…",
    "lead.quotePlaceholderIdle": "Click \"Generate quote\" for an AI estimate, or write your own.",
    "lead.quoteLinkTitle": "Step 3 — Send Quote to Customer",
    "lead.quoteLinkSubtitle": "Send your quote message to the customer. Once accepted, Tasklio automatically marks the lead as booked and updates your workflow.",
    "lead.createQuoteLink": "Create quote link",
    "lead.previewLink": "Preview",
    "lead.copyQuoteLink": "Copy quote link",
    "lead.copyQuoteMessage": "Copy quote message",
    "lead.copyLinkOnly": "Copy link only",
    "lead.quoteMessageCopied": "Quote message copied",
    "lead.linkCopied": "Link copied",
    "lead.copyError": "Couldn't copy to clipboard",
    "lead.copySpanishLink": "Copy Spanish link",
    "lead.sendViaWhatsApp": "Send via WhatsApp",
    "lead.sendViaEmail": "Send via Email",
    "lead.newLink": "Generate a new link",
    "lead.closeTipTitle": "Send this to close the job",
    "lead.closeTipSub": "When accepted, the lead is automatically marked as booked",
    "lead.closeTipWin": "Most customers book the first business that sends a quote",
    "lead.step3InstructionsTitle": "Quote Ready to Send",
    "lead.step3InstructionsStep1": "Click \"Copy Quote Message\"",
    "lead.step3InstructionsStep2": "Send it to your customer",
    "lead.step3InstructionsStep3": "Customer reviews and accepts the quote online",
    "lead.step3InstructionsStep4": "Tasklio automatically marks the lead as booked",
    // Step 2 — editable Suggested Price + Customer Message
    "lead.quotePriceEditLabel": "Edit suggested price",
    "lead.quotePriceEditHelper": "This becomes the customer-visible quote amount.",
    "lead.quotePriceUpdated": "Suggested price updated",
    "lead.quotePriceUpdateError": "Couldn't update the price",
    "lead.quoteMessageUpdated": "Customer message updated",
    "lead.quoteMessageUpdateError": "Couldn't save the message",
    "lead.quoteCustomerMessagePlaceholder": "Write the message your customer will see on the quote page.",
    "lead.quoteSaving": "Saving…",
    // Inline workflow guidance cards (sit directly under each AI section so
    // the next-best-action is always visible where the user is working).
    "lead.guide.replyReady": "Reply ready",
    "lead.guide.replyReadyBody": "Send this message first if you want to start the conversation. If you're ready to send pricing, generate a price estimate next.",
    "lead.guide.replyReadyCta": "Generate Price Estimate",
    "lead.guide.priceReady": "Estimate ready",
    "lead.guide.priceReadyBody": "Review the estimate, then create a quote link so your customer can accept online.",
    "lead.guide.priceReadyCta": "Create Quote Link",
    "lead.guide.quoteReady": "Quote ready to send",
    "lead.guide.quoteReadyBody": "Copy the quote message and send it to your customer. The message includes the quote link.",
    "lead.guide.quoteReadyCta": "Copy Quote Message",
    // Recommended Next Action — single-CTA guidance card under Lead Score.
    // One title/body/cta triplet per state id (see recommendedNextAction).
    "lead.nextAction.eyebrow": "Recommended Next Action",
    "lead.nextAction.generateReply.title": "Generate a reply and contact the customer.",
    "lead.nextAction.generateReply.body": "Tasklio will draft a professional response in seconds.",
    "lead.nextAction.generateReply.cta": "Generate Reply",
    "lead.nextAction.sendReply.title": "Send the reply to the customer and continue the conversation.",
    "lead.nextAction.sendReply.body": "Copy this reply and paste it where the customer contacted you.",
    "lead.nextAction.sendReply.cta": "Copy Reply",
    "lead.nextAction.interest.title": "Customer appears interested. Generate and send a quote.",
    "lead.nextAction.interest.body": "Their last message looks like scheduling or pricing intent — strike while it's hot.",
    "lead.nextAction.interest.cta": "Generate Quote",
    "lead.nextAction.question.title": "Customer needs a response. Reply first.",
    "lead.nextAction.question.body": "Their latest message requires a response before moving forward with pricing or booking.",
    "lead.nextAction.question.cta": "Regenerate Reply",
    "lead.nextAction.followup.title": "Customer answered your question. Continue the conversation.",
    "lead.nextAction.followup.body": "Their reply looks like an answer to your last question — draft a follow-up before sending a quote.",
    "lead.nextAction.followup.cta": "Regenerate Reply",
    "lead.nextAction.sendQuote.title": "Send the quote to the customer.",
    "lead.nextAction.sendQuote.body": "Create a public quote link so the customer can review and accept online.",
    "lead.nextAction.sendQuote.cta": "Create Quote Link",
    "lead.nextAction.waiting.title": "Waiting for customer decision.",
    "lead.nextAction.waiting.body": "Follow up in 24–48 hours if no response.",
    "lead.nextAction.waiting.cta": "",
    "lead.nextAction.booked.title": "Booking secured.",
    "lead.nextAction.booked.body": "This appointment has already been added to your Tasklio calendar.",
    "lead.nextAction.booked.cta": "View in Calendar",
    "lead.nextAction.review.title": "Request a Google review.",
    "lead.nextAction.review.body": "A short review from a happy customer compounds into future leads.",
    "lead.nextAction.review.cta": "Generate Review Request",
    "lead.missedTitle": "This lead has not been contacted for over 2 hours",
    "lead.missedBody": (ago) => `Came in ${ago} ago — reply now to keep the booking warm.`,
    "lead.paywallTitle": "You're one step away from a new paying customer",
    "lead.paywallBody": "Customers requesting quotes typically book within hours. First to respond usually wins.",
    "lead.paywallCta": "Unlock lead for $39/month",
    "lead.paywallTrust": "Cancel anytime • Unlimited leads • No contracts",
    "lead.deleteConfirmTitle": "Delete this lead?",
    "lead.deleteConfirmBody": "This can't be undone.",
    "lead.deleteCta": "Delete",
    "lead.statusNew": "New",
    "lead.statusContacted": "Contacted",
    "lead.statusQuoted": "Quoted",
    "lead.statusBooked": "Booked",
    "lead.statusClosed": "Closed",
    "lead.statusCancelled": "Cancelled",
    "lead.statusClosedHelper": "Closed jobs count toward estimated revenue.",
    "lead.statusBookedHelper": "Booked jobs are scheduled but not counted as revenue until closed.",
    // Cancel-reason dropdown (Lead Detail)
    "lead.cancelReason.label": "Cancel reason (optional)",
    "lead.cancelReason.placeholder": "Why was this cancelled?",
    "lead.cancelReason.too_expensive": "Too expensive",
    "lead.cancelReason.no_response": "No response from customer",
    "lead.cancelReason.went_with_competitor": "Went with a competitor",
    "lead.cancelReason.rescheduled": "Rescheduled / postponed",
    "lead.cancelReason.changed_mind": "Customer changed their mind",
    "lead.cancelReason.other": "Other",
    "lead.cancelledOn": "Cancelled on",
    "lead.cancelledWarning": "This lead is marked as cancelled. It will still appear in your analytics but is excluded from revenue and potential revenue.",
    // Archive / Restore (replaces Delete for non-admins)
    "lead.archive": "Archive lead",
    "lead.archiveConfirm": "Archive this lead? It will be hidden from your dashboard but kept in analytics and reports.",
    "lead.archived": "Archived",
    "lead.archiveSuccess": "Lead archived",
    "lead.archiveError": "Couldn't archive this lead",
    "lead.restore": "Restore lead",
    "lead.restoreSuccess": "Lead restored",
    "lead.restoreError": "Couldn't restore this lead",
    "lead.permanentDelete": "Permanently delete",
    "lead.permanentDeleteConfirm": "Permanently delete this lead? This cannot be undone.",
    "lead.permanentDeleteAdminOnly": "Only platform admins can permanently delete leads.",
    // Business Insights — new revenue-integrity cards
    "bi.lostRevenue": "Lost revenue",
    "bi.cancelledJobs": "Cancelled jobs",
    "bi.completionRate": "Completion rate",
    "bi.bookingConversion": "Booking conversion",
    "bi.potentialRevenue": "Potential revenue",
    "bi.potentialRevenueHint": "Quoted + booked, not yet earned.",
    "bi.revenueHint": "Closed jobs only.",
    "bi.pipelineHealth": "Pipeline health",
    // Dashboard archive tab
    "dash.tabActive": "Active",
    "dash.tabArchived": "Archived",
    "dash.archivedEmpty": "No archived leads yet.",
    "lead.waNoPhone": "Add a phone number to this lead to send via WhatsApp",
    "lead.waReplyNoDraft": "Generate or write a reply first",
    "lead.waReplyNoQuote": "Create a quote link first — WhatsApp message includes the reply + quote link",
    "lead.waNoQuote": "Generate or write a quote first.",
    "lead.waReplyReadyWithLink": "Send the reply, price estimate, and quote link via WhatsApp",
    "lead.waReplyReadyNoLink": "Send the reply and price estimate via WhatsApp",
    "lead.waQuoteEnTitle": "Send the quote link via WhatsApp (English message)",
    "lead.waQuoteEsTitle": "Send the quote link via WhatsApp (Spanish message)",
    "lead.copyEsLinkTitle": "Share a link that opens in Spanish for your customer",

    // Settings
    "settings.title": "Settings",
    "settings.subtitle": "Manage your business profile, account, and password.",
    "settings.businessProfile": "Business profile",
    "settings.businessProfileHelp": "Shown on your public booking link and lead notifications.",
    "settings.businessName": "Business name",
    "settings.smsPhone": "SMS notification phone",
    "settings.smsPhoneHelper": "By entering your mobile phone number and checking the SMS consent box above, you agree to receive transactional SMS notifications from Tasklio AI related to your account, including new lead alerts. Message frequency varies based on lead activity. Message and data rates may apply. Reply STOP to opt out or HELP for assistance. No marketing or promotional SMS messages are ever sent. Leave the phone number blank if you do not wish to receive SMS notifications.",
    "settings.smsConsentCheckboxLabel": "I agree to receive transactional SMS notifications from Tasklio AI about my account, including new lead alerts. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. No marketing or promotional messages.",
    "settings.smsConsentRequired": "Please check the SMS consent box to enable SMS notifications.",
    "settings.account": "Account",
    "settings.accountHelp": "Your sign-in email.",
    "settings.email": "Email",
    "settings.emailContactNote": "Contact",
    "settings.changePassword": "Change password",
    "settings.changePasswordHelp": "Use a strong password you don't use anywhere else.",
    "settings.currentPassword": "Current password",
    "settings.newPassword": "New password",
    "settings.confirmPassword": "Confirm new password",
    "settings.updatePassword": "Update password",
    "settings.billing": "Billing & Subscription",
    "settings.billingHelp": "Manage your Tasklio Pro plan, payment method, or upgrade.",
    "settings.manageBilling": "Manage billing & subscription",
    "settings.preferences": "Preferences",
    "settings.preferencesHelp": "Choose the language used across the Tasklio AI interface.",

    // Settings (i18n fix — Google Review + Customer Reply blocks)
    "settings.googleReviewLink": "Google Review Link",
    "settings.googleReviewHelper": "Add your Google review link so Tasklio can send review request emails.",
    "settings.customerReplyEmail": "Customer Reply Email",
    "settings.customerReplyHelper": "Customer replies to review and rebooking emails will go to this address.",
    "settings.customerReplyCurrent": "Currently replies go to:",
    "settings.timezone": "Business timezone",
    "settings.timezoneHelper": "All appointment dates and times are displayed in this timezone — never the browser's. Used in the calendar, public quotes, AI replies, and email reminders.",
    "settings.invalidUrl": "Invalid URL — must start with http:// or https://",
    "settings.invalidEmail": "Invalid email",
    "settings.profileUpdated": "Settings saved",
    "settings.backToDashboard": "← Back to dashboard",
    "settings.passwordMinLength": "At least 6 characters",
    "settings.businessNameEmpty": "Business name cannot be empty.",
    "settings.profileUpdateError": "Could not update profile. Please try again.",
    "settings.passwordMin": "New password must be at least 6 characters.",
    "settings.passwordMismatch": "New passwords don't match.",
    "settings.passwordSame": "New password must be different from your current password.",
    "settings.passwordUpdated": "Password updated successfully.",
    "settings.passwordUpdatedToast": "Password updated",
    "settings.passwordUpdateError": "Could not update password.",
    "settings.billingOnPro": "You're currently on Tasklio Pro.",
    "settings.billingOpenHint": "Open your billing page to upgrade or manage your plan.",

    // Dashboard — Rebooking Opportunities card
    "dash.growthOpportunity": "Growth Opportunity",
    "dash.rebookingOpportunities": "Rebooking Opportunities",
    "dash.rebookSummary": "Past customers may be ready to book again.",
    "dash.rebookSubcopy": "Send a quick follow-up to bring repeat customers back.",
    "dash.rebookReadyPill": "ready",
    "dash.rebookOneDay": "1 day since booking",
    "dash.rebookManyDays": (n) => `${n} days since booking`,
    "dash.sendFollowUp": "Send Follow-up",
    "dash.previewEmail": "Preview email",

    // Business Insights widget
    "bi.title": "Business Insights",
    "bi.subtitle": "Estimated values",
    "bi.thisMonth": "This month",
    "bi.last30Days": "Last 30 days",
    "bi.excludesNoValue": "Excludes leads without a job value",
    "bi.bookedJobs": "Booked jobs",
    "bi.estimatedRevenue": "Estimated revenue",
    "bi.returningCustomers": "Returning customers",
    "bi.reviewsSent": "Review requests sent",
    "bi.leadsCreated": "Leads created",
    "bi.quotesSent": "Quotes sent",
    "bi.rebooksSent": "Rebook reminders sent",
    "bi.returningLeads": "Returning customer leads",
    "bi.averageJobValue": "Average job value",
    "bi.estimatedReturningRevenue": "Estimated returning revenue",
    "bi.allEstimates": "All values are estimates · USD",
    "bi.bookingsLive": "Bookings live",
    "bi.returningPositive": "They came back — nice.",
    "bi.estimateLabel": "Estimate",
    "bi.valuedHint": (valued, total) => `${valued} of ${total} jobs have a value`,
    "bi.emptyTitle": "Your booked jobs will turn into revenue insights",
    "bi.emptyBody": "Once you mark a lead as booked and add an estimated job value, we'll roll up monthly revenue, returning customers, and growth trends right here.",

    // Business Insights — per-card "Learn more" modal
    "bi.modal.currentValue": "Current value",
    "bi.modal.explain": "What this means",
    "bi.modal.tip": "Tip",
    "bi.modal.close": "Close",
    "bi.modal.learnMore": "Learn more",
    "bi.bookedJobs.explain": "This shows how many leads were marked as booked this month.",
    "bi.bookedJobs.tip": "Mark leads as booked when a customer confirms the job.",
    "bi.estimatedRevenue.explain": "This estimates revenue from booked jobs that have an estimated job value.",
    "bi.estimatedRevenue.tip": "Add an estimated job value on each booked lead to make this number more useful.",
    "bi.returningCustomers.explain": "This shows booked leads that came back through a rebooking reminder or returning-customer link.",
    "bi.returningCustomers.tip": "Use Rebooking Reminders to bring past customers back.",
    "bi.reviewsSent.explain": "This shows how many Google review request emails were sent this month.",
    "bi.reviewsSent.tip": "After a job is booked or completed, send a review request to grow your reputation.",
    "bi.leadsCreated.explain": "This shows how many leads were created in the last 30 days.",
    "bi.leadsCreated.tip": "Share your public lead form link on Instagram, Facebook, Google Business Profile, and messages.",
    "bi.quotesSent.explain": "This counts quote links or quote actions created in the last 30 days.",
    "bi.quotesSent.tip": "Send quotes quickly while the customer is still interested.",
    "bi.rebooksSent.explain": "This shows how many rebooking follow-up emails were sent in the last 30 days.",
    "bi.rebooksSent.tip": "Follow up with past customers after 30 days to bring repeat jobs back.",
    "bi.returningLeads.explain": "This shows leads created by customers coming back through a rebooking link.",
    "bi.returningLeads.tip": "Rebooking reminders can turn past customers into new leads automatically.",
    "bi.closedJobs": "Closed jobs",
    "bi.closedJobs.explain": "This shows jobs marked as closed/completed this month.",
    "bi.closedJobs.tip": "Mark a job as closed when the work is finished and paid, so your revenue reports stay accurate.",
    "bi.potentialRevenue.explain": "Estimated value from quoted and booked jobs that are not closed yet.",
    "bi.potentialRevenue.tip": "Follow up quickly with quoted or booked customers to turn pipeline into revenue.",
    "bi.cancelledJobs.explain": "Leads marked as cancelled.",
    "bi.cancelledJobs.tip": "Track cancellation reasons to understand why jobs are lost.",
    "bi.lostRevenue.explain": "Estimated value from cancelled leads with a job value.",
    "bi.lostRevenue.tip": "Use cancellation reasons to improve pricing, timing, or follow-up.",
    "bi.completionRate.explain": "How many booked jobs became closed/completed jobs.",
    "bi.completionRate.tip": "Mark jobs as closed after they are completed so revenue stays accurate.",

    // Public lead form (/l/:ownerId)
    "public.form.eyebrow": "Request a quote",
    "public.form.title": (biz) => `Book a cleaning with ${biz}`,
    "public.form.subtitle": "Fill in a few details and you'll hear back with a quote fast.",
    "public.form.name": "Your name",
    "public.form.email": "Email",
    "public.form.phone": "Phone (optional)",
    "public.form.address": "Address",
    "public.form.streetAddress": "Street Address",
    "public.form.streetAddressPlaceholder": "Enter your street address",
    "public.form.addressLine2": "Apt / Unit / Suite (optional)",
    "public.form.addressLine2Placeholder": "Optional",
    "public.form.city": "City",
    "public.form.cityPlaceholder": "Enter city",
    "public.form.state": "State",
    "public.form.statePlaceholder": "Select state",
    "public.form.zipCode": "ZIP Code",
    "public.form.zipCodePlaceholder": "ZIP code",
    "public.form.addressRequired": "Please complete Street Address, City, State, and ZIP.",
    "public.form.serviceTypeRequired": "Please select a service type.",
    "public.form.serviceDetailsRequired": "Please complete all service detail fields.",
    "public.form.err.name": "Please enter your name.",
    "footer.smsOptIn": "SMS Opt-In",
    "register.smsNotice": "Tasklio AI only sends transactional SMS to business owners (you), never to your customers. See ",
    "register.smsNoticeLink": "SMS Opt-In",
    "public.form.err.streetAddress": "Street address is required.",
    "public.form.err.city": "City is required.",
    "public.form.err.state": "Please select a state.",
    "public.form.err.zip": "Please enter a valid ZIP code.",
    "public.form.err.zipInvalid": "Please enter a valid ZIP code.",
    "public.form.err.serviceType": "Please select a service type.",
    "public.form.err.sd.bedrooms": "Bedrooms is required.",
    "public.form.err.sd.bathrooms": "Bathrooms is required.",
    "public.form.err.sd.num_kitchens": "Please enter the number of kitchens.",
    "public.form.err.sd.restrooms": "Please enter the number of restrooms.",
    "public.form.err.sd.square_footage": "Please enter the square footage.",
    "public.form.err.sd.frequency": "Please select a service frequency.",
    "public.form.err.sd.pets": "Please select an option.",
    "public.form.err.sd.home_condition": "Please select a home condition.",
    "public.form.err.sd.property_empty": "Please select an option.",
    "public.form.err.sd.inside_appliances": "Please select an option.",
    "public.form.err.sd.business_type": "Please select a business type.",
    "public.form.err.sd.laundry_needed": "Please select an option.",
    "public.form.err.sd.restocking_needed": "Please select an option.",
    "public.form.err.sd.shower_tub": "Please select an option.",
    "public.form.err.sd.hard_water_grout": "Please select an option.",
    "public.form.err.sd.oven_inside": "Please select an option.",
    "public.form.err.sd.fridge_inside": "Please select an option.",
    "public.form.err.sd.cabinets_inside": "Please select an option.",
    "public.form.err.sd.construction_type": "Please select a construction type.",
    "public.form.err.sd.heavy_dust": "Please select an option.",
    "public.form.err.sd.debris_removal": "Please select an option.",
    "public.form.err.sd.generic": "This field is required.",
    "public.form.zipInvalid": "Invalid ZIP code. Use 5 digits (12345) or ZIP+4 (12345-6789).",
    "public.form.service": "Service type",
    "public.form.servicePlaceholder": "What do you need?",
    "public.form.homeSize": "Home size",
    "public.form.homeSizePlaceholder": "e.g. 3 bed / 2 bath",
    "public.form.preferredDate": "Preferred date",
    "public.form.notes": "Anything we should know?",
    "public.form.notesPlaceholder": "Tell us about your space, pets, special requests…",
    "public.form.submit": "Request my quote",
    "public.form.submitError": "Couldn't submit. Please try again.",
    "public.form.tooManyRequests": "We've received several requests in a short time. Please wait a moment and try again.",
    "public.form.thanks": (name) => `Thanks, ${name || "there"}!`,
    "public.form.thanksBody": (biz) => `${biz} will get back to you very shortly with a quote and available times.`,
    "public.form.invalidTitle": "Link not found",
    "public.form.invalidBody": "This booking link isn't valid.",
    "public.form.gotoTasklio": "Go to Tasklio AI →",
    "public.form.poweredBy": "Powered by",
    "public.form.requiredHint": "Required",
    // Smart conditional fields — appear based on the selected service type.
    // Keep wording compact for mobile. Header is shown once above the fields.
    "public.form.smartHeader": "A few quick details",
    "public.form.smartHint": "Optional, but helps us prepare a more accurate quote.",
    "public.form.bedrooms": "Bedrooms",
    "public.form.bathrooms": "Bathrooms",
    "public.form.sqft": "Approximate square footage",
    "public.form.sqftPh": "e.g. 1500",
    "public.form.numKitchens": "Number of kitchens",
    "public.form.numBathrooms": "Number of bathrooms",
    "public.form.numRestrooms": "Number of restrooms",
    "public.form.frequency": "Service frequency",
    "public.form.freq.one_time": "One-time",
    "public.form.freq.daily": "Daily",
    "public.form.freq.weekly": "Weekly",
    "public.form.freq.biweekly": "Bi-weekly",
    "public.form.freq.monthly": "Monthly",
    "public.form.pets": "Pets in the home?",
    "public.form.yes": "Yes",
    "public.form.no": "No",
    "public.form.homeCondition": "Home condition",
    "public.form.cond.light": "Light",
    "public.form.cond.average": "Average",
    "public.form.cond.heavy": "Heavy",
    "public.form.propertyEmpty": "Is the property empty?",
    "public.form.insideAppliances": "Inside appliance cleaning?",
    "public.form.businessType": "Business type",
    "public.form.biz.office": "Office",
    "public.form.biz.retail": "Retail",
    "public.form.biz.medical": "Medical",
    "public.form.biz.restaurant": "Restaurant",
    "public.form.biz.other": "Other",
    "public.form.laundryNeeded": "Laundry needed?",
    "public.form.restockingNeeded": "Restocking needed?",
    "public.form.showerTub": "Shower / tub cleaning?",
    "public.form.hardWater": "Hard water / grout buildup?",
    "public.form.ovenInside": "Inside oven cleaning?",
    "public.form.fridgeInside": "Inside refrigerator cleaning?",
    "public.form.cabinetsInside": "Inside cabinets cleaning?",
    "public.form.constructionType": "Type of construction",
    "public.form.const.renovation": "Renovation",
    "public.form.const.new_build": "New build",
    "public.form.const.remodel": "Remodel",
    "public.form.const.other": "Other",
    "public.form.heavyDust": "Heavy construction dust?",
    "public.form.debrisRemoval": "Debris removal needed?",
    "public.form.selectPh": "Select…",

    // SMS disclosure under the public form submit button (TCPA / A2P 10DLC
    // compliance — exact wording, do not paraphrase). The customer SMS
    // consent checkbox was removed: Tasklio AI does NOT send any SMS to
    // customers. The cleaning business contacts the customer directly.
    "public.form.smsDisclosure": "Submitting this form sends your service request directly to the cleaning business above. They will reach out to you using the contact details you provided. Tasklio AI does not send marketing or promotional SMS messages to customers.",
    "public.form.legalAgreement": "By submitting this form, you agree to our ",
    "public.form.privacyPolicyLink": "Privacy Policy",
    "public.form.legalAgreementAnd": " and ",
    "public.form.termsOfServiceLink": "Terms of Service",
    "public.form.welcomeBack": "Welcome back! Request your next cleaning below.",

    // Public quote page (/q/:quoteId)
    "public.quote.eyebrow": "Your quote",
    "public.quote.greeting": (name) => `Hi, ${name}`,
    "public.quote.intro": "has prepared the following quote for you.",
    "public.quote.serviceLabel": "Service",
    "public.quote.scheduledLabel": "Scheduled appointment",
    "public.quote.scheduledAt": "at",
    "public.quote.frequencyLabel": "Frequency",
    "public.quote.frequencyLong.weekly": "Weekly Recurring Service",
    "public.quote.frequencyLong.biweekly": "Bi-Weekly Recurring Service",
    "public.quote.frequencyLong.monthly": "Monthly Recurring Service",
    "public.quote.frequencyLong.daily": "Daily Recurring Service",
    "public.quote.priceLabel": "Price estimate",
    "public.quote.estimatedQuoteLabel": "Estimated Quote",
    "public.quote.includedLabel": "Service Highlights",
    "public.quote.addOnsLabel": "Optional Add-ons",
    "public.quote.messageFrom": (biz) => `Message from ${biz}`,
    "public.quote.disclaimer": "Estimate prepared using the information provided.",
    "public.quote.accept": "Accept quote",
    "public.quote.acceptError": "Couldn't accept quote",
    "public.quote.previewTitle": "Preview Mode",
    "public.quote.previewBody": "This is exactly how your customer will see the quote.\n\nClose this tab, click 'Copy Quote Message', and send it through your preferred communication channel.",
    "public.quote.previewDisabledTitle": "Accept Quote is disabled in preview",
    "public.quote.previewDisabledBody": "This button is active only for customers. Close this tab and send the quote message to your customer from the lead page.",
    "public.quote.acceptedTitle": "🎉 Quote accepted",
    "public.quote.acceptedHeadline": "Your appointment is booked.",
    "public.quote.acceptedBody": (biz) => (
      `${biz} has been notified and your quote has been successfully accepted.`
    ),
    "public.quote.acceptedThanks": (biz) => `Thank you for choosing ${biz}.`,
    "public.quote.summaryService": "Service:",
    "public.quote.summaryDate": "Date:",
    "public.quote.summaryTime": "Time:",
    "public.quote.summaryPrice": "Price:",
    "public.quote.invalidTitle": "Quote not found",
    "public.quote.invalidBody": "This quote link isn't valid.",

    // ===================== /free landing page =====================
    "free.nav.login": "Log in",
    "free.nav.startFree": "Start free",
    "free.hero.eyebrow": "Built for cleaning businesses",
    "free.hero.headlineMain": "Never miss another",
    "free.hero.headlineAccent": "cleaning lead.",
    "free.hero.sub": "Reply faster, send quotes instantly, follow up automatically, and keep every customer organized in one place.",
    "free.hero.primaryCta": "Start free",
    "free.hero.primaryCtaAuthed": "Start free",
    "free.hero.watchDemo": "See how it works",
    "free.hero.reassurance": "Start free in under 60 seconds.",
    "free.hero.pricingNote1": "No credit card required",
    "free.hero.pricingNote2": "30-day free trial · $39/month after trial",
    "free.hero.trust1": "Built specifically for cleaning businesses",
    "free.hero.trust2": "Works with your existing leads",
    "free.hero.trust3": "English & Spanish",
    "free.hero.trust4": "No credit card required",
    "free.hero.shotAlt.dashboard": "Tasklio dashboard showing active leads, pipeline, and revenue",
    "free.hero.shotAlt.aiReply": "Tasklio lead detail showing an AI-generated reply",
    "free.hero.shotAlt.financials": "Tasklio Financials page showing revenue and expenses",
    // Product mock copy (visible in the hero illustration)
    "free.mock.newLead": "New lead",
    "free.mock.minAgo": "2 min ago",
    "free.mock.customerName": "Maria López — 3-bed move-out",
    "free.mock.customerMsg": "\"Hi, do you have Friday available?\"",
    "free.mock.aiReply": "AI reply",
    "free.mock.aiReplyText": "Hi Maria — yes, Friday 9am works for a 3-bed move-out. Quote attached.",
    "free.mock.bookedLabel": "Booked this month",
    "free.mock.bookedVs": "vs. 11 last month",
    "free.mock.stat.hot": "Hot leads",
    "free.mock.stat.quotes": "Quotes sent",
    "free.mock.stat.reviews": "Reviews",
    // Section 2 — outcomes
    "free.outcomes.eyebrow": "Why cleaning owners switch",
    "free.outcomes.title": "Less juggling. More booked jobs.",
    "free.outcome1.title": "Reply to every lead in seconds",
    "free.outcome1.body": "AI drafts a warm reply or polished quote — in English or Spanish — before the customer moves on.",
    "free.outcome2.title": "Follow up without thinking about it",
    "free.outcome2.body": "Quiet leads get gentle nudges, happy customers get review requests, and past clients get rebooking reminders — automatically.",
    "free.outcome3.title": "Know your numbers, end-to-end",
    "free.outcome3.body": "See booked revenue, log expenses by category, and download a clean year-end summary for your bookkeeper.",
    "free.outcome4.title": "One screen instead of five apps",
    "free.outcome4.body": "Leads, quotes, follow-ups, reviews, revenue, and expenses — together, no extra setup.",
    // Section 3 — features
    "free.features.eyebrow": "Everything you need",
    "free.features.title": "Replies, quotes, follow-ups, reviews — and the books.",
    "free.feature.replies.title": "Instant AI replies",
    "free.feature.replies.body": "Warm, professional drafts in seconds, in English or Spanish.",
    "free.feature.quotes.title": "AI quote generation",
    "free.feature.quotes.body": "Price ranges, line items, and a shareable quote link before the customer cools off.",
    "free.feature.followups.title": "Automatic follow-ups",
    "free.feature.followups.body": "Quiet leads get nudged and past customers get rebooking reminders — without effort.",
    "free.feature.reviews.title": "Google review requests",
    "free.feature.reviews.body": "One tap to ask happy customers for the 5-star review.",
    "free.feature.inbox.title": "Revenue & expense tracking",
    "free.feature.inbox.body": "Closed-job revenue, expenses by category, year-end exports — all built in.",
    "free.feature.bilingual.title": "English & Spanish",
    "free.feature.bilingual.body": "Switch the entire app and your AI drafts between languages in one click.",
    // Section 4 — problem / solution
    "free.problems.eyebrow": "The real problem",
    "free.problems.title": "Most cleaning companies lose jobs simply by replying too late.",
    "free.problems.1": "Missed Instagram DMs while you're on the job site.",
    "free.problems.2": "Forgotten callbacks from voicemail or texts.",
    "free.problems.3": "Delayed quote replies — customers hire someone faster.",
    "free.problems.4": "No follow-up after an estimate is sent.",
    "free.problems.5": "Disorganized customer messages across 5 different apps.",
    "free.solution.eyebrow": "How Tasklio helps",
    "free.solution.title": "Reply first. Follow up automatically. See your numbers.",
    "free.solution.body": "Tasklio gives you one dashboard for every cleaning inquiry. AI drafts a friendly reply or quote in seconds, follow-ups happen on their own, and your revenue & expenses stay organized — so you finally know where the business stands.",
    "free.solution.1": "AI replies & quotes that sound like you, in English or Spanish.",
    "free.solution.2": "Shareable quote links your customers can open on any phone.",
    "free.solution.3": "Auto follow-ups, review requests, and rebooking reminders.",
    "free.solution.4": "Closed-job revenue + categorized expenses, in one place.",
    "free.solution.5": "Year-end summary and CSV exports for your bookkeeper.",
    // Section 5 — demo
    "free.demo.eyebrow": "See it work",
    "free.demo.title": "See Tasklio in 60 seconds.",
    "free.demo.sub": "A real walkthrough — DMs and inquiries turning into booked jobs, with the books staying organized in the background.",
    "free.demo.button.title": "Watch the 60-second demo",
    "free.demo.button.sub": "Real dashboard, real replies, no slides.",
    // Section 6 — final CTA
    "free.finalCta.eyebrow": "30 days free",
    "free.finalCta.title": "Try Tasklio free for 30 days.",
    "free.finalCta.sub": "Reply faster, follow up automatically, and see exactly how your cleaning business is doing.",
    "free.finalCta.button": "Start free",
    "free.finalCta.note1": "No credit card required",
    "free.finalCta.note2": "Cancel anytime",
    // Sticky mobile CTA
    "free.sticky.cta": "Start free",
    // Footer
    "free.footer.tagline": "For cleaning businesses",
    "free.footer.privacy": "Privacy",
    "free.footer.terms": "Terms",
    "free.footer.login": "Log in",
    // Demo modal fallback
    "free.demo.fallbackTitle": "Demo video coming soon",
    "free.demo.fallbackBody": "We're putting the finishing touches on the walkthrough. The fastest way to see Tasklio in action is to start your free 30-day trial.",

    // Financials page
    "nav.financials": "Financials",
    "nav.growthAi": "Grow Your Business",
    "nav.calendar": "Calendar",

    // Tasklio Calendar
    "cal.title": "Calendar",
    "cal.subtitle": "Every booked job in one place — auto-synced from your leads.",
    "cal.viewToday": "Today",
    "cal.viewWeek": "Week",
    "cal.viewMonth": "Month",
    "cal.statusBooked": "Booked",
    "cal.statusCompleted": "Completed",
    "cal.statusCancelled": "Cancelled",
    "cal.todayLabel": "Today",
    "cal.emptyTitle": "No jobs scheduled yet",
    "cal.emptyBody": "When a lead is marked as booked, it shows up here automatically. Mark one as booked from a lead detail page to see it on your calendar.",
    "cal.noJobsToday": "No jobs scheduled for today.",
    "cal.noJobsThisWeek": "No jobs scheduled this week.",
    "cal.noJobsThisMonth": "No jobs scheduled this month.",
    "cal.prev": "Previous",
    "cal.next": "Next",
    "cal.thisMonth": "This month",
    "cal.thisWeek": "This week",
    "cal.weekOf": "Week of",
    "cal.eventDetails": "Job details",
    "cal.customer": "Customer",
    "cal.phone": "Phone",
    "cal.email": "Email",
    "cal.address": "Address",
    "cal.serviceType": "Service",
    "cal.scheduledFor": "Scheduled for",
    "cal.duration": "Duration",
    "cal.jobValue": "Estimated value",
    "cal.notes": "Notes",
    "cal.calendarNotes": "Calendar notes (gate codes, parking, supplies…)",
    "cal.calendarNotesPlaceholder": "Owner-only. Never shown to the customer.",
    "cal.editSchedule": "Edit schedule",
    "cal.dateLabel": "Date",
    "cal.timeLabel": "Time",
    "cal.frequencyLabel": "Frequency",
    "cal.durationLabel": "Duration (minutes)",
    "cal.save": "Save",
    "cal.cancel": "Cancel",
    "cal.viewLead": "View Lead",
    "cal.markCompleted": "Mark Completed",
    "cal.requestReview": "Request Review",
    "cal.addToCalendar": "Add to Calendar",
    "cal.openEvent": "Open",
    "cal.allDay": "All day",
    "cal.jobsScheduled": (n) => `${n} jobs scheduled`,
    "cal.oneJobScheduled": "1 job scheduled",
    "cal.scheduledHelper": "Default is the booking moment — pick a date and time so the job shows up where you expect on the calendar.",
    "cal.savedSchedule": "Schedule saved",
    "cal.savedScheduleErr": "Couldn't save schedule",
    "cal.timeNotSet": "Time not set",
    "cal.conflict": "Time conflict",
    "cal.conflictHelper": "Another job is scheduled at the same time. Both are kept on the calendar.",

    // Today's Jobs widget on Dashboard
    "todayJobs.title": "Today's jobs",
    "todayJobs.emptyTitle": "Nothing scheduled today",
    "todayJobs.emptyBody": "Your booked jobs for today will show up here.",
    "todayJobs.viewCalendar": "Open Calendar",
    "todayJobs.upcomingToday": (n) => `${n} upcoming today`,
    "todayJobs.completedToday": "All done for today",
    "todayJobs.minutes": "min",
    "todayJobs.countOne": "1 job today",
    "todayJobs.countMany": (n) => `${n} jobs today`,

    // Financials page
    "fin.title": "Financials",
    "fin.subtitle": "Track your earned revenue, business expenses, and estimated net — all in one place.",
    "fin.estimateBadge": "Estimates · USD · Not a tax document",
    "fin.sectionThisMonth": "This month",
    "fin.sectionYearToDate": "Year to date",
    "fin.sectionPipeline": "Pipeline",
    "fin.revenue": "Revenue",
    "fin.expenses": "Expenses",
    "fin.estimatedNet": "Estimated net",
    "fin.potentialRevenue": "Potential revenue",
    "fin.cancelledRevenue": "Cancelled revenue",
    "fin.closedJobs": "Closed jobs",
    "fin.revenueHelper": "Earned revenue from closed jobs only.",
    "fin.expensesHelper": "Manually entered business expenses.",
    "fin.netHelper": "Revenue minus expenses. Estimates only — not an accounting figure.",
    "fin.potentialHelper": "Open quoted and booked jobs with a value set.",
    "fin.cancelledHelper": "Estimated value of cancelled jobs.",
    "fin.closedJobsHelper": "Total jobs marked closed.",

    // Expense management
    "fin.expensesTitle": "Business expenses",
    "fin.expensesSubtitle": "Add your gas, supplies, ads, and other business costs to see your true picture.",
    "fin.addExpense": "Add expense",
    "fin.editExpense": "Edit expense",
    "fin.deleteExpense": "Delete",
    "fin.expenseTitle": "Title",
    "fin.expenseAmount": "Amount (USD)",
    "fin.expenseCategory": "Category",
    "fin.expenseDate": "Date",
    "fin.expenseNote": "Note (optional)",
    "fin.expenseNotePlaceholder": "e.g. Truck #1 fuel, Costco run, Facebook ads…",
    "fin.expenseTitlePlaceholder": "e.g. Gas refill, Mop bucket, Google Ads",
    "fin.save": "Save",
    "fin.cancel": "Cancel",
    "fin.saving": "Saving…",
    "fin.deleting": "Deleting…",
    "fin.confirmDelete": "Delete this expense? This can't be undone.",
    "fin.deletedToast": "Expense deleted.",
    "fin.savedToast": "Expense saved.",
    "fin.errorTitle": "Title is required.",
    "fin.errorAmount": "Amount must be greater than 0.",
    "fin.errorCategory": "Please choose a category.",
    "fin.errorDate": "Please choose a date.",
    "fin.errorGeneric": "Something went wrong. Please try again.",
    "fin.emptyTitle": "No expenses logged yet",
    "fin.emptyBody": "Add gas, supplies, or ad spend — and watch your real net update in real time.",
    "fin.tableTitle": "Title",
    "fin.tableCategory": "Category",
    "fin.tableAmount": "Amount",
    "fin.tableDate": "Date",
    "fin.tableNote": "Note",
    "fin.tableActions": "Actions",
    "fin.editLabel": "Edit",
    "fin.filterAll": "All categories",
    "fin.loading": "Loading…",

    // Categories
    "fin.cat.gas": "Gas",
    "fin.cat.supplies": "Cleaning Supplies",
    "fin.cat.ads": "Advertising",
    "fin.cat.equipment": "Equipment",
    "fin.cat.payroll": "Payroll",
    "fin.cat.insurance": "Insurance",
    "fin.cat.vehicle": "Vehicle",
    "fin.cat.software": "Software",
    "fin.cat.meals": "Meals",
    "fin.cat.other": "Other",

    // Card modals — explanations + tips for each financial metric
    "fin.expenseDateHelper": "Use the date you want this expense counted under. You can enter daily expenses, monthly totals, or yearly totals.",
    "fin.modal.revenue.explain": "Earned revenue from leads marked closed/completed. Booked jobs are not counted until they are closed.",
    "fin.modal.revenue.tip": "Mark a job as closed the moment it's done — that's when it counts toward earned revenue.",
    "fin.modal.expenses.explain": "Manually entered business expenses. These are used for organization and estimated summaries only.",
    "fin.modal.expenses.tip": "Log expenses as they happen so your estimated net stays accurate.",
    "fin.modal.net.explain": "Estimated revenue minus manually entered expenses. This is not accounting or tax advice.",
    "fin.modal.net.tip": "Keep both revenue and expenses up to date for the most useful estimate.",
    "fin.modal.potential.explain": "Estimated value from quoted and booked jobs that are not closed yet.",
    "fin.modal.potential.tip": "Convert quoted jobs into booked, then booked into closed to turn this into real revenue.",
    "fin.modal.cancelled.explain": "Estimated value from cancelled jobs with a job value.",
    "fin.modal.cancelled.tip": "Track cancellation reasons in the lead so you can spot patterns and lose fewer jobs over time.",
    "fin.modal.closed.explain": "Jobs marked closed/completed. Closed jobs count toward earned revenue.",
    "fin.modal.closed.tip": "Add an estimated job value to each closed lead so your revenue number is complete.",

    // Exports & year-end summary
    "fin.exportsTitle": "Exports",
    "fin.exportCsv": "Export CSV",
    "fin.downloadYearEnd": "Year-end summary",
    "fin.exportDialogTitle": "Export summary",
    "fin.exportDialogBody": "Pick a date range. The CSV includes closed-job revenue and expenses — for your records and accountant. Estimates only.",
    "fin.periodThisMonth": "This month",
    "fin.periodYtd": "Year to date",
    "fin.periodCustom": "Custom range",
    "fin.customStart": "Start date",
    "fin.customEnd": "End date",
    "fin.downloadCsv": "Download CSV",
    "fin.downloadJson": "Download JSON",
    "fin.exporting": "Exporting…",
    "fin.exportErrorRange": "Please choose a start and end date.",
    "fin.exportError": "Export failed. Please try again.",
    "fin.yearEndTitle": "Year-end summary",
    "fin.yearEndSubtitle": "An organized recap of your year — useful for bookkeeping. Estimates only. Not a tax document.",
    "fin.yearEndYear": "Year",
    "fin.totalRevenue": "Total revenue",
    "fin.totalExpenses": "Total expenses",
    "fin.cancelledJobsLabel": "Cancelled jobs",
    "fin.topCategoriesTitle": "Top expense categories",
    "fin.yearEndEmpty": "No data for this year yet.",
    "fin.yearEndPrint": "Print summary",
    "fin.close": "Close",

    // Growth AI page — "Grow Your Business"
    "growth.pageTitle": "Grow Your Business",
    "growth.pageSubtitle": "AI-powered tools to help you get more leads, reviews, and repeat customers.",
    "growth.generate": "Generate",
    "growth.generating": "Generating…",
    "growth.regenerate": "Regenerate",
    "growth.copy": "Copy",
    "growth.copied": "Copied to clipboard.",
    "growth.copyFailed": "Copy failed. Select and copy manually.",
    "growth.errorGeneric": "AI generation failed. Please try again.",
    "growth.tone": "Tone",
    "growth.tone.friendly": "Friendly",
    "growth.tone.professional": "Professional",
    "growth.tone.warm": "Warm",
    "growth.tone.premium": "Premium",
    "growth.tone.promotional": "Promotional",
    "growth.serviceType": "Service type",
    "growth.service.deep": "Deep cleaning",
    "growth.service.moveOut": "Move-out cleaning",
    "growth.service.recurring": "Recurring cleaning",
    "growth.service.office": "Office cleaning",
    "growth.service.custom": "Custom",
    "growth.customService": "Custom service",
    "growth.customServicePlaceholder": "e.g. Post-construction cleaning",
    "growth.city": "City",
    "growth.cityPlaceholder": "e.g. Dawsonville, GA",
    // Output language selector (independent from app UI language)
    "growth.outputLanguage": "Output language",
    "growth.outputLanguage.en": "English",
    "growth.outputLanguage.es": "Spanish",
    // Tool 1
    "growth.review.title": "Review reply assistant",
    "growth.review.subtitle": "Write warm, professional responses to customer reviews in seconds.",
    "growth.review.inputLabel": "Customer review",
    "growth.review.placeholder": "Paste the review here…",
    "growth.review.errorEmpty": "Please paste the customer review first.",
    // Tool 2
    "growth.gbp.title": "Google Business post assistant",
    "growth.gbp.subtitle": "Create local SEO-friendly Google Business posts with AI.",
    "growth.gbp.offer": "Special offer (optional)",
    "growth.gbp.offerPlaceholder": "e.g. 10% off your first booking",
    "growth.gbp.errorCity": "Please enter your city.",
    "growth.gbp.errorCustom": "Please describe your custom service.",
    // Tool 3
    "growth.ba.title": "Before & after content assistant",
    "growth.ba.subtitle": "Generate social captions and hooks for completed cleaning jobs.",
    "growth.ba.platform": "Platform",
    "growth.ba.googleBusiness": "Google Business",
    "growth.ba.customerReview": "Customer review (optional)",
    "growth.ba.reviewPlaceholder": "Paste a short customer quote to feature in the caption…",
    "growth.ba.hookLabel": "Reel hook",
    "growth.ba.ctaLabel": "Call to action",
    "growth.ba.hashtagsLabel": "Hashtags",
    // Tool 4 — Referral Campaign Assistant
    "growth.ref.title": "Referral Campaign Assistant",
    "growth.ref.subtitle": "Generate matched SMS, email, and Facebook posts to ask customers for referrals.",
    "growth.ref.businessName": "Business name",
    "growth.ref.businessNamePlaceholder": "Defaults to your saved business name",
    "growth.ref.offer": "Referral offer (optional)",
    "growth.ref.offerPlaceholder": "e.g. $25 off their next cleaning",
    "growth.ref.smsLabel": "SMS referral request",
    "growth.ref.emailLabel": "Email referral request",
    "growth.ref.emailSubjectLabel": "Email subject",
    "growth.ref.facebookLabel": "Facebook referral post",
    "growth.ref.errorEmpty": "Please add some context before generating.",
    // Tool 5 — Slow Season Assistant
    "growth.slow.title": "Slow Season Assistant",
    "growth.slow.subtitle": "Get 5 concrete, owner-ready actions to drive bookings during a quiet stretch.",
    "growth.slow.context": "Business context (optional)",
    "growth.slow.contextPlaceholder": "e.g. We focus on move-out and recurring residential cleans. Bookings have slowed in the last 3 weeks.",
    "growth.slow.cityLabel": "City (optional)",
    "growth.slow.ideasLabel": "Ideas",

    // SMS Opt-In page (owner-only SMS program)
    "smsOptIn.pageTitle": "SMS Opt-In — Owner-Only Notifications",
    "smsOptIn.subtitle": "How transactional SMS works at Tasklio AI",
    "smsOptIn.intro": "Tasklio AI sends transactional SMS messages to one audience only: cleaning business owners who hold a Tasklio account. We never send SMS to your customers on your behalf. Customers who submit a request through your public lead form receive no SMS messages from Tasklio AI.",
    "smsOptIn.whoTitle": "Who receives SMS",
    "smsOptIn.whoBody": "Only the cleaning business owner (the Tasklio account holder). End customers do not receive SMS from Tasklio AI through any program.",
    "smsOptIn.howOptInTitle": "How to opt in",
    "smsOptIn.howOptInBody": "Sign in to your Tasklio account, go to Settings → SMS notification phone, enter and save your own mobile number. Saving the number is the opt-in event. Leaving the field blank disables SMS for your account.",
    "smsOptIn.whereOptInTitle": "Where does opt-in happen?",
    "smsOptIn.whereOptInBody": "Tasklio account holders opt in by entering their own mobile phone number in Settings → SMS Notification Phone and saving the settings.",
    "smsOptIn.mockupFieldLabel": "SMS Notification Phone",
    "smsOptIn.mockupFieldValue": "+1 404 555 1234",
    "smsOptIn.mockupHelper": "By saving a phone number here you consent to receive transactional SMS notifications from Tasklio AI when a new lead is submitted or created in your account.",
    "smsOptIn.mockupBullet1": "Message frequency varies based on lead volume.",
    "smsOptIn.mockupBullet2": "Message and data rates may apply.",
    "smsOptIn.mockupBullet3": "Reply STOP to opt out.",
    "smsOptIn.mockupBullet4": "Reply HELP for help.",
    "smsOptIn.mockupBullet5": "No marketing or promotional messages.",
    "smsOptIn.mockupCaption": "Illustration only — this is what the opt-in field looks like inside your Tasklio Settings.",
    "smsOptIn.mockupBreadcrumb": "Tasklio · Settings · Notifications",
    "smsOptIn.mockupCheckboxLabel": "I agree to receive transactional SMS notifications from Tasklio AI about my account, including new lead alerts. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. No marketing or promotional messages.",
    "smsOptIn.mockupSaveButton": "Save Settings",
    "smsOptIn.howOptInWorksTitle": "How opt-in works",
    "smsOptIn.howOptInWorksIntro": "SMS opt-in occurs behind login. Tasklio account holders opt in by going to Settings → SMS Notification Phone, entering their own mobile number, checking the SMS consent checkbox, and saving the settings.",
    "smsOptIn.howOptInPoint1": "Phone number is optional.",
    "smsOptIn.howOptInPoint2": "The checkbox is not pre-selected.",
    "smsOptIn.howOptInPoint3": "Users can save settings without enabling SMS.",
    "smsOptIn.howOptInPoint4": "Customers and end users do not receive SMS from Tasklio AI.",
    "smsOptIn.howOptInPoint5": "SMS is only for registered Tasklio account holders.",
    "smsOptIn.howOptInPoint6": "No marketing or promotional SMS messages are sent.",
    "smsOptIn.howOptInPoint7": "Reply STOP to opt out or HELP for help.",
    "smsOptIn.howOptInPoint8": "Message and data rates may apply.",
    "smsOptIn.messageTypesTitle": "What you'll receive",
    "smsOptIn.messageType1": "New lead arrival — \"New lead: [Customer Name] – [Service Type]. Call: [Customer Phone]. Review in your Tasklio account at https://cleaning.tasklio.co.\" Sent when a new lead is submitted or created in your Tasklio account.",
    "smsOptIn.frequencyTitle": "Frequency",
    "smsOptIn.frequencyBody": "Message frequency varies based on lead volume.",
    "smsOptIn.ratesTitle": "Rates",
    "smsOptIn.ratesBody": "Message and data rates may apply. Tasklio AI does not charge for messages; standard carrier rates from your mobile provider may apply.",
    "smsOptIn.helpStopTitle": "HELP and STOP",
    "smsOptIn.helpStopBody": "Reply HELP at any time for help instructions. Reply STOP at any time to opt out — you'll receive a final confirmation and no further messages. You can also clear the SMS notification phone field under Settings to disable SMS for your account.",
    "smsOptIn.privacyTitle": "Privacy and no marketing",
    "smsOptIn.privacyBody": "No marketing or promotional SMS messages are sent. SMS opt-in data is never shared, sold, or rented to third parties for marketing purposes. We share data only with Twilio (our SMS delivery provider) strictly to deliver the messages you've requested.",
    "smsOptIn.customerNoteTitle": "What about my customers?",
    "smsOptIn.customerNoteBody": "Customers who submit a request through your public lead form are contacted by you directly using the contact details they provide. Tasklio AI does not send any SMS messages to your customers on your behalf.",
    "smsOptIn.contactTitle": "Questions?",
    "smsOptIn.contactBody": "Contact support@tasklio.co for any SMS-related questions.",
    "smsOptIn.backToHome": "Back to home",
    "smsOptIn.programSummaryTitle": "SMS Program Summary",
    "smsOptIn.programSummary1": "Tasklio AI operates a single transactional SMS program.",
    "smsOptIn.programSummary2": "Recipients are limited to Tasklio account holders (cleaning business owners).",
    "smsOptIn.programSummary3": "Customers who submit service requests through public lead forms are not enrolled in the SMS program.",
    "smsOptIn.programSummary4": "Customers do not receive SMS messages from Tasklio AI.",
    "smsOptIn.programSummary5": "Business owners voluntarily opt in by entering and saving their own mobile number under Settings → SMS Notification Phone.",
    "smsOptIn.programSummary6": "Saving the number is the opt-in event.",
    "smsOptIn.privacyAddon1": "Mobile information will not be shared with third parties or affiliates for marketing or promotional purposes.",
    "smsOptIn.privacyAddon2": "SMS consent data is never sold, rented, transferred, disclosed, or shared for marketing purposes.",
    "smsOptIn.relatedDocsTitle": "Related Documents",
    "smsOptIn.relatedDocsProgramLabel": "SMS Program Details:",
    "smsOptIn.relatedDocsPrivacyLabel": "Privacy Policy:",
    "smsOptIn.relatedDocsTermsLabel": "Terms and Conditions:",

    // ---- Public Customer Booking Page mini-site ----
    "public.hero.getQuote": "Get a Free Quote",
    "public.hero.website": "Website",
    "public.hero.reviews": (n) => `${n} Google reviews`,
    "public.about.title": "About Us",
    "public.highlights.title": "Why Choose Us",
    "public.highlight.insured": "Insured",
    "public.highlight.locally_owned": "Locally Owned",
    "public.highlight.supplies_included": "Supplies Included",
    "public.highlight.pet_friendly": "Pet Friendly",
    "public.highlight.recurring_available": "Recurring Cleaning Available",
    "public.highlight.residential_cleaning": "Residential Cleaning",
    "public.highlight.commercial_cleaning": "Commercial Cleaning",
    "public.highlight.english_speaking": "English Speaking",
    "public.highlight.spanish_speaking": "Spanish Speaking",
    "public.reviews.title": "Google Reviews",
    "public.reviews.basedOn": (n) => `Based on ${n} Google reviews`,
    "public.reviews.viewProfile": "View Google Profile",
    "public.cta.title": "Ready for a cleaner space?",
    "public.cta.subtitle": "Tell us what you need and get a free quote in just a few minutes.",
    "public.gallery.title": "Our Work",
    "public.services.title": "Services",
    "public.services.subtitle": "Professional cleaning services tailored to your needs.",
    "public.gallery.photoAlt": (n, total) => `Work photo ${n} of ${total}`,
    "public.lightbox.close": "Close",
    "public.lightbox.prev": "Previous photo",
    "public.lightbox.next": "Next photo",
    "public.lightbox.counter": (n, total) => `${n} / ${total}`,

    // ---- Booking Page Settings (owner customization) ----
    "bps.title": "Customize Your Booking Page",
    "bps.subtitle": "Create a professional page customers can visit to learn about your business and request a quote.",
    "bps.preview": "Preview Page",
    "bps.sectionIntro": "Business Intro",
    "bps.sectionIntroHelp": "The first thing customers see on your page.",
    "bps.businessName": "Business Name",
    "bps.tagline": "Tagline",
    "bps.taglinePlaceholder": "Reliable, professional cleaning for homes across Atlanta.",
    "bps.taglineEs": "Tagline — Spanish (optional)",
    "bps.taglineEsPlaceholder": "Limpieza confiable y profesional para hogares y negocios.",
    "bps.aboutEs": "About Us — Spanish (optional)",
    "bps.aboutEsPlaceholder": "Cuéntales a tus clientes sobre tu negocio, en español.",
    "bps.serviceAreaEs": "Service area — Spanish (optional)",
    "bps.serviceAreaEsPlaceholder": "Atendiendo Atlanta y áreas cercanas dentro de 30 millas.",
    "bps.esFallbackHint": "Optional. Shown when a visitor views your page in Spanish. If left blank, the English text is shown instead.",
    "settings.weeklyEmailTitle": "Weekly Performance Email",
    "settings.weeklyEmailHelp": "Get a weekly summary of your Booking Page views, quote requests, and conversion.",
    "settings.weeklyEmailSaved": "Preference saved.",
    "settings.lifecycleEmailTitle": "Setup Tips Email",
    "settings.lifecycleEmailHelp": "Occasional getting-started emails with tips for sharing your booking page and sending your first quote.",
    "settings.fcTitle": "Cleaning Business Listing",
    "settings.fcHelp": "Make your business visible to customers searching for cleaning services in your area.",
    "settings.fcZipLabel": "Business ZIP code",
    "settings.fcRadiusLabel": "Service radius",
    "settings.fcRadiusPlaceholder": "Select radius",
    "settings.fcRadius20": "20 miles",
    "settings.fcRadius30": "30 miles",
    "settings.fcToggleLabel": "Show My Business in Search Results",
    "settings.fcToggleHelp": "Customers searching your service area can discover your business when this is enabled. You can turn it off anytime.",
    "settings.fcSaved": "Find a Cleaner settings saved.",
    "settings.fcZipInvalid": "Enter a valid 5-digit US ZIP code.",
    "settings.fcNeedZipRadius": "Add a valid ZIP code and service radius to appear in Find a Cleaner.",
    "fc.title": "Find a Cleaner Near You",
    "fc.subtitle": "Enter your ZIP code to discover trusted cleaning professionals serving your area — and request a free quote in minutes.",
    "fc.zipLabel": "ZIP code",
    "fc.zipPlaceholder": "Enter your ZIP code",
    "fc.searchBtn": "Search",
    "fc.searching": "Searching…",
    "fc.zipInvalid": "Enter a valid 5-digit US ZIP code.",
    "fc.tooMany": "Too many searches in a short time. Please wait a moment and try again.",
    "fc.networkError": "Something went wrong. Please check your connection and try again.",
    "fc.emptyTitle": "No cleaners in this area yet",
    "fc.emptyBody": "No Tasklio cleaners are available in this area yet. We're growing our network. Try another nearby ZIP code or check back soon.",
    "fc.resultsFor": (zip) => `Cleaning professionals near ${zip}`,
    "fc.distance": (mi) => `Approx. ${mi} miles away`,
    "fc.reviews": (n) => `(${n} reviews)`,
    "fc.getQuote": "Get a Free Quote",
    "fc.distanceNote": "Distances are approximate, based on ZIP code locations — not driving distance.",
    "lead.viaFindACleaner": "Via Find a Cleaner",
    "nav.findCleaner": "Find a Cleaner",
    "landing.fcBandTitle": "Looking for a Cleaning Service?",
    "landing.fcBandBody": "Find cleaning businesses serving your ZIP code and request a free quote.",
    "landing.fcBandCta": "Find a Cleaner",
    "dash.mkNewBadge": "New • Included with Tasklio",
    "dash.mkInviteTitle": "Get Discovered by New Customers",
    "dash.mkInviteBody": "Help customers in your area find your cleaning business. Set your service area and start appearing in local searches.",
    "dash.mkFreeNote": "Free with your subscription",
    "dash.mkIncluded": "Included with your Tasklio subscription.",
    "dash.mkEnableCta": "Enable My Listing",
    "dash.mkListedTitle": "Your Business Is Listed",
    "dash.mkListedBody": "Customers searching Find a Cleaner near your service area can discover your business.",
    "dash.mkViewCta": "View Marketplace",
    "dash.mkManageCta": "Manage Listing",
    "dash.mkInactiveTitle": "Find a Cleaner listing not active",
    "dash.mkInactiveBody": "Your business isn't currently visible in Find a Cleaner. An active subscription or trial is required for marketplace listings.",
    "bps.about": "About Your Business",
    "bps.aboutPlaceholder": "Tell customers a little about your business, experience, and what makes your service different.",
    "bps.sectionArea": "Service Area",
    "bps.sectionAreaHelp": "Let customers know where you work.",
    "bps.city": "City",
    "bps.state": "State",
    "bps.serviceArea": "Service Area Description",
    "bps.serviceAreaPlaceholder": "Serving Atlanta and surrounding areas within 30 miles.",
    "bps.sectionContact": "Public Contact",
    "bps.sectionContactHelp": "Contact details shown to customers on your public page.",
    "bps.publicPhone": "Public Phone Number",
    "bps.publicPhoneHelper": "This number is shown to customers on your public page. It is completely separate from your private SMS notification number in Settings.",
    "bps.website": "Website URL",
    "bps.sectionHighlights": "Business Highlights",
    "bps.sectionHighlightsHelp": "Select only the highlights that truly apply to your business. They appear under \u201cWhy Choose Us\u201d.",
    "bps.sectionGoogle": "Google Business",
    "bps.sectionGoogleHelp": "Shown as a Google rating summary on your page. Only enter values that match your actual Google Business profile.",
    "bps.googleUrl": "Google Business Profile URL",
    "bps.googleRating": "Google Rating",
    "bps.googleReviewCount": "Google Review Count",
    "bps.googleHelper": "If the rating or review count is empty, the Google section will not appear on your page.",
    "bps.saved": "Booking page updated",
    "bps.saveError": "Could not save your booking page. Please try again.",
    "bps.ratingInvalid": "Google rating must be between 0 and 5.",
    "bps.countInvalid": "Review count must be a positive whole number.",
    "bps.backToSettings": "Back to Settings",
    "bps.sectionPhotos": "Logo & Work Photos",
    "bps.sectionPhotosHelp": "Add your logo and photos of your work. Changes here save instantly.",
    "bps.logo": "Business Logo",
    "bps.logoHelper": "Shown next to your business name on your public page.",
    "bps.uploadLogo": "Upload logo",
    "bps.replaceLogo": "Replace logo",
    "bps.removeLogo": "Remove",
    "bps.gallery": "Work Photos",
    "bps.galleryHelper": "Add up to 6 photos of your work.",
    "bps.galleryTip": "Use clear, well-lit photos. Landscape photos work best.",
    "bps.galleryCount": (n) => `${n} / 6 photos`,
    "bps.galleryFull": "Gallery is full. Remove a photo to add another.",
    "bps.addPhoto": "Add photo",
    "bps.photoUploaded": "Photo added",
    "bps.photoRemoved": "Photo removed",
    "bps.logoUpdated": "Logo updated",
    "bps.logoRemoved": "Logo removed",
    "bps.uploadError": "Upload failed. Please try again.",
    "bps.invalidImage": "Please choose a JPG, PNG, or WebP image.",
    "bps.imageTooLarge": "Image is too large. Maximum size is 8 MB.",
    "bps.moveLeft": "Move left",
    "bps.moveRight": "Move right",
    "bps.deletePhoto": "Delete photo",
    "bps.sectionServices": "Services You Offer",
    "bps.sectionServicesHelp": "Choose the cleaning services customers can request from your booking page.",
    "bps.servicesHint": "Only checked services appear in your quote form.",
    "bps.selectAll": "Select all",
    "bps.servicesMin": "Select at least one service.",
    "bps.reviewLinkConnected": "Connected from Settings",
    "bps.reviewLinkConnectedHelp": "This booking page uses the Google review link already saved in your Tasklio Settings.",
    "bps.reviewLinkMissing": "No Google review link connected yet.",
    "bps.reviewLinkMissingHelp": "Add your Google review link in Settings to connect it to your booking page.",
    "bps.editInSettings": "Edit in Settings",
    "bps.addInSettings": "Add in Settings",
    "bps.reviewLinkLabel": "Google Review Link",
    "dash.bookingPageCustomize": "Customize Page",
    "dash.qrButton": "QR Code",
    "dash.qrTitle": "Your Booking Page QR Code",
    "dash.qrHelp": "Customers can scan this code to open your booking page. Perfect for flyers, business cards, and vehicles.",
    "dash.qrDownload": "Download PNG",
    "dash.bpaTitle": "Booking Page Performance",
    "dash.bpaPeriod.today": "Today",
    "dash.bpaPeriod.7d": "7 Days",
    "dash.bpaPeriod.30d": "30 Days",
    "dash.bpaViews": "Page Views",
    "dash.bpaStarts": "Quote Starts",
    "dash.bpaRequests": "Quote Requests",
    "dash.bpaConversion": "Conversion Rate",
    "dash.bpaEmpty": "No booking page activity yet.",
    "dash.bpaEmptyHint": "Share your Customer Booking Page to start tracking views and quote requests.",
    "settings.bookingPage": "Customize Your Booking Page",
    "settings.bookingPageHelp": "Add your tagline, about section, highlights, and Google rating to make your public page shine.",
    "settings.bookingPageHint": "Turn your Customer Booking Page into a small professional website.",
    "settings.bookingPageButton": "Customize Page",
  },

  es: {
    // Navbar
    "nav.dashboard": "Panel",
    "nav.billing": "Facturación",
    "nav.pricing": "Precios",
    "nav.settings": "Configuración",
    "nav.admin": "Administrador",
    "nav.logout": "Cerrar sesión",
    "nav.login": "Iniciar sesión",
    "nav.startTrial": "Empieza tu prueba gratis de 30 días",
    "nav.goDashboard": "Ir al panel",

    // Landing page
    "landing.heroEyebrow": "IA para negocios de limpieza",
    "landing.heroTitle": "Maneja tu negocio de limpieza — ",
    "landing.heroTitleAccent": "todo en un solo lugar.",
    "landing.heroBody": "Tasklio responde a tus clientes con IA, envía cotizaciones en segundos, da seguimiento a cada trabajo y mantiene tus ingresos y gastos organizados — desde el primer mensaje hasta tu resumen anual.",
    "landing.ctaSeePricing": "Ver precios",
    "landing.ctaWatchDemo": "Ver demo",
    "landing.trust14Days": "Prueba gratis de 30 días",
    "landing.trust2Min": "Sin contrato",
    "landing.trustSms": "Inglés y español",
    "landing.heroLangBadge": "Disponible en inglés y español",
    "landing.heroSubhead": "Responde más rápido, envía cotizaciones en segundos, automatiza el seguimiento, solicita reseñas en Google y reserva más trabajos.",
    "landing.heroPositioning": "Creado específicamente para negocios de limpieza.",
    "landing.ctaScheduleDemo": "Reserva una demo gratis",
    "landing.ctaStartTrial": "Inicia prueba gratis de 30 días",
    "landing.demoCardEyebrow": "Demo en vivo",
    "landing.demoCardTitle": "Reserva una demo gratis",
    "landing.demoCardBody": "Mira exactamente cómo funciona Tasklio para tu negocio de limpieza.",
    "landing.demoCard.aiReplies": "Respuestas con IA",
    "landing.demoCard.aiQuotes": "Cotizaciones con IA",
    "landing.demoCard.bookingPage": "Página de reservas",
    "landing.demoCard.followUps": "Seguimientos y reseñas",
    "landing.demoCard.crm": "CRM y pipeline",
    "landing.demoCard.liveQa": "Preguntas en vivo",
    "landing.demoCard.bilingual": "Disponible en inglés y español",
    "landing.demoCardSocialContext": "Diseñado para limpiadores independientes y equipos de limpieza en crecimiento.",
    "landing.demoCardCta": "Reservar mi demo gratis",
    "landing.demoCardFinePrint": "Demo en vivo de 15 minutos · sin tarjeta de crédito",
    "landing.showcaseEyebrow": "Producto",
    "landing.showcaseTitle": "Mira Tasklio en acción",
    "landing.showcaseSubtitle": "Todo lo que tu negocio de limpieza necesita en un solo lugar.",
    "landing.showcase.dashboardTitle": "Panel",
    "landing.showcase.dashboardBody": "Da seguimiento a clientes, trabajos, ingresos y al desempeño de tu negocio desde un solo lugar.",
    "landing.showcase.calendarTitle": "Calendario",
    "landing.showcase.calendarBody": "Cada trabajo reservado aparece automáticamente en tu agenda.",
    "landing.showcase.financialsTitle": "Finanzas",
    "landing.showcase.financialsBody": "Monitorea ingresos, gastos y ganancias sin hojas de cálculo.",
    "landing.showcase.quotesTitle": "Cotizaciones con IA",
    "landing.showcase.quotesBody": "Genera cotizaciones profesionales en segundos y envíalas en línea.",
    "landing.showcase.growTitle": "Haz crecer tu negocio",
    "landing.showcase.growBody": "Crea respuestas a reseñas, campañas de referidos, publicaciones de Google e ideas de marketing para temporada baja con IA.",
    "landing.heroTag.dashboard": "Clientes y trabajos",
    "landing.heroTag.calendar": "Agendamiento automático",
    "landing.heroTag.financials": "Seguimiento de ingresos",
    "landing.heroTag.quotes": "Cotizaciones con IA",
    "landing.heroTag.grow": "Crecimiento del negocio",
    "landing.socialEyebrow": "Hecho para dueños de limpieza que lo hacen todo",
    "landing.socialQuote": "“Respuestas, cotizaciones, seguimientos y mi contabilidad — por fin todo en un solo lugar.”",
    "landing.socialAttribution": "— Dueño de negocio de limpieza",
    "landing.socialTrust": "Sin contratos • Cancela cuando quieras • Prueba gratis de 30 días",
    "landing.howItWorks": "Cómo funciona",
    "landing.howItWorksTitle": "Del primer cliente al resumen anual — sin malabares.",
    "landing.step1": "Llega el mensaje de un cliente a Tasklio",
    "landing.step2": "La IA redacta una respuesta o cotización en tu voz",
    "landing.step3": "Ganas el trabajo — y los números se llevan solos",
    "landing.testimonialsTitle": "Usado por profesionales de la limpieza que trabajan más inteligente",
    "landing.testimonialsBody": "Desde limpiadores solos hasta equipos en crecimiento — Tasklio te ayuda a responder más rápido, cotizar mejor, dar seguimiento automático y saber exactamente a dónde va tu dinero.",
    "landing.testimonialsStat": "La mayoría de dueños responden en menos de 2 minutos y empiezan a reservar más trabajos en la primera semana.",
    "landing.trustNoContracts": "Sin contratos",
    "landing.trustCancel": "Cancela cuando quieras",
    "landing.trustStripe": "Pagos seguros con Stripe",
    "landing.featureCaptureTitle": "Cada cliente, cada canal — una sola bandeja.",
    "landing.featureCaptureBody": "Comparte tu enlace de reservas en Instagram, Google o tu sitio. DMs, llamadas y formularios llegan a un solo lugar — con seguimiento de estado de nuevo a cerrado.",
    "landing.featureReplyTitle": "Respuestas y cotizaciones con IA — en tu voz.",
    "landing.featureReplyBody": "Un clic redacta una respuesta cálida o una cotización pulida en inglés o español. Suena profesional en segundos y gana siempre a la competencia más lenta.",
    "landing.featureWinTitle": "Seguimientos, reseñas y re-reservas — automáticos.",
    "landing.featureWinBody": "Tasklio toca a los clientes inactivos, pide reseñas a los felices y trae de vuelta a los antiguos — así los trabajos siguen llegando sin perseguir.",
    "landing.featureQuoteTitle": "Ingresos y gastos, organizados.",
    "landing.featureQuoteBody": "Sigue los ingresos ganados de trabajos cerrados, registra gastos por categoría y exporta un resumen anual limpio para tu contador — todo incluido.",
    "landing.statResponseTitle": "Responde en menos de 60 segundos",
    "landing.statResponseBody": "Desde la solicitud hasta tu respuesta lista.",
    "landing.statBookingsTitle": "3× más reservas",
    "landing.statBookingsBody": "Cuando respondes en menos de 5 minutos.",
    "landing.statSaveTitle": "Una app en lugar de cinco",
    "landing.statSaveBody": "Respuestas, cotizaciones, seguimientos, reseñas y contabilidad — juntos.",

    // FAQ section — Spanish
    "faq.title": "Todo lo que necesitas saber",
    "faq.subtitle": "Respuestas a las preguntas más comunes sobre Tasklio AI, precios, agendamiento, cotizaciones y crecimiento del negocio.",
    "faq.q.what": "¿Qué es Tasklio AI?",
    "faq.a.what": "Tasklio AI es una plataforma todo-en-uno creada para dueños de negocios de limpieza. Maneja clientes potenciales, crea cotizaciones y respuestas asistidas por IA, comparte tu página de reservas, organiza tus trabajos, da seguimiento a tus clientes, solicita reseñas y lleva el control de tu negocio — todo en un solo lugar.",
    "faq.q.cost": "¿Cuánto cuesta Tasklio AI?",
    "faq.a.cost": "Tasklio AI cuesta $39 al mes después de tu prueba gratuita. Tu suscripción incluye acceso a las funciones disponibles de la plataforma, incluida la elegibilidad para aparecer en Find a Cleaner. No se requiere ningún contrato anual.",
    "faq.q.trial": "¿La prueba gratuita de 30 días es realmente gratis?",
    "faq.a.trial": "¡Sí! Puedes probar Tasklio AI durante 30 días sin tarjeta de crédito. Explora la plataforma y sus funciones antes de decidir si te suscribes.",
    "faq.q.noWebsite": "¿Necesito un sitio web para recibir solicitudes de limpieza?",
    "faq.a.noWebsite": "No. Tasklio le da a tu negocio su propia Página de Reservas para Clientes, donde los clientes potenciales pueden solicitar cotizaciones de limpieza. Comparte el enlace en redes sociales, en tu Perfil de Empresa de Google o directamente con tus clientes.",
    "faq.q.existingLeads": "¿Puedo usar Tasklio con los clientes que ya recibo?",
    "faq.a.existingLeads": "Por supuesto. Agrega y maneja clientes potenciales de Instagram, Facebook, llamadas, mensajes de texto, WhatsApp, correo, referidos y otras fuentes en un solo lugar.",
    "faq.q.quotes": "¿Tasklio puede generar cotizaciones de limpieza por mí?",
    "faq.a.quotes": "Sí. La función de Cotización con IA de Tasklio te ayuda a preparar estimaciones de precio según los detalles del trabajo. Puedes revisar y editar cada cotización antes de enviarla a tu cliente.",
    "faq.q.acceptOnline": "¿Los clientes pueden aceptar cotizaciones en línea?",
    "faq.a.acceptOnline": "Sí. Comparte un enlace de cotización profesional que tus clientes pueden abrir y aceptar desde su teléfono o computadora.",
    "faq.q.afterAccept": "¿Qué pasa después de que un cliente acepta una cotización?",
    "faq.a.afterAccept": "Tasklio marca automáticamente el lead como reservado y agrega el trabajo a tu calendario integrado.",
    "faq.q.aiReply": "¿Cómo funciona AI Reply?",
    "faq.a.aiReply": "AI Reply te ayuda a redactar respuestas profesionales a las consultas de tus clientes en segundos. Tú revisas la respuesta sugerida y decides qué enviar. Tasklio no responde automáticamente los mensajes de tus clientes en tu nombre.",
    "faq.q.notifications": "¿Cómo sabré cuando un nuevo cliente solicite una cotización?",
    "faq.a.notifications": "Tasklio puede notificarte por correo electrónico cuando llegue una nueva solicitud. Las alertas por SMS son opcionales y están disponibles para dueños de negocio que den el consentimiento requerido. Las notificaciones por SMS se envían al dueño del negocio, no automáticamente a los clientes.",
    "faq.q.findCleaner": "¿Qué es Find a Cleaner?",
    "faq.a.findCleaner": "Find a Cleaner es el directorio público de búsqueda de Tasklio que ayuda a los clientes potenciales a descubrir negocios de limpieza participantes por código postal. Los negocios elegibles pueden elegir aparecer en los resultados activando su listado y seleccionando un radio de servicio. El listado está incluido con una prueba o suscripción activa; las consultas de clientes no están garantizadas.",
    "faq.q.recurring": "¿Tasklio puede manejar citas de limpieza recurrentes?",
    "faq.a.recurring": "Sí. Maneja trabajos de limpieza de una sola vez, semanales, quincenales y mensuales con las herramientas de agenda y calendario integradas de Tasklio.",
    "faq.q.googleReviews": "¿Puedo solicitar reseñas de Google a mis clientes?",
    "faq.a.googleReviews": "Sí. Tasklio te ayuda a enviar solicitudes de reseña con un enlace directo a la página de reseñas de tu Perfil de Empresa de Google.",
    "faq.q.pastCustomers": "¿Tasklio me ayuda a reconectar con clientes anteriores?",
    "faq.a.pastCustomers": "Sí. Usa recordatorios de re-reserva y herramientas de seguimiento para mantener el contacto con clientes anteriores y fomentar que vuelvan a reservar.",
    "faq.q.finance": "¿Puedo llevar el control de gastos, ingresos y ganancias?",
    "faq.a.finance": "Sí. Tasklio incluye herramientas de seguimiento financiero para registrar gastos, monitorear ingresos y entender el desempeño de tu negocio.",
    "faq.q.growSection": "¿Qué es la sección Haz Crecer Tu Negocio?",
    "faq.a.growSection": "Es una colección de herramientas con IA para escribir respuestas a reseñas, publicaciones para tu Perfil de Empresa de Google, contenido de antes y después, campañas de referidos y promociones para temporada baja.",
    "faq.q.spanish": "¿Tasklio funciona en español?",
    "faq.a.spanish": "Sí. Tasklio funciona en inglés y español, e incluye herramientas para crear contenido dirigido a tus clientes en español cuando lo necesites.",
    "faq.q.noApp": "¿Necesito instalar una app o algún software especial?",
    "faq.a.noApp": "No. Tasklio funciona en el navegador de tu teléfono, tableta o computadora. No necesitas descargar ninguna app ni instalar software especial.",
    "faq.q.solo": "¿Tasklio es adecuado para limpiadores independientes y equipos pequeños?",
    "faq.a.solo": "Sí. Tasklio está diseñado para limpiadores independientes, negocios familiares y equipos pequeños de limpieza que quieren mantenerse organizados y crecer.",
    "faq.q.trialEnd": "¿Qué pasa cuando termina mi prueba gratuita? ¿Puedo cancelar?",
    "faq.a.trialEnd": "Si no te suscribes después de tu prueba de 30 días, no se te cobrará automáticamente. Algunas funciones quedan restringidas, incluido el acceso a los datos de contacto de nuevos leads, hasta que actualices tu plan. Si te suscribes, puedes administrar o cancelar tu suscripción desde el portal de facturación.",
    "landing.ctaTitle": "Maneja tu negocio de limpieza con más inteligencia — desde hoy.",
    "landing.ctaBody": "Únete a los dueños de limpieza que usan Tasklio para responder más rápido, cotizar en segundos, dar seguimiento automático y mantener sus números organizados.",
    "landing.ctaCreate": "Empieza gratis",
    "landing.ctaViewPricing": "Ver precios",

    // Footer
    "footer.home": "Inicio",
    "footer.pricing": "Precios",
    "footer.login": "Iniciar sesión",
    "footer.signup": "Registrarse",
    "footer.privacy": "Privacidad",
    "footer.terms": "Términos",
    "footer.smsTerms": "Términos SMS",
    "footer.support": "Contactar soporte",
    "footer.copyright": (year) => `© ${year} Tasklio AI`,

    // Pricing page
    "pricing.eyebrow": "Precios",
    "pricing.title": "Un plan simple. Maneja todo tu negocio de limpieza.",
    "pricing.subtitle": "Hecho para limpiadores solos y equipos en crecimiento. Prueba gratis durante 30 días — sin tarjeta de crédito.",
    "pricing.tierName": "Tasklio Pro",
    "pricing.earlyAccess": "Precio de acceso anticipado",
    "pricing.active": "Activa",
    "pricing.perMonth": "/ mes",
    "pricing.trialLine": "Prueba gratis de 30 días — sin tarjeta de crédito",
    "pricing.feature1": "Respuestas con IA en tu voz — en segundos",
    "pricing.feature2": "Cotizaciones con IA con rangos de precio y partidas",
    "pricing.feature3": "Respuestas y cotizaciones bilingües (inglés + español)",
    "pricing.feature4": "Clientes ilimitados en una sola bandeja organizada",
    "pricing.feature5": "CRM completo: seguimiento del embudo de nuevo → cerrado",
    "pricing.feature6": "Seguimientos automáticos para clientes inactivos",
    "pricing.feature7": "Solicitudes de reseñas de Google con un toque",
    "pricing.feature8": "Recordatorios de re-reserva para clientes anteriores",
    "pricing.feature10": "Panel de Business Insights con seguimiento de ingresos",
    "pricing.feature11": "Seguimiento de gastos por categoría (gasolina, suministros, anuncios y más)",
    "pricing.feature12": "Resumen anual y exportaciones CSV para tu contador",
    "pricing.feature13": "Tu propia Página de Reservas para Clientes — compártela donde quieras",
    "pricing.feature14": "Listado en el directorio Find a Cleaner — incluido sin costo adicional",
    "pricing.feature9": "Cancela cuando quieras",
    "pricing.ctaManage": "Gestionar facturación",
    "pricing.ctaUpgradeNow": "Maneja tu negocio con más inteligencia",
    "pricing.ctaUpgradeUnlock": "Maneja tu negocio con más inteligencia",
    "pricing.ctaStartTrial": "Empieza tu prueba gratis de 30 días",
    "pricing.portalNote": "Cancela, actualiza tu tarjeta o consulta facturas en tu portal seguro de Stripe.",
    "pricing.staleBilling": "Tu antiguo registro de facturación de prueba ya no es válido. Suscríbete de nuevo en Stripe en vivo.",
    "pricing.trialActiveNote": "Estás actualmente en tu prueba gratis de 30 días.",
    "pricing.subText": "Sin tarjeta de crédito • Cancela cuando quieras",
    "pricing.secureLine": "Pago seguro con Stripe • Prueba gratis de 30 días • Cancela cuando quieras",
    "pricing.trustHeading": "Hecho para limpiadores solos y equipos en crecimiento.",
    "pricing.trustLine1": "Respuestas, cotizaciones, seguimientos, reseñas, ingresos y gastos — juntos.",
    "pricing.trustLine2": "Un solo trabajo de limpieza reservado puede cubrir meses de Tasklio.",
    "pricing.noRefunds": "Todos los pagos son finales y no reembolsables.",
    "pricing.cancelPolicy": "Puedes cancelar cuando quieras. El acceso continúa hasta el final de tu período de facturación.",
    "pricing.needHelp": "¿Necesitas ayuda?",
    "pricing.contactLink": "Contactar a support@tasklio.co",
    "pricing.benefitCancelTitle": "Cancela cuando quieras",
    "pricing.benefitCancelBody": "Sin contratos a largo plazo. Cancela desde tu panel.",
    "pricing.benefitTrialTitle": "Prueba gratis de 30 días",
    "pricing.benefitTrialBody": "Prueba todas las funciones gratis — sin tarjeta de crédito.",
    "pricing.benefitBuiltTitle": "Flujo todo-en-uno",
    "pricing.benefitBuiltBody": "Respuestas, cotizaciones, CRM, seguimientos, ingresos y gastos — juntos.",

    // Legal pages (Terms + Privacy)
    "legal.eyebrow": "Legal",
    "legal.lastUpdated": "Última actualización:",
    "terms.title": "Términos y Condiciones",
    "terms.intro": "Bienvenido a Tasklio AI (\"Tasklio\", \"nosotros\", \"nuestro\"). Al crear una cuenta o usar el servicio, aceptas estos Términos y Condiciones.",
    "terms.subTitle": "Suscripción y facturación",
    "terms.subBody1": "Tasklio se ofrece como una suscripción mensual. Tu tarjeta se cobra el mismo día de cada mes por el próximo período de servicio.",
    "terms.subBody2": "Puedes cancelar tu suscripción en cualquier momento desde tu cuenta. Tras la cancelación seguirás teniendo acceso hasta el final del período de facturación actual; no se realizarán cobros adicionales.",
    "terms.subBody3": "Podemos ofrecer una prueba gratis introductoria. Si no te suscribes antes de que termine la prueba, tu cuenta pasará a un estado limitado — no se realiza ningún cobro.",
    "terms.refundsTitle": "Reembolsos",
    "terms.refundsHeadline": "Todos los pagos son finales y no reembolsables.",
    "terms.refundsBody": "No ofrecemos reembolsos, reembolsos prorrateados ni crédito por períodos de facturación parciales, funciones no usadas o cambios a un plan inferior. Si cancelas a mitad de ciclo, conservas acceso hasta el final del período que ya pagaste.",
    "terms.guaranteeTitle": "Sin garantía de resultados",
    "terms.guaranteeBody1": "Tasklio proporciona sugerencias asistidas por IA, incluyendo borradores de respuestas, estimaciones de precios y puntuación de clientes potenciales. Estos resultados son solo orientativos y no se garantiza que sean precisos, óptimos ni que te ganen el trabajo.",
    "terms.guaranteeBody2": "Los resultados dependen de muchos factores fuera de nuestro control — tus precios, la decisión de tu cliente, las condiciones del mercado y tu seguimiento. No ofrecemos ninguna garantía ni representación sobre ingresos, tasa de conversión o resultados de negocio.",
    "terms.respTitle": "Tu responsabilidad",
    "terms.respBody1": "Eres el único responsable del uso del contenido generado por IA. Antes de enviar una respuesta o cotización a un cliente, debes revisar la precisión, el tono y los precios, y editarla según sea necesario. Eres responsable de cumplir con los precios, compromisos o promesas que comuniques a tus clientes.",
    "terms.respBody2": "Aceptas usar Tasklio solo para fines comerciales lícitos y no enviar spam, acosar a clientes ni violar las regulaciones aplicables de telecomunicaciones y correo electrónico (incluyendo TCPA y CAN-SPAM en Estados Unidos).",
    "terms.smsTitle": "Notificaciones SMS",
    "terms.smsBody1": "Tasklio AI opera un único programa de SMS transaccionales para dueños de negocio (titulares de cuenta de Tasklio). Los dueños de negocio que guarden un número de teléfono en Ajustes → Teléfono para notificaciones SMS pueden recibir una notificación SMS transaccional cuando se envíe o cree un nuevo lead en su cuenta de Tasklio. Los clientes que envían una solicitud a través del formulario público de Tasklio no reciben mensajes SMS de Tasklio AI.",
    "terms.smsBody2": "La frecuencia de los mensajes varía según el volumen de leads. Pueden aplicar tarifas de mensajes y datos. No se envían mensajes SMS de marketing ni promocionales a través de este programa. Los datos de aceptación de SMS nunca se venden, alquilan ni comparten con terceros con fines de marketing.",
    "terms.smsBodyNotRequired1": "El consentimiento para recibir notificaciones SMS no es un requisito para la compra, suscripción o uso de Tasklio AI.",
    "terms.smsBodyNotRequired2": "Los usuarios pueden usar Tasklio AI sin proporcionar un número de teléfono.",
    "terms.smsBodyNotRequired3": "Las notificaciones SMS son opcionales y pueden desactivarse en cualquier momento eliminando el número de teléfono de Ajustes o respondiendo STOP.",
    "terms.smsPhoneOptional": "Proporcionar un número de teléfono es opcional.",
    "terms.smsMobileNotShared": "La información móvil no se compartirá con terceros ni afiliados con fines de marketing o promocionales.",
    "terms.smsProgramUrlsTitle": "URLs del programa SMS",
    "terms.smsProgramDetailsLabel": "Detalles del programa SMS:",
    "terms.smsPolicyLinkLabel": "Política de privacidad:",
    "terms.smsTermsLinkLabel": "Términos y condiciones:",
    "terms.smsBody3Pre": "Para obtener ayuda, responde",
    "terms.smsBody3Mid": "Para dejar de recibir mensajes en cualquier momento, responde",
    "terms.smsConsentTitle": "Notificaciones SMS para dueños del negocio",
    "terms.smsConsentBody": "Los dueños de negocio activan el servicio guardando un número de móvil en Ajustes → Teléfono para notificaciones SMS dentro de su cuenta de Tasklio. Guardar el número es la acción de aceptación. Los mensajes se limitan a una única notificación transaccional enviada cuando se envía o crea un nuevo lead en la cuenta de Tasklio. La frecuencia de los mensajes varía según el volumen de leads. Pueden aplicar tarifas de mensajes y datos. Responde STOP para darte de baja en cualquier momento o HELP para obtener ayuda. No se envían mensajes de marketing ni promocionales a través de este programa. Los datos de aceptación de SMS nunca se venden, alquilan ni comparten con terceros con fines de marketing.",
    "terms.changesTitle": "Cambios a estos términos",
    "terms.changesBody": "Podemos actualizar estos Términos de vez en cuando. El uso continuado de Tasklio después de publicar una actualización significa que aceptas los Términos revisados.",
    "terms.contactTitle": "Contacto",
    "terms.contactBody": "¿Preguntas sobre estos Términos? Escríbenos a",
    "privacy.title": "Política de Privacidad",
    "privacy.intro": "Tasklio AI (\"Tasklio\", \"nosotros\", \"nuestro\") respeta tu privacidad. Esta política explica qué datos recopilamos, cómo los usamos y qué nunca hacemos con ellos.",
    "privacy.collectTitle": "Qué recopilamos",
    "privacy.collectIntro": "Cuando te registras y usas Tasklio recopilamos lo siguiente:",
    "privacy.collect1": "Tu nombre y nombre del negocio",
    "privacy.collect2": "Tu correo electrónico (usado para iniciar sesión y recibir notificaciones)",
    "privacy.collect3": "Tu número de teléfono (opcional, usado para notificaciones SMS)",
    "privacy.collect4": "Información de clientes potenciales que tus clientes envían por tu formulario público — nombre, correo, teléfono, tipo de servicio, mensaje y cualquier otro detalle que proporcionen",
    "privacy.collect5": "Metadatos básicos de facturación de Stripe cuando te suscribes",
    "privacy.cardNote": "No almacenamos el número de tu tarjeta. Los pagos se procesan a través de Stripe.",
    "privacy.useTitle": "Cómo usamos tus datos",
    "privacy.useBody1": "Usamos la información anterior solo para proporcionar el servicio. Eso significa: mostrarte tus clientes, enviarte notificaciones de nuevos clientes, generar borradores de respuestas y estimaciones de precios con IA, procesar tu suscripción y brindar soporte.",
    "privacy.useBody2": "Datos de uso agregados y anonimizados pueden usarse para mejorar Tasklio (por ejemplo, qué funciones se usan más). Estos datos no pueden usarse para identificarte.",
    "privacy.smsNotificationsTitle": "Comunicaciones SMS",
    "privacy.smsNotificationsBody": "Tasklio AI envía solo una categoría de mensaje SMS, estrictamente transaccional. NOTIFICACIONES DE NUEVO LEAD PARA DUEÑOS DEL NEGOCIO: Si eres titular de una cuenta de Tasklio (dueño de un negocio de limpieza) e ingresas y guardas un número de teléfono en Ajustes → Teléfono para notificaciones SMS, puedes recibir una notificación SMS transaccional cuando se envíe o cree un nuevo lead en tu cuenta de Tasklio. Los clientes que envían solicitudes a través del formulario público de Tasklio no reciben mensajes SMS de Tasklio AI. La frecuencia de los mensajes varía según el volumen de leads. Pueden aplicar tarifas de mensajes y datos. Puedes responder STOP a cualquier mensaje para darte de baja en cualquier momento, o HELP para obtener ayuda. No enviamos mensajes SMS de marketing ni promocionales.",
    "privacy.smsUnifiedNotShared": "La información móvil y los datos de consentimiento SMS no se compartirán, venderán, alquilarán, transferirán ni divulgarán a terceros ni afiliados con fines de marketing o promocionales.",
    "privacy.smsMobileNotShared": "La información móvil no se compartirá con terceros ni afiliados con fines de marketing o promocionales.",
    "privacy.smsConsentDataUsage": "Toda la información de consentimiento SMS, los registros de aceptación y los números de teléfono recopilados para notificaciones SMS se utilizan únicamente para proporcionar el servicio SMS transaccional solicitado.",
    "privacy.smsConsentNeverSold": "Los datos de consentimiento SMS nunca se venden, alquilan, comparten, transfieren ni divulgan a terceros con fines de marketing.",
    "privacy.smsConsentSectionTitle": "Consentimiento SMS",
    "privacy.smsConsentIntro": "El consentimiento SMS se recopila solo cuando un titular de cuenta de Tasklio ingresa y guarda voluntariamente su propio número de teléfono móvil en Ajustes → Teléfono para notificaciones SMS.",
    "privacy.smsConsentSavingIs": "Guardar el número de teléfono es la acción de aceptación.",
    "privacy.smsConsentUsage": "El consentimiento se utiliza únicamente para entregar las notificaciones SMS transaccionales solicitadas relacionadas con la cuenta de Tasklio.",
    "privacy.smsConsentFrequency": "La frecuencia de los mensajes varía según el volumen de leads.",
    "privacy.smsConsentRates": "Pueden aplicar tarifas de mensajes y datos.",
    "privacy.smsConsentStop": "Responde STOP para darte de baja.",
    "privacy.smsConsentHelp": "Responde HELP para obtener ayuda.",
    "privacy.smsConsentNotShared": "El consentimiento SMS no se comparte con terceros ni afiliados con fines de marketing.",
    "privacy.smsConsentDataUsageAlt": "Los datos de consentimiento SMS, los registros de aceptación y los números de teléfono recopilados para notificaciones SMS se utilizan únicamente para la entrega de las notificaciones SMS transaccionales solicitadas.",
    "privacy.smsConsentNeverSoldAlt": "Los datos de consentimiento SMS nunca se venden, alquilan, transfieren, divulgan ni comparten con fines de marketing.",
    "privacy.smsProgramUrlsTitle": "URLs del programa SMS",
    "privacy.smsProgramDetailsLabel": "Detalles del programa SMS:",
    "privacy.smsPolicyLinkLabel": "Política de privacidad:",
    "privacy.smsTermsLinkLabel": "Términos y condiciones:",
    "privacy.dontTitle": "Lo que no hacemos",
    "privacy.dontBody1": "No vendemos, alquilamos ni compartimos tus datos personales — ni la información de clientes potenciales — con terceros para fines de marketing. Nunca.",
    "privacy.dontBody2": "Compartimos datos con nuestros subprocesadores solo para entregar el servicio: Stripe (pagos), Resend (entrega de correos), Twilio (entrega de SMS), MongoDB Atlas (alojamiento de base de datos) y el proveedor de IA que genera respuestas/cotizaciones. Cada uno está obligado por sus propias políticas de privacidad.",
    "privacy.choicesTitle": "Tus opciones",
    "privacy.choicesBody1Pre": "Puedes editar tu perfil y eliminar tu número de teléfono en cualquier momento desde",
    "privacy.choicesBody1Post": ". Para eliminar tu cuenta por completo, escríbenos — ver más abajo.",
    "privacy.choicesBody2": "Para detener las notificaciones SMS, responde STOP a cualquier mensaje que recibas de Tasklio.",
    "privacy.contactTitle": "Contacto",
    "privacy.contactBody": "¿Preguntas, solicitudes de eliminación o inquietudes? Escríbenos a",
    "privacy.settingsWord": "Configuración",

    // Auth pages (Register / Login)
    "auth.registerSidebarTitle": "Maneja tu negocio de limpieza en un solo lugar.",
    "auth.registerSidebarPoint1": "Respuestas, cotizaciones y seguimientos con IA — en segundos",
    "auth.registerSidebarPoint2": "Sigue clientes, ingresos y gastos juntos",
    "auth.registerSidebarPoint3": "Prueba gratis de 30 días — sin tarjeta de crédito",
    "auth.registerTitle": "Empieza a organizar tu negocio de limpieza hoy",
    "auth.registerSubtitle": "Sin configuración complicada · Funciona en escritorio y móvil · Toma menos de 60 segundos.",
    "auth.businessName": "Nombre del negocio",
    "auth.businessPlaceholder": "Limpieza Brillo S.A.",
    "auth.email": "Correo electrónico",
    "auth.emailPlaceholder": "tu@negocio.com",
    "auth.password": "Contraseña",
    "auth.passwordPlaceholder": "Al menos 6 caracteres",
    "auth.createAccount": "Crear cuenta",
    "auth.accountCreatedToast": "¡Cuenta creada!",
    "auth.registrationFailed": "Error al registrarse",
    "auth.alreadyHaveAccount": "¿Ya tienes una cuenta?",
    "auth.logInLink": "Iniciar sesión",
    "auth.loginSidebarTitle": "Bienvenido de nuevo. Tus clientes te están esperando.",
    "auth.loginSidebarBody": "Continúa justo donde lo dejaste — responde a nuevas consultas, envía cotizaciones y reserva trabajos.",
    "auth.loginTitle": "Iniciar sesión",
    "auth.loginSubtitle": "Ingresa tu correo para acceder a tu panel.",
    "auth.forgotPassword": "¿Olvidaste tu contraseña?",
    "auth.loginButton": "Iniciar sesión",
    "auth.welcomeBackToast": "¡Bienvenido de nuevo!",
    "auth.loginFailed": "Error al iniciar sesión",
    "auth.noAccount": "¿No tienes una cuenta?",
    "auth.signUpLink": "Regístrate gratis",

    // Forgot password
    "forgot.sidebarTitle": "¿Olvidaste tu contraseña? No hay problema.",
    "forgot.sidebarBody": "Ingresa tu correo y te enviaremos un enlace seguro para establecer una nueva. El enlace expira en 1 hora.",
    "forgot.title": "Restablece tu contraseña",
    "forgot.subtitle": "Ingresa el correo asociado a tu cuenta y te enviaremos un enlace para restablecerla.",
    "forgot.submit": "Enviar enlace",
    "forgot.backToLogin": "Volver a iniciar sesión",
    "forgot.successTitle": "Revisa tu bandeja",
    "forgot.successBody": "Si existe una cuenta, enviamos un enlace para restablecer la contraseña. El enlace expira en 1 hora.",
    "forgot.spamHint": "Revisa tu carpeta de spam si no lo ves en unos minutos.",
    "forgot.tryAnother": "Probar con otro correo",

    // Reset password
    "reset.sidebarTitle": "Establece una nueva contraseña",
    "reset.sidebarBody": "Elige una contraseña segura que recuerdes. Una vez guardada, inicia sesión y continúa donde lo dejaste.",
    "reset.successTitle": "Contraseña actualizada",
    "reset.successBody": "Tu contraseña ha sido cambiada. Ahora puedes iniciar sesión con tu nueva contraseña.",
    "reset.goToLogin": "Ir a iniciar sesión",
    "reset.missingTitle": "Falta el enlace de restablecimiento",
    "reset.missingBody": "Esta página necesita un enlace válido del correo que enviamos. Solicita un nuevo enlace para continuar.",
    "reset.requestNew": "Solicitar un nuevo enlace",
    "reset.title": "Restablecer contraseña",
    "reset.subtitle": "Elige una nueva contraseña para tu cuenta.",
    "reset.newPassword": "Nueva contraseña",
    "reset.confirmPassword": "Confirmar contraseña",
    "reset.confirmPlaceholder": "Vuelve a ingresar la contraseña",
    "reset.errorTooShort": "La contraseña debe tener al menos 6 caracteres.",
    "reset.errorMismatch": "Las contraseñas no coinciden.",
    "reset.errorGeneric": "No se pudo restablecer la contraseña. El enlace puede ser inválido o haber expirado.",
    "reset.submit": "Restablecer contraseña",

    // Common
    "common.save": "Guardar",
    "common.saveChanges": "Guardar cambios",
    "common.cancel": "Cancelar",
    "common.copy": "Copiar",
    "common.regenerate": "Regenerar",
    "common.loading": "Cargando…",
    "common.english": "Inglés",
    "common.spanish": "Español",

    // Language toggles
    "lang.appLanguage": "Idioma de la aplicación",
    "lang.customerMessageLanguage": "Idioma del mensaje al cliente",
    "lang.replyLanguage": "Idioma de la respuesta",
    "lang.quoteLanguage": "Idioma de la cotización",

    // Dashboard
    "dash.greeting": "Hola,",
    "dash.subtitle": "Esto es lo que pasa con tus clientes. ¿Necesitas ayuda?",
    "dash.contactSupport": "Contactar soporte",
    "dash.addLead": "Agregar cliente",
    "dash.potentialRevenue": "Ingresos potenciales",
    "dash.fromActiveLeads": (count) =>
      `De ${count} ${count === 1 ? "cliente activo" : "clientes activos"} — responde rápido →`,
    "dash.noActiveLeadsYet": "Aún no tienes clientes activos — comparte tu página de reservas para empezar",
    "dash.statTotalLeads": "Total de clientes",
    "dash.statNew": "Nuevos",
    "dash.statBooked": "Reservados",
    "dash.statLast7": "Últimos 7 días",
    "dash.leadTemperature": "Temperatura de clientes",
    "dash.leadTemperatureHelp": "Atiende primero a los clientes más calientes.",
    "dash.scoreHot": "Caliente",
    "dash.scoreWarm": "Tibio",
    "dash.scoreCold": "Frío",
    "dash.returningCustomerBadge": "Cliente recurrente",
    "dash.smsPhoneSaved": "Teléfono SMS guardado",
    "dash.smsPhoneCleared": "Teléfono SMS borrado",
    "dash.smsPhoneSaveError": "No se pudo guardar el teléfono",
    "dash.hotReady": "Listo para reservar",
    "dash.leadProgress": "Progreso de clientes",
    "dash.leadProgressEmpty": "Aquí aparecerá tu embudo de conversión.",
    "dash.percentBooked": (p) => `${p}% de clientes reservados`,
    "dash.progressTotal": "Total de clientes",
    "dash.progressContacted": "Contactados",
    "dash.progressQuoted": "Cotizados",
    "dash.progressBooked": "Reservados",
    "dash.missedBannerOne": "Tienes 1 cliente esperando — no lo pierdas",
    "dash.missedBannerMany": (n) => `Tienes ${n} clientes esperando — no los pierdas`,
    "dash.missedHelper": "Los clientes calientes se enfrían rápido. Responde ya para ganar la reserva.",
    "dash.respondNow": "Responder ahora",
    "dash.filterAll": "Todos",
    "dash.filterNew": "Nuevos",
    "dash.filterContacted": "Contactados",
    "dash.filterQuoted": "Cotizados",
    "dash.filterBooked": "Reservados",
    "dash.filterClosed": "Cerrados",
    "dash.trialActiveTitle": (days) => `Prueba gratis de 30 días activa — ${days} ${days === 1 ? "día" : "días"} restantes`,
    "dash.trialActiveBody": "Tienes acceso completo a todas las funciones. Mejora tu plan cuando quieras para seguir después de la prueba.",
    "dash.upgradeNow": "Mejorar plan",
    "dash.trialExpiredTitle": "Tu prueba terminó — mejora tu plan para desbloquear nuevos clientes.",
    "dash.trialExpiredBody": "Sigues recibiendo alertas por SMS y correo. Mejora tu plan para ver los datos de contacto y seguir ganando trabajos.",
    "dash.trialExpiredCta": "Mejorar para desbloquear",
    "dash.publicLinkTitle": "Tu enlace público de captura",
    "dash.publicLinkPreview": "Vista previa",
    "dash.publicLinkCopy": "Copiar enlace",
    "dash.publicLinkCopied": "Copiado",
    "dash.bookingPageEyebrow": "Página de reservas",
    "dash.bookingPageTitle": "Comparte tu página de reservas",
    "dash.bookingPageDescription": "Esta es la página que tus clientes usan para pedir cotizaciones y reservar servicios de limpieza.",
    "dash.bookingPageCopy": "Copiar enlace de la página",
    "dash.bookingPageCopied": "Enlace copiado",
    "dash.bookingPageOpen": "Abrir página",
    "dash.bookingPageHelper": "Compártela en tu bio de Instagram, perfil de Google Business, página de Facebook, sitio web, mensajes de texto o donde tus clientes te contacten.",
    "dash.firstBookingTitle": "Consigue tu primera reserva",
    "dash.onbProgress": (n) => `${n} de 4 completados`,
    "dash.onbStep1": "Prepara tu Página de Reservas para Clientes",
    "dash.onbStep1Action": "Configurar página",
    "dash.onbStep2": "Consigue tu primer lead — comparte tu página o agrega un cliente existente",
    "dash.onbStep2Action": "Agregar lead",
    "dash.onbStep3": "Crea y envía tu primera cotización",
    "dash.onbStep3Hint": "Abre un lead para preparar la cotización — tú la revisas antes de enviarla.",
    "dash.onbStep4": "Consigue tu primera reserva",
    "dash.onbStep4Hint": "Cuando un cliente acepta tu cotización, el trabajo se reserva automáticamente.",
    "dash.onbAllDone": "¡Listo! Reservaste tu primer trabajo. Sigue compartiendo tu página para llegar a más clientes.",
    "dash.manualLeadEntryTitle": "Entrada manual de cliente",
    "dash.manualLeadEntryDescription": "Úsalo cuando un cliente te contacte por teléfono, referido, walk-in u otro método fuera de tu Página de reservas.",
    "dash.manualLeadTitle": "Agregar un cliente manualmente",
    "dash.manualLeadHelper": "Usa este formulario para llamadas telefónicas, referidos, walk-ins, clientes recurrentes o cualquier persona que no usó tu Página de reservas.",
    "dash.smsPhoneTitle": "Teléfono para notificaciones SMS",
    "dash.smsPhoneHelp": "Te enviaremos un SMS cuando llegue un cliente nuevo. Formato E.164, ej. +14049177181.",
    "dash.smsPhoneSave": "Guardar",
    "dash.searchPlaceholder": "Buscar por nombre, correo, teléfono…",
    "dash.tableHeaderName": "Nombre",
    "dash.tableHeaderContact": "Contacto",
    "dash.tableHeaderService": "Servicio",
    "dash.tableHeaderStatus": "Estado",
    "dash.tableHeaderScore": "Puntaje",
    "dash.tableHeaderAdded": "Agregado",
    "dash.noLeadsYet": "Listo para cuando llegue tu primer cliente",
    "dash.noLeadsHint": "Comparte tu enlace de reservas o agrega un cliente manualmente — Tasklio redacta la respuesta, tú ganas el trabajo.",
    "dash.missedBadge": "Perdido",

    // Lead detail
    "lead.replySection": "Borrador de respuesta IA",
    "lead.quoteSection": "Estimación de precio IA",
    "lead.generateReply": "Generar respuesta",
    "lead.generateQuote": "Generar cotización",
    "lead.copyReply": "Copiar respuesta",
    "lead.copyQuote": "Copiar cotización",
    "lead.regenerate": "Regenerar",
    "lead.step1": "Respuesta IA",
    "lead.step2": "Estimación de precio",
    "lead.step3": "Enviar cotización",
    "lead.backToDashboard": "Volver al panel",
    "lead.statusHelper": "Marca como reservado cuando el cliente confirme.",
    "lead.closeIn3Steps": "Cierra este cliente en 3 pasos",
    "lead.closerStep1": "Responde al cliente",
    "lead.closerStep2": "Genera la estimación de precio",
    "lead.closerStep3": "Envía el enlace de cotización",
    "lead.closeIn3StepsHelp": "Tasklio prepara los mensajes, pero tú mantienes el control. Nada se envía automáticamente a menos que tú lo copies o lo compartas.",
    "lead.markContacted": "Marcar como contactado",
    "lead.markQuoted": "Marcar como cotizado",
    "lead.copyReplyQuote": "Copiar respuesta + cotización",
    "lead.addToCalendar": "Agregar al calendario",
    "lead.scoreTitle": "Puntaje del cliente",
    "lead.scoreSubtitle": "Intención de compra evaluada por IA",
    "lead.scoreNotAnalyzed": "Aún no analizado.",
    "lead.scoreThisLead": "Analizar este cliente",
    "lead.winProbability": "probabilidad de ganar",
    "lead.detailsTitle": "Datos del cliente",
    "lead.notProvided": "No proporcionado",
    "lead.fieldSource": "Origen",
    "lead.fieldSubmittedAt": "Fecha de envío",
    "lead.fieldEmail": "Correo",
    "lead.fieldPhone": "Teléfono",
    "lead.fieldAddress": "Dirección",
    "lead.fieldHomeSize": "Tamaño del hogar",
    "lead.fieldService": "Servicio",
    "lead.fieldPreferredDate": "Fecha preferida",
    "lead.fieldFrequency": "Frecuencia",
    "lead.fieldFrequencyValue.one_time": "Única vez",
    "lead.fieldFrequencyValue.weekly": "Semanal",
    "lead.fieldFrequencyValue.biweekly": "Quincenal",
    "lead.fieldFrequencyValue.monthly": "Mensual",
    "lead.fieldFrequencyValue.daily": "Diario",
    "lead.theirMessage": "Su mensaje",
    "lead.serviceDetailsTitle": "Detalles del servicio",
    "lead.sd.bedrooms": "Dormitorios",
    "lead.sd.bathrooms": "Baños",
    "lead.sd.square_footage": "Pies cuadrados",
    "lead.sd.num_kitchens": "Cocinas",
    "lead.sd.restrooms": "Baños",
    "lead.sd.frequency": "Frecuencia",
    "lead.sd.pets": "Mascotas",
    "lead.sd.home_condition": "Condición del hogar",
    "lead.sd.property_empty": "Propiedad vacía",
    "lead.sd.inside_appliances": "Electrodomésticos por dentro",
    "lead.sd.business_type": "Tipo de negocio",
    "lead.sd.laundry_needed": "Lavandería",
    "lead.sd.restocking_needed": "Reabastecimiento",
    "lead.sd.shower_tub": "Regadera/tina",
    "lead.sd.hard_water_grout": "Sarro / juntas",
    "lead.sd.oven_inside": "Horno por dentro",
    "lead.sd.fridge_inside": "Refrigerador por dentro",
    "lead.sd.cabinets_inside": "Gabinetes por dentro",
    "lead.sd.construction_type": "Tipo de construcción",
    "lead.sd.heavy_dust": "Polvo pesado",
    "lead.sd.debris_removal": "Remoción de escombros",
    // Enum value labels
    "lead.sd.val.one_time": "Una sola vez",
    "lead.sd.val.daily": "Diario",
    "lead.sd.val.weekly": "Semanal",
    "lead.sd.val.biweekly": "Quincenal",
    "lead.sd.val.monthly": "Mensual",
    "lead.sd.val.light": "Ligera",
    "lead.sd.val.average": "Promedio",
    "lead.sd.val.heavy": "Pesada",
    "lead.sd.val.office": "Oficina",
    "lead.sd.val.retail": "Tienda / Retail",
    "lead.sd.val.medical": "Médico",
    "lead.sd.val.restaurant": "Restaurante",
    "lead.sd.val.other": "Otro",
    "lead.sd.val.renovation": "Renovación",
    "lead.sd.val.new_build": "Construcción nueva",
    "lead.sd.val.remodel": "Remodelación",
    "lead.translateToSpanish": "Traducir al español",
    "lead.translateToEnglish": "Traducir al inglés",
    "lead.showOriginal": "Ver original",
    "lead.translatedNote": "Traducido por IA — original preservado",
    "lead.notesTitle": "Notas internas",
    "lead.notesPlaceholder": "Notas privadas sobre este cliente…",
    "lead.jobValueTitle": "Valor estimado del trabajo (USD)",
    "lead.jobValueHelper": "Solo para estadísticas de ingresos. Estas estimaciones no son cifras contables.",
    "lead.jobValueBookedAt": "Reservado el",
    // AI v2 — selector de tono + contexto del cliente + tarjetas de opción
    "lead.replyToneLabel": "Tono",
    "lead.toneFriendly": "Amistoso",
    "lead.toneProfessional": "Profesional",
    "lead.tonePremium": "Premium",
    "lead.toneFastClosing": "Cierre rápido",
    "lead.customerContextLabel": "Última respuesta del cliente (opcional)",
    "lead.customerContextPlaceholder": "Pega aquí el mensaje más reciente del cliente…",
    "lead.customerContextHelper": "Pega la última respuesta del cliente y luego haz clic en Regenerar para redactar una nueva respuesta. Nada se envía automáticamente.",
    "lead.customerContextExamplesLabel": "Ejemplos rápidos:",
    "lead.customerContextEx1": "Es muy caro",
    "lead.customerContextEx2": "¿Pueden el viernes?",
    "lead.customerContextEx3": "¿Qué incluye?",
    "lead.customerContextEx4": "¿Pueden bajar el precio?",
    "lead.replyOptionsLabel": "Elige una respuesta",
    // AI v2 — cotización estructurada
    "lead.quoteSuggestedPrice": "Precio sugerido",
    "lead.quoteRangeLabel": "Rango:",
    "lead.quoteCustomerOnlyHelper": "Solo el precio sugerido se muestra al cliente. El rango, la confianza, el razonamiento y las suposiciones permanecen privados.",
    "lead.quoteEstimated": "estimado",
    "lead.quoteConfidence": "Confianza",
    "lead.quoteWhyThisPrice": "Por qué este precio",
    // Sistema de visibilidad
    "lead.vis.summaryTitle": "Guía de visibilidad",
    "lead.vis.summaryCustomer": "Visible al cliente — aparece en la cotización pública",
    "lead.vis.summaryOwner": "Solo para el dueño — información interna",
    "lead.vis.customer": "Visible al cliente",
    "lead.vis.owner": "Solo para el dueño",
    "lead.vis.customerHelper": "Se muestra al cliente.",
    "lead.vis.ownerHelper": "Solo visible para ti. Nunca se muestra al cliente.",
    "lead.upsellTitle": "Oportunidades de venta",
    "lead.upsellHelper": "Sugerencias para aumentar el valor de la cotización. Nunca se muestran al cliente.",
    "lead.quoteIncluded": "Qué incluye",
    "lead.quoteAddOns": "Servicios adicionales",
    "lead.quoteAssumptions": "Suposiciones",
    "lead.quoteCustomerMessage": "Mensaje para el cliente",
    "lead.quoteCustomerHelper": "Copia / edita antes de enviar. El dueño tiene el control.",
    "lead.quoteNextBestAction": "Próxima mejor acción",
    "lead.saveNotes": "Guardar notas",
    "lead.saved": "Guardado",
    "lead.aiReplyTitle": "Paso 1 — Responde al cliente",
    "lead.aiReplySubtitle": "Usa este mensaje para responder rápidamente. Nada se envía automáticamente.",
    "lead.aiReplyHelper": "Copia esta respuesta y envíala por Instagram, SMS, WhatsApp, Facebook, correo o donde el cliente te haya contactado.",
    "lead.aiReplyHelperSub": "Claude Sonnet 4.5 — edita antes de enviar.",
    "lead.replyPlaceholderBusy": "Redactando respuesta…",
    "lead.replyPlaceholderIdle": "Haz clic en \"Generar respuesta\" para redactar un correo, o escribe el tuyo.",
    "lead.saveEdits": "Guardar cambios",
    "lead.aiQuoteTitle": "Paso 2 — Genera una estimación de precio",
    "lead.aiQuoteSubtitle": "Crea un precio sugerido y los detalles de cotización listos para el cliente.",
    "lead.aiQuoteHelper": "Esto no se envía al cliente hasta que crees y compartas un enlace de cotización.",
    "lead.aiQuoteHelperSub": "Genera una estimación rápida en segundos.",
    "lead.quotePlaceholderBusy": "Calculando estimación…",
    "lead.quotePlaceholderIdle": "Haz clic en \"Generar cotización\" para una estimación IA, o escribe la tuya.",
    "lead.quoteLinkTitle": "Paso 3 — Envía la cotización al cliente",
    "lead.quoteLinkSubtitle": "Envía tu mensaje de cotización al cliente. Una vez aceptada, Tasklio marca automáticamente el lead como reservado y actualiza tu flujo de trabajo.",
    "lead.createQuoteLink": "Crear enlace de cotización",
    "lead.previewLink": "Vista previa",
    "lead.copyQuoteLink": "Copiar enlace",
    "lead.copyQuoteMessage": "Copiar mensaje de cotización",
    "lead.copyLinkOnly": "Copiar solo el enlace",
    "lead.quoteMessageCopied": "Mensaje de cotización copiado",
    "lead.linkCopied": "Enlace copiado",
    "lead.copyError": "No se pudo copiar al portapapeles",
    "lead.copySpanishLink": "Copiar enlace en español",
    "lead.sendViaWhatsApp": "Enviar por WhatsApp",
    "lead.sendViaEmail": "Enviar por Email",
    "lead.newLink": "Generar un nuevo enlace",
    "lead.closeTipTitle": "Envía esto para cerrar el trabajo",
    "lead.closeTipSub": "Cuando lo acepte, el cliente se marca como reservado automáticamente",
    "lead.closeTipWin": "La mayoría de los clientes reservan con el primero en enviar una cotización",
    "lead.step3InstructionsTitle": "Cotización lista para enviar",
    "lead.step3InstructionsStep1": "Haz clic en \"Copiar mensaje de cotización\"",
    "lead.step3InstructionsStep2": "Envíaselo a tu cliente",
    "lead.step3InstructionsStep3": "El cliente revisa y acepta la cotización en línea",
    "lead.step3InstructionsStep4": "Tasklio marca automáticamente el lead como reservado",
    // Paso 2 — Precio sugerido + Mensaje editable
    "lead.quotePriceEditLabel": "Editar precio sugerido",
    "lead.quotePriceEditHelper": "Este será el monto visible para el cliente.",
    "lead.quotePriceUpdated": "Precio sugerido actualizado",
    "lead.quotePriceUpdateError": "No se pudo actualizar el precio",
    "lead.quoteMessageUpdated": "Mensaje del cliente actualizado",
    "lead.quoteMessageUpdateError": "No se pudo guardar el mensaje",
    "lead.quoteCustomerMessagePlaceholder": "Escribe el mensaje que tu cliente verá en la página de cotización.",
    "lead.quoteSaving": "Guardando…",
    // Inline workflow guidance cards
    "lead.guide.replyReady": "Respuesta lista",
    "lead.guide.replyReadyBody": "Envía este mensaje primero si quieres iniciar la conversación. Si estás listo para enviar el precio, genera una estimación a continuación.",
    "lead.guide.replyReadyCta": "Generar estimación de precio",
    "lead.guide.priceReady": "Estimación lista",
    "lead.guide.priceReadyBody": "Revisa la estimación y luego crea un enlace de cotización para que tu cliente pueda aceptar en línea.",
    "lead.guide.priceReadyCta": "Crear enlace de cotización",
    "lead.guide.quoteReady": "Cotización lista para enviar",
    "lead.guide.quoteReadyBody": "Copia el mensaje de cotización y envíaselo a tu cliente. El mensaje incluye el enlace.",
    "lead.guide.quoteReadyCta": "Copiar mensaje de cotización",
    // Recommended Next Action (Spanish)
    "lead.nextAction.eyebrow": "Acción recomendada",
    "lead.nextAction.generateReply.title": "Genera una respuesta y contacta al cliente.",
    "lead.nextAction.generateReply.body": "Tasklio redactará una respuesta profesional en segundos.",
    "lead.nextAction.generateReply.cta": "Generar respuesta",
    "lead.nextAction.sendReply.title": "Envía la respuesta al cliente y continúa la conversación.",
    "lead.nextAction.sendReply.body": "Copia esta respuesta y pégala donde el cliente te contactó.",
    "lead.nextAction.sendReply.cta": "Copiar respuesta",
    "lead.nextAction.interest.title": "El cliente parece interesado. Genera y envía una cotización.",
    "lead.nextAction.interest.body": "Su último mensaje parece interés en agendar o pedir precio — aprovecha el momento.",
    "lead.nextAction.interest.cta": "Generar cotización",
    "lead.nextAction.question.title": "El cliente necesita una respuesta. Responde primero.",
    "lead.nextAction.question.body": "Su último mensaje requiere una respuesta antes de avanzar con el precio o la reserva.",
    "lead.nextAction.question.cta": "Regenerar respuesta",
    "lead.nextAction.followup.title": "El cliente respondió a tu pregunta. Continúa la conversación.",
    "lead.nextAction.followup.body": "Su respuesta parece contestar a tu última pregunta — redacta un seguimiento antes de enviar la cotización.",
    "lead.nextAction.followup.cta": "Regenerar respuesta",
    "lead.nextAction.sendQuote.title": "Envía la cotización al cliente.",
    "lead.nextAction.sendQuote.body": "Crea un enlace de cotización para que el cliente pueda revisar y aceptar en línea.",
    "lead.nextAction.sendQuote.cta": "Crear enlace de cotización",
    "lead.nextAction.waiting.title": "Esperando la decisión del cliente.",
    "lead.nextAction.waiting.body": "Da seguimiento en 24–48 horas si no hay respuesta.",
    "lead.nextAction.waiting.cta": "",
    "lead.nextAction.booked.title": "Reserva confirmada.",
    "lead.nextAction.booked.body": "Esta cita ya se agregó a tu calendario de Tasklio.",
    "lead.nextAction.booked.cta": "Ver en el calendario",
    "lead.nextAction.review.title": "Pide una reseña de Google.",
    "lead.nextAction.review.body": "Una reseña corta de un cliente feliz se traduce en más clientes futuros.",
    "lead.nextAction.review.cta": "Generar pedido de reseña",
    "lead.missedTitle": "Este cliente no ha sido contactado en más de 2 horas",
    "lead.missedBody": (ago) => `Llegó hace ${ago} — responde ya para mantener la reserva caliente.`,
    "lead.paywallTitle": "Estás a un paso de un nuevo cliente que paga",
    "lead.paywallBody": "Los clientes que piden cotización suelen reservar en horas. Gana quien responde primero.",
    "lead.paywallCta": "Desbloquear cliente por $39/mes",
    "lead.paywallTrust": "Cancela cuando quieras • Clientes ilimitados • Sin contratos",
    "lead.deleteConfirmTitle": "¿Eliminar este cliente?",
    "lead.deleteConfirmBody": "Esta acción no se puede deshacer.",
    "lead.deleteCta": "Eliminar",
    "lead.statusNew": "Nuevo",
    "lead.statusContacted": "Contactado",
    "lead.statusQuoted": "Cotizado",
    "lead.statusBooked": "Reservado",
    "lead.statusClosed": "Cerrado",
    "lead.statusCancelled": "Cancelado",
    "lead.statusClosedHelper": "Los trabajos cerrados cuentan para los ingresos estimados.",
    "lead.statusBookedHelper": "Los trabajos reservados están agendados pero no cuentan como ingresos hasta que se cierran.",
    // Cancel-reason (ES)
    "lead.cancelReason.label": "Motivo de cancelación (opcional)",
    "lead.cancelReason.placeholder": "¿Por qué se canceló?",
    "lead.cancelReason.too_expensive": "Muy caro",
    "lead.cancelReason.no_response": "Sin respuesta del cliente",
    "lead.cancelReason.went_with_competitor": "Se fue con la competencia",
    "lead.cancelReason.rescheduled": "Reprogramado / pospuesto",
    "lead.cancelReason.changed_mind": "El cliente cambió de opinión",
    "lead.cancelReason.other": "Otro",
    "lead.cancelledOn": "Cancelado el",
    "lead.cancelledWarning": "Este cliente está marcado como cancelado. Sigue apareciendo en tus analíticas, pero se excluye de los ingresos y los ingresos potenciales.",
    // Archive / Restore (ES)
    "lead.archive": "Archivar cliente",
    "lead.archiveConfirm": "¿Archivar este cliente? Se ocultará del panel pero permanecerá en analíticas y reportes.",
    "lead.archived": "Archivado",
    "lead.archiveSuccess": "Cliente archivado",
    "lead.archiveError": "No se pudo archivar el cliente",
    "lead.restore": "Restaurar cliente",
    "lead.restoreSuccess": "Cliente restaurado",
    "lead.restoreError": "No se pudo restaurar el cliente",
    "lead.permanentDelete": "Eliminar permanentemente",
    "lead.permanentDeleteConfirm": "¿Eliminar este cliente para siempre? Esto no se puede deshacer.",
    "lead.permanentDeleteAdminOnly": "Solo los administradores pueden eliminar clientes de forma permanente.",
    // Business Insights (ES)
    "bi.lostRevenue": "Ingresos perdidos",
    "bi.cancelledJobs": "Trabajos cancelados",
    "bi.completionRate": "Tasa de finalización",
    "bi.bookingConversion": "Conversión a reserva",
    "bi.potentialRevenue": "Ingresos potenciales",
    "bi.potentialRevenueHint": "Cotizados + reservados, aún no ganados.",
    "bi.revenueHint": "Solo trabajos cerrados.",
    "bi.pipelineHealth": "Salud del pipeline",
    // Dashboard archive tab (ES)
    "dash.tabActive": "Activos",
    "dash.tabArchived": "Archivados",
    "dash.archivedEmpty": "No hay clientes archivados todavía.",
    "lead.waNoPhone": "Agrega un teléfono a este cliente para enviar por WhatsApp",
    "lead.waReplyNoDraft": "Genera o escribe una respuesta primero",
    "lead.waReplyNoQuote": "Crea un enlace de cotización primero — el mensaje de WhatsApp incluye la respuesta + el enlace",
    "lead.waNoQuote": "Genera o escribe una cotización primero.",
    "lead.waReplyReadyWithLink": "Enviar la respuesta, el precio estimado y el enlace de cotización por WhatsApp",
    "lead.waReplyReadyNoLink": "Enviar la respuesta y el precio estimado por WhatsApp",
    "lead.waQuoteEnTitle": "Enviar el enlace de cotización por WhatsApp (mensaje en inglés)",
    "lead.waQuoteEsTitle": "Enviar el enlace de cotización por WhatsApp (mensaje en español)",
    "lead.copyEsLinkTitle": "Comparte un enlace que abre en español para tu cliente",

    // Settings
    "settings.title": "Configuración",
    "settings.subtitle": "Administra el perfil de tu negocio, tu cuenta y tu contraseña.",
    "settings.businessProfile": "Perfil del negocio",
    "settings.businessProfileHelp": "Se muestra en tu enlace público y en las notificaciones.",
    "settings.businessName": "Nombre del negocio",
    "settings.smsPhone": "Teléfono para notificaciones SMS",
    "settings.smsPhoneHelper": "Al ingresar tu número de teléfono móvil y marcar la casilla de consentimiento SMS de arriba, aceptas recibir notificaciones SMS transaccionales de Tasklio AI relacionadas con tu cuenta, incluidas alertas de nuevos leads. La frecuencia de los mensajes varía según la actividad de leads. Pueden aplicar tarifas de mensajes y datos. Responde STOP para darte de baja o HELP para obtener ayuda. Nunca se envían mensajes SMS de marketing ni promocionales. Deja el número de teléfono en blanco si no deseas recibir notificaciones SMS.",
    "settings.smsConsentCheckboxLabel": "Acepto recibir notificaciones SMS transaccionales de Tasklio AI sobre mi cuenta, incluidas alertas de nuevos leads. La frecuencia de los mensajes varía. Pueden aplicar tarifas de mensajes y datos. Responde STOP para darte de baja o HELP para obtener ayuda. Sin mensajes de marketing ni promocionales.",
    "settings.smsConsentRequired": "Por favor marca la casilla de consentimiento SMS para activar las notificaciones SMS.",
    "settings.account": "Cuenta",
    "settings.accountHelp": "Tu correo de acceso.",
    "settings.email": "Correo electrónico",
    "settings.emailContactNote": "Contacta a",
    "settings.changePassword": "Cambiar contraseña",
    "settings.changePasswordHelp": "Usa una contraseña fuerte que no uses en otro sitio.",
    "settings.currentPassword": "Contraseña actual",
    "settings.newPassword": "Contraseña nueva",
    "settings.confirmPassword": "Confirmar contraseña nueva",
    "settings.updatePassword": "Actualizar contraseña",
    "settings.billing": "Facturación y Suscripción",
    "settings.billingHelp": "Gestiona tu plan Tasklio Pro, el método de pago o mejora tu suscripción.",
    "settings.manageBilling": "Gestionar facturación y suscripción",
    "settings.preferences": "Preferencias",
    "settings.preferencesHelp": "Elige el idioma utilizado en la interfaz de Tasklio AI.",

    // Settings (corrección i18n — bloques Google Review + Customer Reply)
    "settings.googleReviewLink": "Enlace de reseñas de Google",
    "settings.googleReviewHelper": "Agrega tu enlace de reseñas de Google para que Tasklio pueda enviar solicitudes de reseña.",
    "settings.customerReplyEmail": "Email de respuesta del cliente",
    "settings.customerReplyHelper": "Las respuestas de clientes a emails de reseñas y re-reservas llegarán a esta dirección.",
    "settings.customerReplyCurrent": "Actualmente las respuestas van a:",
    "settings.timezone": "Zona horaria del negocio",
    "settings.timezoneHelper": "Todas las fechas y horas de citas se muestran en esta zona horaria, nunca en la del navegador. Se usa en el calendario, cotizaciones públicas, respuestas de IA y recordatorios por correo.",
    "settings.invalidUrl": "URL inválida — debe comenzar con http:// o https://",
    "settings.invalidEmail": "Email inválido",
    "settings.profileUpdated": "Configuración guardada",
    "settings.backToDashboard": "← Volver al panel",
    "settings.passwordMinLength": "Al menos 6 caracteres",
    "settings.businessNameEmpty": "El nombre del negocio no puede estar vacío.",
    "settings.profileUpdateError": "No se pudo actualizar el perfil. Intenta de nuevo.",
    "settings.passwordMin": "La nueva contraseña debe tener al menos 6 caracteres.",
    "settings.passwordMismatch": "Las nuevas contraseñas no coinciden.",
    "settings.passwordSame": "La nueva contraseña debe ser diferente a la actual.",
    "settings.passwordUpdated": "Contraseña actualizada correctamente.",
    "settings.passwordUpdatedToast": "Contraseña actualizada",
    "settings.passwordUpdateError": "No se pudo actualizar la contraseña.",
    "settings.billingOnPro": "Tu plan actual es Tasklio Pro.",
    "settings.billingOpenHint": "Abre tu página de facturación para mejorar o administrar tu plan.",

    // Dashboard — tarjeta de Oportunidades de re-reserva
    "dash.growthOpportunity": "Oportunidad de crecimiento",
    "dash.rebookingOpportunities": "Oportunidades de re-reserva",
    "dash.rebookSummary": "Clientes anteriores podrían estar listos para reservar otra vez.",
    "dash.rebookSubcopy": "Envía un seguimiento rápido para traer clientes de vuelta.",
    "dash.rebookReadyPill": "listo",
    "dash.rebookOneDay": "1 día desde la reserva",
    "dash.rebookManyDays": (n) => `${n} días desde la reserva`,
    "dash.sendFollowUp": "Enviar seguimiento",
    "dash.previewEmail": "Vista previa del email",

    // Resumen del negocio (Business Insights)
    "bi.title": "Resumen del negocio",
    "bi.subtitle": "Valores estimados",
    "bi.thisMonth": "Este mes",
    "bi.last30Days": "Últimos 30 días",
    "bi.excludesNoValue": "Excluye clientes sin valor estimado",
    "bi.bookedJobs": "Trabajos reservados",
    "bi.estimatedRevenue": "Ingresos estimados",
    "bi.returningCustomers": "Clientes recurrentes",
    "bi.reviewsSent": "Solicitudes de reseña enviadas",
    "bi.leadsCreated": "Clientes creados",
    "bi.quotesSent": "Cotizaciones enviadas",
    "bi.rebooksSent": "Recordatorios de re-reserva enviados",
    "bi.returningLeads": "Clientes recurrentes",
    "bi.averageJobValue": "Valor promedio por trabajo",
    "bi.estimatedReturningRevenue": "Ingresos recurrentes estimados",
    "bi.allEstimates": "Todos los valores son estimados · USD",
    "bi.bookingsLive": "Reservas activas",
    "bi.returningPositive": "Volvieron — excelente.",
    "bi.estimateLabel": "Estimado",
    "bi.valuedHint": (valued, total) => `${valued} de ${total} trabajos con valor`,
    "bi.emptyTitle": "Tus trabajos reservados aparecerán aquí como ingresos estimados",
    "bi.emptyBody": "Cuando marques un cliente como reservado y agregues un valor estimado del trabajo, sumaremos los ingresos del mes, los clientes recurrentes y las tendencias de crecimiento aquí.",

    // Resumen del negocio — modal "Más información" por tarjeta
    "bi.modal.currentValue": "Valor actual",
    "bi.modal.explain": "Qué significa",
    "bi.modal.tip": "Consejo",
    "bi.modal.close": "Cerrar",
    "bi.modal.learnMore": "Más información",
    "bi.bookedJobs.explain": "Esto muestra cuántos clientes fueron marcados como reservados este mes.",
    "bi.bookedJobs.tip": "Marca los clientes como reservados cuando confirmen el trabajo.",
    "bi.estimatedRevenue.explain": "Esto estima los ingresos de los trabajos reservados que tienen un valor estimado.",
    "bi.estimatedRevenue.tip": "Agrega un valor estimado en cada cliente reservado para que este número sea más útil.",
    "bi.returningCustomers.explain": "Esto muestra clientes reservados que regresaron a través de un recordatorio de re-reserva o un enlace de cliente recurrente.",
    "bi.returningCustomers.tip": "Usa los recordatorios de re-reserva para traer de vuelta a clientes anteriores.",
    "bi.reviewsSent.explain": "Esto muestra cuántos correos de solicitud de reseña de Google se enviaron este mes.",
    "bi.reviewsSent.tip": "Después de reservar o completar un trabajo, envía una solicitud de reseña para mejorar tu reputación.",
    "bi.leadsCreated.explain": "Esto muestra cuántos clientes fueron creados en los últimos 30 días.",
    "bi.leadsCreated.tip": "Comparte tu enlace público en Instagram, Facebook, Google Business Profile y mensajes directos.",
    "bi.quotesSent.explain": "Esto cuenta los enlaces o acciones de cotización creados en los últimos 30 días.",
    "bi.quotesSent.tip": "Envía cotizaciones rápido mientras el cliente sigue interesado.",
    "bi.rebooksSent.explain": "Esto muestra cuántos correos de seguimiento de re-reserva se enviaron en los últimos 30 días.",
    "bi.rebooksSent.tip": "Da seguimiento a tus clientes anteriores después de 30 días para recuperar trabajos recurrentes.",
    "bi.returningLeads.explain": "Esto muestra clientes creados por personas que regresaron a través de un enlace de re-reserva.",
    "bi.returningLeads.tip": "Los recordatorios de re-reserva pueden convertir clientes anteriores en nuevos clientes automáticamente.",
    "bi.closedJobs": "Trabajos cerrados",
    "bi.closedJobs.explain": "Esto muestra los trabajos marcados como cerrados/completados este mes.",
    "bi.closedJobs.tip": "Marca un trabajo como cerrado cuando esté terminado y pagado, para que tus reportes de ingresos sigan siendo precisos.",
    "bi.potentialRevenue.explain": "Valor estimado de los trabajos cotizados y reservados que aún no se cierran.",
    "bi.potentialRevenue.tip": "Da seguimiento rápido a los clientes cotizados o reservados para convertir el pipeline en ingresos.",
    "bi.cancelledJobs.explain": "Clientes marcados como cancelados.",
    "bi.cancelledJobs.tip": "Registra los motivos de cancelación para entender por qué se pierden trabajos.",
    "bi.lostRevenue.explain": "Valor estimado de los clientes cancelados con un valor de trabajo.",
    "bi.lostRevenue.tip": "Usa los motivos de cancelación para mejorar precios, tiempos o seguimiento.",
    "bi.completionRate.explain": "Qué tantos trabajos reservados se convirtieron en cerrados/completados.",
    "bi.completionRate.tip": "Marca los trabajos como cerrados después de terminarlos para que los ingresos sigan siendo precisos.",

    // Public lead form (/l/:ownerId)
    "public.form.eyebrow": "Solicita una cotización",
    "public.form.title": (biz) => `Reserva una limpieza con ${biz}`,
    "public.form.subtitle": "Cuéntanos algunos detalles y te enviaremos una cotización rápido.",
    "public.form.name": "Tu nombre",
    "public.form.email": "Correo electrónico",
    "public.form.phone": "Teléfono (opcional)",
    "public.form.address": "Dirección",
    "public.form.streetAddress": "Dirección",
    "public.form.streetAddressPlaceholder": "Ingresa tu dirección",
    "public.form.addressLine2": "Apto / Unidad / Suite (opcional)",
    "public.form.addressLine2Placeholder": "Opcional",
    "public.form.city": "Ciudad",
    "public.form.cityPlaceholder": "Ingresa la ciudad",
    "public.form.state": "Estado",
    "public.form.statePlaceholder": "Selecciona estado",
    "public.form.zipCode": "Código postal",
    "public.form.zipCodePlaceholder": "Código postal",
    "public.form.addressRequired": "Por favor completa la dirección, ciudad, estado y código postal.",
    "public.form.serviceTypeRequired": "Por favor selecciona un tipo de servicio.",
    "public.form.serviceDetailsRequired": "Por favor completa todos los campos de detalles del servicio.",
    "public.form.err.name": "Por favor ingresa tu nombre.",
    "footer.smsOptIn": "Aceptación SMS",
    "register.smsNotice": "Tasklio AI solo envía SMS transaccionales a los dueños del negocio (a ti), nunca a tus clientes. Consulta ",
    "register.smsNoticeLink": "Aceptación SMS",
    "public.form.err.streetAddress": "La dirección es obligatoria.",
    "public.form.err.city": "La ciudad es obligatoria.",
    "public.form.err.state": "Por favor selecciona un estado.",
    "public.form.err.zip": "Por favor ingresa un código postal válido.",
    "public.form.err.zipInvalid": "Por favor ingresa un código postal válido.",
    "public.form.err.serviceType": "Por favor selecciona un tipo de servicio.",
    "public.form.err.sd.bedrooms": "Las habitaciones son obligatorias.",
    "public.form.err.sd.bathrooms": "Los baños son obligatorios.",
    "public.form.err.sd.num_kitchens": "Por favor ingresa el número de cocinas.",
    "public.form.err.sd.restrooms": "Por favor ingresa el número de baños.",
    "public.form.err.sd.square_footage": "Por favor ingresa los metros cuadrados.",
    "public.form.err.sd.frequency": "Por favor selecciona una frecuencia de servicio.",
    "public.form.err.sd.pets": "Por favor selecciona una opción.",
    "public.form.err.sd.home_condition": "Por favor selecciona el estado del hogar.",
    "public.form.err.sd.property_empty": "Por favor selecciona una opción.",
    "public.form.err.sd.inside_appliances": "Por favor selecciona una opción.",
    "public.form.err.sd.business_type": "Por favor selecciona un tipo de negocio.",
    "public.form.err.sd.laundry_needed": "Por favor selecciona una opción.",
    "public.form.err.sd.restocking_needed": "Por favor selecciona una opción.",
    "public.form.err.sd.shower_tub": "Por favor selecciona una opción.",
    "public.form.err.sd.hard_water_grout": "Por favor selecciona una opción.",
    "public.form.err.sd.oven_inside": "Por favor selecciona una opción.",
    "public.form.err.sd.fridge_inside": "Por favor selecciona una opción.",
    "public.form.err.sd.cabinets_inside": "Por favor selecciona una opción.",
    "public.form.err.sd.construction_type": "Por favor selecciona un tipo de construcción.",
    "public.form.err.sd.heavy_dust": "Por favor selecciona una opción.",
    "public.form.err.sd.debris_removal": "Por favor selecciona una opción.",
    "public.form.err.sd.generic": "Este campo es obligatorio.",
    "public.form.zipInvalid": "Código postal inválido. Usa 5 dígitos (12345) o ZIP+4 (12345-6789).",
    "public.form.service": "Tipo de servicio",
    "public.form.servicePlaceholder": "¿Qué necesitas?",
    "public.form.homeSize": "Tamaño del hogar",
    "public.form.homeSizePlaceholder": "ej. 3 hab / 2 baños",
    "public.form.preferredDate": "Fecha preferida",
    "public.form.notes": "¿Algo más que debamos saber?",
    "public.form.notesPlaceholder": "Cuéntanos sobre el espacio, mascotas, peticiones especiales…",
    "public.form.submit": "Solicitar cotización",
    "public.form.submitError": "No se pudo enviar. Inténtalo de nuevo.",
    "public.form.tooManyRequests": "Hemos recibido varias solicitudes en poco tiempo. Espera un momento e inténtalo de nuevo.",
    "public.form.thanks": (name) => `¡Gracias, ${name || "amigo/a"}!`,
    "public.form.thanksBody": (biz) => `${biz} se pondrá en contacto contigo muy pronto con una cotización y horarios disponibles.`,
    "public.form.invalidTitle": "Enlace no encontrado",
    "public.form.invalidBody": "Este enlace de reserva no es válido.",
    "public.form.gotoTasklio": "Ir a Tasklio AI →",
    "public.form.poweredBy": "Con tecnología de",
    "public.form.requiredHint": "Obligatorio",
    // Smart conditional fields (Spanish)
    "public.form.smartHeader": "Algunos detalles rápidos",
    "public.form.smartHint": "Opcional, pero nos ayuda a preparar una cotización más precisa.",
    "public.form.bedrooms": "Dormitorios",
    "public.form.bathrooms": "Baños",
    "public.form.sqft": "Pies cuadrados aproximados",
    "public.form.sqftPh": "ej. 1500",
    "public.form.numKitchens": "Número de cocinas",
    "public.form.numBathrooms": "Número de baños",
    "public.form.numRestrooms": "Número de baños",
    "public.form.frequency": "Frecuencia del servicio",
    "public.form.freq.one_time": "Una sola vez",
    "public.form.freq.daily": "Diario",
    "public.form.freq.weekly": "Semanal",
    "public.form.freq.biweekly": "Quincenal",
    "public.form.freq.monthly": "Mensual",
    "public.form.pets": "¿Hay mascotas en casa?",
    "public.form.yes": "Sí",
    "public.form.no": "No",
    "public.form.homeCondition": "Condición del hogar",
    "public.form.cond.light": "Ligera",
    "public.form.cond.average": "Promedio",
    "public.form.cond.heavy": "Pesada",
    "public.form.propertyEmpty": "¿La propiedad está vacía?",
    "public.form.insideAppliances": "¿Limpieza interior de electrodomésticos?",
    "public.form.businessType": "Tipo de negocio",
    "public.form.biz.office": "Oficina",
    "public.form.biz.retail": "Tienda / Retail",
    "public.form.biz.medical": "Médico",
    "public.form.biz.restaurant": "Restaurante",
    "public.form.biz.other": "Otro",
    "public.form.laundryNeeded": "¿Se necesita lavandería?",
    "public.form.restockingNeeded": "¿Reabastecer amenidades?",
    "public.form.showerTub": "¿Limpieza de regadera / tina?",
    "public.form.hardWater": "¿Sarro / acumulación en juntas?",
    "public.form.ovenInside": "¿Limpieza interior del horno?",
    "public.form.fridgeInside": "¿Limpieza interior del refrigerador?",
    "public.form.cabinetsInside": "¿Limpieza interior de gabinetes?",
    "public.form.constructionType": "Tipo de construcción",
    "public.form.const.renovation": "Renovación",
    "public.form.const.new_build": "Construcción nueva",
    "public.form.const.remodel": "Remodelación",
    "public.form.const.other": "Otro",
    "public.form.heavyDust": "¿Polvo de construcción pesado?",
    "public.form.debrisRemoval": "¿Remoción de escombros?",
    "public.form.selectPh": "Selecciona…",

    // Aviso bajo el botón de envío del formulario público (cumplimiento
    // A2P 10DLC — texto exacto, no parafrasear). La casilla de
    // consentimiento SMS para clientes fue eliminada: Tasklio AI NO envía
    // SMS a los clientes. El negocio de limpieza contacta al cliente
    // directamente.
    "public.form.smsDisclosure": "Al enviar este formulario, tu solicitud se envía directamente al negocio de limpieza arriba mencionado. Ellos se comunicarán contigo usando los datos de contacto que proporcionaste. Tasklio AI no envía mensajes SMS de marketing ni promocionales a los clientes.",
    "public.form.legalAgreement": "Al enviar este formulario, aceptas nuestra ",
    "public.form.privacyPolicyLink": "Política de privacidad",
    "public.form.legalAgreementAnd": " y ",
    "public.form.termsOfServiceLink": "Términos de servicio",
    "public.form.welcomeBack": "¡Bienvenido de vuelta! Solicita tu próxima limpieza a continuación.",

    // Public quote page (/q/:quoteId)
    "public.quote.eyebrow": "Tu cotización",
    "public.quote.greeting": (name) => `Hola, ${name}`,
    "public.quote.intro": "ha preparado la siguiente cotización para ti.",
    "public.quote.serviceLabel": "Servicio",
    "public.quote.scheduledLabel": "Cita agendada",
    "public.quote.scheduledAt": "a las",
    "public.quote.frequencyLabel": "Frecuencia",
    "public.quote.frequencyLong.weekly": "Servicio recurrente semanal",
    "public.quote.frequencyLong.biweekly": "Servicio recurrente quincenal",
    "public.quote.frequencyLong.monthly": "Servicio recurrente mensual",
    "public.quote.frequencyLong.daily": "Servicio recurrente diario",
    "public.quote.priceLabel": "Estimación de precio",
    "public.quote.estimatedQuoteLabel": "Cotización Estimada",
    "public.quote.includedLabel": "Aspectos destacados del servicio",
    "public.quote.addOnsLabel": "Servicios Adicionales Opcionales",
    "public.quote.messageFrom": (biz) => `Mensaje de ${biz}`,
    "public.quote.disclaimer": "Estimación preparada con la información proporcionada.",
    "public.quote.accept": "Aceptar cotización",
    "public.quote.acceptError": "No se pudo aceptar la cotización",
    "public.quote.previewTitle": "Modo vista previa",
    "public.quote.previewBody": "Así es exactamente como tu cliente verá la cotización.\n\nCierra esta pestaña, haz clic en 'Copiar mensaje de cotización' y envíalo por tu canal de comunicación preferido.",
    "public.quote.previewDisabledTitle": "Aceptar cotización está deshabilitado en la vista previa",
    "public.quote.previewDisabledBody": "Este botón solo está activo para los clientes. Cierra esta pestaña y envía el mensaje de cotización a tu cliente desde la página del lead.",
    "public.quote.acceptedTitle": "🎉 Cotización aceptada",
    "public.quote.acceptedHeadline": "Tu cita está reservada.",
    "public.quote.acceptedBody": (biz) => (
      `${biz} ha sido notificado y tu cotización fue aceptada correctamente.`
    ),
    "public.quote.acceptedThanks": (biz) => `Gracias por elegir a ${biz}.`,
    "public.quote.summaryService": "Servicio:",
    "public.quote.summaryDate": "Fecha:",
    "public.quote.summaryTime": "Hora:",
    "public.quote.summaryPrice": "Precio:",
    "public.quote.invalidTitle": "Cotización no encontrada",
    "public.quote.invalidBody": "Este enlace de cotización no es válido.",

    // ===================== /free landing page (ES) =====================
    "free.nav.login": "Iniciar sesión",
    "free.nav.startFree": "Empezar gratis",
    "free.hero.eyebrow": "Hecho para negocios de limpieza",
    "free.hero.headlineMain": "No pierdas ni un",
    "free.hero.headlineAccent": "cliente de limpieza.",
    "free.hero.sub": "Responde más rápido, envía cotizaciones al instante, da seguimiento automático y mantén a cada cliente organizado en un solo lugar.",
    "free.hero.primaryCta": "Empieza gratis",
    "free.hero.primaryCtaAuthed": "Empieza gratis",
    "free.hero.watchDemo": "Ver cómo funciona",
    "free.hero.reassurance": "Empieza gratis en menos de 60 segundos.",
    "free.hero.pricingNote1": "Sin tarjeta de crédito",
    "free.hero.pricingNote2": "Prueba gratis de 30 días · $39/mes después de la prueba",
    "free.hero.trust1": "Hecho específicamente para negocios de limpieza",
    "free.hero.trust2": "Funciona con tus clientes actuales",
    "free.hero.trust3": "Inglés y español",
    "free.hero.trust4": "Sin tarjeta de crédito",
    "free.hero.shotAlt.dashboard": "Panel de Tasklio con clientes activos, embudo e ingresos",
    "free.hero.shotAlt.aiReply": "Detalle de cliente en Tasklio con una respuesta generada por IA",
    "free.hero.shotAlt.financials": "Página de Finanzas de Tasklio con ingresos y gastos",
    // Product mock copy (ES)
    "free.mock.newLead": "Nuevo cliente",
    "free.mock.minAgo": "hace 2 min",
    "free.mock.customerName": "María López — mudanza 3 recámaras",
    "free.mock.customerMsg": "\"Hola, ¿tienen disponible el viernes?\"",
    "free.mock.aiReply": "Respuesta IA",
    "free.mock.aiReplyText": "Hola María — sí, viernes 9am funciona para una mudanza de 3 recámaras. Cotización adjunta.",
    "free.mock.bookedLabel": "Reservados este mes",
    "free.mock.bookedVs": "vs. 11 el mes pasado",
    "free.mock.stat.hot": "Clientes calientes",
    "free.mock.stat.quotes": "Cotizaciones",
    "free.mock.stat.reviews": "Reseñas",
    // Section 2 — outcomes (localized, not literal)
    "free.outcomes.eyebrow": "Por qué los dueños de limpieza eligen Tasklio",
    "free.outcomes.title": "Menos malabares. Más trabajos reservados.",
    "free.outcome1.title": "Responde a cada cliente en segundos",
    "free.outcome1.body": "La IA redacta una respuesta cálida o una cotización pulida — en inglés o español — antes de que el cliente se vaya con otro.",
    "free.outcome2.title": "Da seguimiento sin pensar en ello",
    "free.outcome2.body": "Los clientes inactivos reciben un recordatorio, los felices reciben pedido de reseña y los antiguos reciben recordatorios de re-reserva — automático.",
    "free.outcome3.title": "Conoce tus números, de principio a fin",
    "free.outcome3.body": "Ve los ingresos reservados, registra gastos por categoría y descarga un resumen anual limpio para tu contador.",
    "free.outcome4.title": "Una pantalla en lugar de cinco apps",
    "free.outcome4.body": "Clientes, cotizaciones, seguimientos, reseñas, ingresos y gastos — juntos, sin configuración extra.",
    // Section 3 — features (ES)
    "free.features.eyebrow": "Todo lo que necesitas",
    "free.features.title": "Respuestas, cotizaciones, seguimientos, reseñas — y los libros.",
    "free.feature.replies.title": "Respuestas IA al instante",
    "free.feature.replies.body": "Borradores cálidos y profesionales en segundos, en inglés o español.",
    "free.feature.quotes.title": "Cotizaciones con IA",
    "free.feature.quotes.body": "Rangos de precio, partidas y un enlace de cotización para compartir antes de que el cliente se enfríe.",
    "free.feature.followups.title": "Seguimientos automáticos",
    "free.feature.followups.body": "Clientes inactivos reciben recordatorios y los antiguos reciben re-reservas — sin esfuerzo.",
    "free.feature.reviews.title": "Solicitudes de reseñas de Google",
    "free.feature.reviews.body": "Un solo toque para pedir la reseña de 5 estrellas.",
    "free.feature.inbox.title": "Seguimiento de ingresos y gastos",
    "free.feature.inbox.body": "Ingresos de trabajos cerrados, gastos por categoría y exportaciones anuales — todo incluido.",
    "free.feature.bilingual.title": "Inglés y español",
    "free.feature.bilingual.body": "Cambia toda la app y los borradores de IA entre idiomas con un clic.",
    // Section 4 — problem / solution (ES, conversion-focused)
    "free.problems.eyebrow": "El problema real",
    "free.problems.title": "La mayoría de los negocios de limpieza pierden trabajos solo por responder tarde.",
    "free.problems.1": "DM de Instagram que se pierden mientras estás trabajando.",
    "free.problems.2": "Llamadas o mensajes que olvidas devolver.",
    "free.problems.3": "Cotizaciones que tardan — y el cliente contrata a alguien más.",
    "free.problems.4": "Cero seguimiento después de mandar el estimado.",
    "free.problems.5": "Mensajes regados entre WhatsApp, IG, llamadas y correos.",
    "free.solution.eyebrow": "Cómo te ayuda Tasklio",
    "free.solution.title": "Responde primero. Da seguimiento solo. Conoce tus números.",
    "free.solution.body": "Tasklio te da un solo panel para cada cliente de limpieza. La IA escribe una respuesta amable o una cotización en segundos, los seguimientos se hacen solos y tus ingresos y gastos quedan organizados — para que por fin sepas dónde está parado el negocio.",
    "free.solution.1": "Respuestas y cotizaciones con IA que suenan a ti, en inglés o español.",
    "free.solution.2": "Enlaces de cotización que tus clientes abren desde cualquier teléfono.",
    "free.solution.3": "Seguimientos automáticos, reseñas y recordatorios de re-reserva.",
    "free.solution.4": "Ingresos de trabajos cerrados + gastos por categoría, en un solo lugar.",
    "free.solution.5": "Resumen anual y exportaciones CSV para tu contador.",
    // Section 5 — demo (ES)
    "free.demo.eyebrow": "Míralo funcionando",
    "free.demo.title": "Mira Tasklio en 60 segundos.",
    "free.demo.sub": "Un recorrido real — DMs y mensajes convirtiéndose en trabajos reservados, con los libros organizándose solos en el fondo.",
    "free.demo.button.title": "Mira el demo de 60 segundos",
    "free.demo.button.sub": "Panel real, respuestas reales, sin diapositivas.",
    // Section 6 — final CTA (ES)
    "free.finalCta.eyebrow": "30 días gratis",
    "free.finalCta.title": "Prueba Tasklio gratis por 30 días.",
    "free.finalCta.sub": "Responde más rápido, da seguimiento automático y ve exactamente cómo va tu negocio de limpieza.",
    "free.finalCta.button": "Empieza gratis",
    "free.finalCta.note1": "No se requiere tarjeta de crédito",
    "free.finalCta.note2": "Cancela cuando quieras",
    // Sticky mobile CTA
    "free.sticky.cta": "Empieza gratis",
    // Footer
    "free.footer.tagline": "Para negocios de limpieza",
    "free.footer.privacy": "Privacidad",
    "free.footer.terms": "Términos",
    "free.footer.login": "Iniciar sesión",
    // Demo modal fallback
    "free.demo.fallbackTitle": "Demo en video próximamente",
    "free.demo.fallbackBody": "Estamos puliendo los últimos detalles del recorrido. La forma más rápida de ver Tasklio en acción es empezar tu prueba gratis de 30 días.",

    // Financials page
    "nav.financials": "Finanzas",
    "nav.growthAi": "Hacer Crecer tu Negocio",
    "nav.calendar": "Calendario",

    // Tasklio Calendar
    "cal.title": "Calendario",
    "cal.subtitle": "Todos tus trabajos reservados en un solo lugar — sincronizados desde tus clientes potenciales.",
    "cal.viewToday": "Hoy",
    "cal.viewWeek": "Semana",
    "cal.viewMonth": "Mes",
    "cal.statusBooked": "Reservado",
    "cal.statusCompleted": "Completado",
    "cal.statusCancelled": "Cancelado",
    "cal.todayLabel": "Hoy",
    "cal.emptyTitle": "Aún no hay trabajos agendados",
    "cal.emptyBody": "Cuando marcas un cliente como reservado, aparece aquí automáticamente. Marca uno como reservado desde el detalle del cliente para verlo en tu calendario.",
    "cal.noJobsToday": "No hay trabajos agendados para hoy.",
    "cal.noJobsThisWeek": "No hay trabajos agendados esta semana.",
    "cal.noJobsThisMonth": "No hay trabajos agendados este mes.",
    "cal.prev": "Anterior",
    "cal.next": "Siguiente",
    "cal.thisMonth": "Este mes",
    "cal.thisWeek": "Esta semana",
    "cal.weekOf": "Semana del",
    "cal.eventDetails": "Detalles del trabajo",
    "cal.customer": "Cliente",
    "cal.phone": "Teléfono",
    "cal.email": "Correo",
    "cal.address": "Dirección",
    "cal.serviceType": "Servicio",
    "cal.scheduledFor": "Programado para",
    "cal.duration": "Duración",
    "cal.jobValue": "Valor estimado",
    "cal.notes": "Notas",
    "cal.calendarNotes": "Notas del calendario (códigos de portón, estacionamiento, suministros…)",
    "cal.calendarNotesPlaceholder": "Solo para ti. Nunca se muestra al cliente.",
    "cal.editSchedule": "Editar horario",
    "cal.dateLabel": "Fecha",
    "cal.timeLabel": "Hora",
    "cal.frequencyLabel": "Frecuencia",
    "cal.durationLabel": "Duración (minutos)",
    "cal.save": "Guardar",
    "cal.cancel": "Cancelar",
    "cal.viewLead": "Ver Cliente",
    "cal.markCompleted": "Marcar Completado",
    "cal.requestReview": "Pedir Reseña",
    "cal.addToCalendar": "Agregar al Calendario",
    "cal.openEvent": "Abrir",
    "cal.allDay": "Todo el día",
    "cal.jobsScheduled": (n) => `${n} trabajos agendados`,
    "cal.oneJobScheduled": "1 trabajo agendado",
    "cal.scheduledHelper": "El valor por defecto es el momento de la reserva — elige una fecha y hora para que el trabajo aparezca donde esperas en el calendario.",
    "cal.savedSchedule": "Horario guardado",
    "cal.savedScheduleErr": "No se pudo guardar el horario",
    "cal.timeNotSet": "Hora no definida",
    "cal.conflict": "Conflicto de horario",
    "cal.conflictHelper": "Otro trabajo está agendado a la misma hora. Ambos se mantienen en el calendario.",

    // Today's Jobs widget on Dashboard
    "todayJobs.title": "Trabajos de hoy",
    "todayJobs.emptyTitle": "Nada agendado hoy",
    "todayJobs.emptyBody": "Tus trabajos reservados de hoy aparecerán aquí.",
    "todayJobs.viewCalendar": "Abrir Calendario",
    "todayJobs.upcomingToday": (n) => `${n} próximos hoy`,
    "todayJobs.completedToday": "Todo listo por hoy",
    "todayJobs.minutes": "min",
    "todayJobs.countOne": "1 trabajo hoy",
    "todayJobs.countMany": (n) => `${n} trabajos hoy`,

    // Financials page
    "fin.title": "Finanzas",
    "fin.subtitle": "Lleva el control de tus ingresos ganados, gastos del negocio y utilidad estimada — todo en un solo lugar.",
    "fin.estimateBadge": "Estimados · USD · No es un documento fiscal",
    "fin.sectionThisMonth": "Este mes",
    "fin.sectionYearToDate": "Año en curso",
    "fin.sectionPipeline": "Embudo",
    "fin.revenue": "Ingresos",
    "fin.expenses": "Gastos",
    "fin.estimatedNet": "Utilidad estimada",
    "fin.potentialRevenue": "Ingresos potenciales",
    "fin.cancelledRevenue": "Ingresos cancelados",
    "fin.closedJobs": "Trabajos cerrados",
    "fin.revenueHelper": "Ingresos ganados solo de trabajos cerrados.",
    "fin.expensesHelper": "Gastos del negocio ingresados manualmente.",
    "fin.netHelper": "Ingresos menos gastos. Solo estimaciones — no es una cifra contable.",
    "fin.potentialHelper": "Trabajos cotizados y agendados abiertos con un valor.",
    "fin.cancelledHelper": "Valor estimado de los trabajos cancelados.",
    "fin.closedJobsHelper": "Total de trabajos marcados como cerrados.",

    // Expense management
    "fin.expensesTitle": "Gastos del negocio",
    "fin.expensesSubtitle": "Agrega tu gasolina, suministros, anuncios y otros costos para ver tu panorama real.",
    "fin.addExpense": "Agregar gasto",
    "fin.editExpense": "Editar gasto",
    "fin.deleteExpense": "Eliminar",
    "fin.expenseTitle": "Título",
    "fin.expenseAmount": "Monto (USD)",
    "fin.expenseCategory": "Categoría",
    "fin.expenseDate": "Fecha",
    "fin.expenseNote": "Nota (opcional)",
    "fin.expenseNotePlaceholder": "Ej. Combustible camioneta #1, compra en Costco, anuncios de Facebook…",
    "fin.expenseTitlePlaceholder": "Ej. Carga de gasolina, balde, Google Ads",
    "fin.save": "Guardar",
    "fin.cancel": "Cancelar",
    "fin.saving": "Guardando…",
    "fin.deleting": "Eliminando…",
    "fin.confirmDelete": "¿Eliminar este gasto? No se puede deshacer.",
    "fin.deletedToast": "Gasto eliminado.",
    "fin.savedToast": "Gasto guardado.",
    "fin.errorTitle": "El título es obligatorio.",
    "fin.errorAmount": "El monto debe ser mayor que 0.",
    "fin.errorCategory": "Selecciona una categoría.",
    "fin.errorDate": "Selecciona una fecha.",
    "fin.errorGeneric": "Algo salió mal. Inténtalo de nuevo.",
    "fin.emptyTitle": "Aún no hay gastos registrados",
    "fin.emptyBody": "Agrega gasolina, suministros o anuncios — y ve tu utilidad real actualizarse al momento.",
    "fin.tableTitle": "Título",
    "fin.tableCategory": "Categoría",
    "fin.tableAmount": "Monto",
    "fin.tableDate": "Fecha",
    "fin.tableNote": "Nota",
    "fin.tableActions": "Acciones",
    "fin.editLabel": "Editar",
    "fin.filterAll": "Todas las categorías",
    "fin.loading": "Cargando…",

    // Categories
    "fin.cat.gas": "Gasolina",
    "fin.cat.supplies": "Suministros de limpieza",
    "fin.cat.ads": "Publicidad",
    "fin.cat.equipment": "Equipo",
    "fin.cat.payroll": "Nómina",
    "fin.cat.insurance": "Seguro",
    "fin.cat.vehicle": "Vehículo",
    "fin.cat.software": "Software",
    "fin.cat.meals": "Comidas",
    "fin.cat.other": "Otro",

    // Card modals — explanations + tips for each financial metric
    "fin.expenseDateHelper": "Usa la fecha bajo la cual quieres contar este gasto. Puedes ingresar gastos diarios, totales mensuales o totales anuales.",
    "fin.modal.revenue.explain": "Ingresos ganados de los clientes marcados como cerrados/completados. Los trabajos agendados no se cuentan hasta que se cierran.",
    "fin.modal.revenue.tip": "Marca un trabajo como cerrado en cuanto termine — ahí es cuando cuenta como ingreso ganado.",
    "fin.modal.expenses.explain": "Gastos del negocio ingresados manualmente. Se usan solo para organización y resúmenes estimados.",
    "fin.modal.expenses.tip": "Registra los gastos a medida que ocurren para que tu utilidad estimada se mantenga precisa.",
    "fin.modal.net.explain": "Ingresos estimados menos los gastos ingresados manualmente. Esto no es asesoría contable ni fiscal.",
    "fin.modal.net.tip": "Mantén actualizados tanto los ingresos como los gastos para obtener la estimación más útil.",
    "fin.modal.potential.explain": "Valor estimado de los trabajos cotizados y agendados que aún no se han cerrado.",
    "fin.modal.potential.tip": "Convierte trabajos cotizados en agendados, y luego en cerrados, para que esto se convierta en ingresos reales.",
    "fin.modal.cancelled.explain": "Valor estimado de los trabajos cancelados que tienen un valor asignado.",
    "fin.modal.cancelled.tip": "Registra el motivo de cancelación en el cliente para detectar patrones y perder menos trabajos con el tiempo.",
    "fin.modal.closed.explain": "Trabajos marcados como cerrados/completados. Los trabajos cerrados cuentan como ingresos ganados.",
    "fin.modal.closed.tip": "Agrega un valor estimado a cada cliente cerrado para que tu número de ingresos esté completo.",

    // Exports & year-end summary
    "fin.exportsTitle": "Exportaciones",
    "fin.exportCsv": "Exportar CSV",
    "fin.downloadYearEnd": "Resumen anual",
    "fin.exportDialogTitle": "Exportar resumen",
    "fin.exportDialogBody": "Elige un rango de fechas. El CSV incluye los ingresos de trabajos cerrados y los gastos — para tus registros y tu contador. Solo estimaciones.",
    "fin.periodThisMonth": "Este mes",
    "fin.periodYtd": "Año en curso",
    "fin.periodCustom": "Rango personalizado",
    "fin.customStart": "Fecha de inicio",
    "fin.customEnd": "Fecha de fin",
    "fin.downloadCsv": "Descargar CSV",
    "fin.downloadJson": "Descargar JSON",
    "fin.exporting": "Exportando…",
    "fin.exportErrorRange": "Selecciona una fecha de inicio y fin.",
    "fin.exportError": "La exportación falló. Inténtalo de nuevo.",
    "fin.yearEndTitle": "Resumen anual",
    "fin.yearEndSubtitle": "Un recuento organizado de tu año — útil para tu contabilidad. Solo estimaciones. No es un documento fiscal.",
    "fin.yearEndYear": "Año",
    "fin.totalRevenue": "Ingresos totales",
    "fin.totalExpenses": "Gastos totales",
    "fin.cancelledJobsLabel": "Trabajos cancelados",
    "fin.topCategoriesTitle": "Principales categorías de gastos",
    "fin.yearEndEmpty": "Aún no hay datos para este año.",
    "fin.yearEndPrint": "Imprimir resumen",
    "fin.close": "Cerrar",

    // Growth AI page — "Hacer Crecer tu Negocio"
    "growth.pageTitle": "Hacer Crecer tu Negocio",
    "growth.pageSubtitle": "Herramientas con IA para conseguir más clientes, reseñas y trabajos recurrentes.",
    "growth.generate": "Generar",
    "growth.generating": "Generando…",
    "growth.regenerate": "Regenerar",
    "growth.copy": "Copiar",
    "growth.copied": "Copiado al portapapeles.",
    "growth.copyFailed": "No se pudo copiar. Selecciona y copia manualmente.",
    "growth.errorGeneric": "La generación con IA falló. Inténtalo de nuevo.",
    "growth.tone": "Tono",
    "growth.tone.friendly": "Amigable",
    "growth.tone.professional": "Profesional",
    "growth.tone.warm": "Cálido",
    "growth.tone.premium": "Premium",
    "growth.tone.promotional": "Promocional",
    "growth.serviceType": "Tipo de servicio",
    "growth.service.deep": "Limpieza profunda",
    "growth.service.moveOut": "Limpieza por mudanza",
    "growth.service.recurring": "Limpieza recurrente",
    "growth.service.office": "Limpieza de oficinas",
    "growth.service.custom": "Personalizado",
    "growth.customService": "Servicio personalizado",
    "growth.customServicePlaceholder": "Ej. Limpieza post-obra",
    "growth.city": "Ciudad",
    "growth.cityPlaceholder": "Ej. Dawsonville, GA",
    // Output language selector (independent from app UI language)
    "growth.outputLanguage": "Idioma del contenido",
    "growth.outputLanguage.en": "Inglés",
    "growth.outputLanguage.es": "Español",
    // Tool 1
    "growth.review.title": "Asistente de respuestas a reseñas",
    "growth.review.subtitle": "Escribe respuestas cálidas y profesionales a las reseñas en segundos.",
    "growth.review.inputLabel": "Reseña del cliente",
    "growth.review.placeholder": "Pega la reseña aquí…",
    "growth.review.errorEmpty": "Pega primero la reseña del cliente.",
    // Tool 2
    "growth.gbp.title": "Asistente de publicaciones de Google Business",
    "growth.gbp.subtitle": "Crea publicaciones locales optimizadas para Google Business con IA.",
    "growth.gbp.offer": "Oferta especial (opcional)",
    "growth.gbp.offerPlaceholder": "Ej. 10% de descuento en tu primera reserva",
    "growth.gbp.errorCity": "Ingresa tu ciudad.",
    "growth.gbp.errorCustom": "Describe tu servicio personalizado.",
    // Tool 3
    "growth.ba.title": "Asistente de contenido antes y después",
    "growth.ba.subtitle": "Genera subtítulos y ganchos para redes con tus trabajos terminados.",
    "growth.ba.platform": "Plataforma",
    "growth.ba.googleBusiness": "Google Business",
    "growth.ba.customerReview": "Reseña del cliente (opcional)",
    "growth.ba.reviewPlaceholder": "Pega una frase corta de un cliente para destacar en el subtítulo…",
    "growth.ba.hookLabel": "Gancho de reel",
    "growth.ba.ctaLabel": "Llamada a la acción",
    "growth.ba.hashtagsLabel": "Hashtags",
    // Tool 4 — Asistente de campaña de referidos
    "growth.ref.title": "Asistente de campaña de referidos",
    "growth.ref.subtitle": "Genera SMS, correo y publicación de Facebook listos para pedir referidos a tus clientes.",
    "growth.ref.businessName": "Nombre del negocio",
    "growth.ref.businessNamePlaceholder": "Por defecto usa tu nombre guardado",
    "growth.ref.offer": "Oferta de referido (opcional)",
    "growth.ref.offerPlaceholder": "Ej. $25 de descuento en su próxima limpieza",
    "growth.ref.smsLabel": "Solicitud de referido por SMS",
    "growth.ref.emailLabel": "Solicitud de referido por correo",
    "growth.ref.emailSubjectLabel": "Asunto del correo",
    "growth.ref.facebookLabel": "Publicación de referidos en Facebook",
    "growth.ref.errorEmpty": "Agrega un poco de contexto antes de generar.",
    // Tool 5 — Asistente de temporada baja
    "growth.slow.title": "Asistente de temporada baja",
    "growth.slow.subtitle": "Obtén 5 acciones concretas y listas para ejecutar y reactivar las reservas durante una temporada lenta.",
    "growth.slow.context": "Contexto del negocio (opcional)",
    "growth.slow.contextPlaceholder": "Ej. Nos enfocamos en limpiezas de salida y recurrentes. Las reservas han bajado las últimas 3 semanas.",
    "growth.slow.cityLabel": "Ciudad (opcional)",
    "growth.slow.ideasLabel": "Ideas",

    // Página de Aceptación SMS (programa solo para dueños del negocio)
    "smsOptIn.pageTitle": "Aceptación SMS — Notificaciones solo para dueños",
    "smsOptIn.subtitle": "Cómo funcionan los SMS transaccionales en Tasklio AI",
    "smsOptIn.intro": "Tasklio AI envía mensajes SMS transaccionales a una única audiencia: los dueños de negocios de limpieza que tienen una cuenta de Tasklio. Nunca enviamos SMS a tus clientes en tu nombre. Los clientes que envían una solicitud a través de tu formulario público no reciben mensajes SMS de Tasklio AI.",
    "smsOptIn.whoTitle": "Quién recibe SMS",
    "smsOptIn.whoBody": "Solo el dueño del negocio de limpieza (el titular de la cuenta de Tasklio). Los clientes finales no reciben SMS de Tasklio AI a través de ningún programa.",
    "smsOptIn.howOptInTitle": "Cómo aceptar",
    "smsOptIn.howOptInBody": "Inicia sesión en tu cuenta de Tasklio, ve a Ajustes → Teléfono para notificaciones SMS, ingresa y guarda tu propio número móvil. Guardar el número es la acción de aceptación. Dejar el campo en blanco desactiva los SMS para tu cuenta.",
    "smsOptIn.whereOptInTitle": "¿Dónde ocurre la aceptación?",
    "smsOptIn.whereOptInBody": "Los titulares de una cuenta de Tasklio aceptan ingresando su propio número de teléfono móvil en Ajustes → Teléfono para notificaciones SMS y guardando los ajustes.",
    "smsOptIn.mockupFieldLabel": "Teléfono para notificaciones SMS",
    "smsOptIn.mockupFieldValue": "+1 404 555 1234",
    "smsOptIn.mockupHelper": "Al guardar un número de teléfono aquí, consientes recibir notificaciones SMS transaccionales de Tasklio AI cuando se envíe o cree un nuevo lead en tu cuenta.",
    "smsOptIn.mockupBullet1": "La frecuencia de los mensajes varía según el volumen de leads.",
    "smsOptIn.mockupBullet2": "Pueden aplicar tarifas de mensajes y datos.",
    "smsOptIn.mockupBullet3": "Responde STOP para darte de baja.",
    "smsOptIn.mockupBullet4": "Responde HELP para obtener ayuda.",
    "smsOptIn.mockupBullet5": "Sin mensajes de marketing ni promocionales.",
    "smsOptIn.mockupCaption": "Solo ilustración — así se ve el campo de aceptación dentro de los Ajustes de tu cuenta de Tasklio.",
    "smsOptIn.mockupBreadcrumb": "Tasklio · Ajustes · Notificaciones",
    "smsOptIn.mockupCheckboxLabel": "Acepto recibir notificaciones SMS transaccionales de Tasklio AI sobre mi cuenta, incluidas alertas de nuevos leads. La frecuencia de los mensajes varía. Pueden aplicar tarifas de mensajes y datos. Responde STOP para darte de baja o HELP para obtener ayuda. Sin mensajes de marketing ni promocionales.",
    "smsOptIn.mockupSaveButton": "Guardar ajustes",
    "smsOptIn.howOptInWorksTitle": "Cómo funciona la aceptación",
    "smsOptIn.howOptInWorksIntro": "La aceptación SMS ocurre detrás del inicio de sesión. Los titulares de una cuenta de Tasklio aceptan yendo a Ajustes → Teléfono para notificaciones SMS, ingresando su propio número móvil, marcando la casilla de consentimiento SMS y guardando los ajustes.",
    "smsOptIn.howOptInPoint1": "El número de teléfono es opcional.",
    "smsOptIn.howOptInPoint2": "La casilla no está marcada por defecto.",
    "smsOptIn.howOptInPoint3": "Los usuarios pueden guardar los ajustes sin activar el SMS.",
    "smsOptIn.howOptInPoint4": "Los clientes y usuarios finales no reciben SMS de Tasklio AI.",
    "smsOptIn.howOptInPoint5": "El SMS es solo para titulares registrados de una cuenta de Tasklio.",
    "smsOptIn.howOptInPoint6": "No se envían mensajes SMS de marketing ni promocionales.",
    "smsOptIn.howOptInPoint7": "Responde STOP para darte de baja o HELP para obtener ayuda.",
    "smsOptIn.howOptInPoint8": "Pueden aplicar tarifas de mensajes y datos.",
    "smsOptIn.messageTypesTitle": "Qué recibirás",
    "smsOptIn.messageType1": "Llegada de un nuevo lead — \"New lead: [Nombre del Cliente] – [Tipo de Servicio]. Call: [Teléfono del Cliente]. Review in your Tasklio account at https://cleaning.tasklio.co.\" Se envía cuando se envía o crea un nuevo lead en tu cuenta de Tasklio.",
    "smsOptIn.frequencyTitle": "Frecuencia",
    "smsOptIn.frequencyBody": "La frecuencia de los mensajes varía según el volumen de leads.",
    "smsOptIn.ratesTitle": "Tarifas",
    "smsOptIn.ratesBody": "Pueden aplicar tarifas de mensajes y datos. Tasklio AI no cobra por los mensajes; pueden aplicar tarifas estándar de tu operador móvil.",
    "smsOptIn.helpStopTitle": "HELP y STOP",
    "smsOptIn.helpStopBody": "Responde HELP en cualquier momento para obtener ayuda. Responde STOP en cualquier momento para darte de baja — recibirás una confirmación final y no más mensajes. También puedes borrar el campo del teléfono para notificaciones SMS en Ajustes para desactivar los SMS de tu cuenta.",
    "smsOptIn.privacyTitle": "Privacidad y sin marketing",
    "smsOptIn.privacyBody": "No se envían mensajes SMS de marketing ni promocionales. Los datos de aceptación de SMS nunca se comparten, venden ni alquilan a terceros con fines de marketing. Compartimos datos solo con Twilio (nuestro proveedor de entrega SMS) estrictamente para entregar los mensajes que has solicitado.",
    "smsOptIn.customerNoteTitle": "¿Y mis clientes?",
    "smsOptIn.customerNoteBody": "Los clientes que envían una solicitud a través de tu formulario público son contactados por ti directamente usando los datos de contacto que proporcionan. Tasklio AI no envía mensajes SMS a tus clientes en tu nombre.",
    "smsOptIn.contactTitle": "¿Preguntas?",
    "smsOptIn.contactBody": "Contacta a support@tasklio.co para cualquier pregunta relacionada con SMS.",
    "smsOptIn.backToHome": "Volver al inicio",
    "smsOptIn.programSummaryTitle": "Resumen del programa SMS",
    "smsOptIn.programSummary1": "Tasklio AI opera un único programa SMS transaccional.",
    "smsOptIn.programSummary2": "Los destinatarios se limitan a titulares de una cuenta de Tasklio (dueños de negocios de limpieza).",
    "smsOptIn.programSummary3": "Los clientes que envían solicitudes de servicio a través de los formularios públicos no están inscritos en el programa SMS.",
    "smsOptIn.programSummary4": "Los clientes no reciben mensajes SMS de Tasklio AI.",
    "smsOptIn.programSummary5": "Los dueños de negocio aceptan voluntariamente ingresando y guardando su propio número móvil en Ajustes → Teléfono para notificaciones SMS.",
    "smsOptIn.programSummary6": "Guardar el número es la acción de aceptación.",
    "smsOptIn.privacyAddon1": "La información móvil no se compartirá con terceros ni afiliados con fines de marketing o promocionales.",
    "smsOptIn.privacyAddon2": "Los datos de consentimiento SMS nunca se venden, alquilan, transfieren, divulgan ni comparten con fines de marketing.",
    "smsOptIn.relatedDocsTitle": "Documentos relacionados",
    "smsOptIn.relatedDocsProgramLabel": "Detalles del programa SMS:",
    "smsOptIn.relatedDocsPrivacyLabel": "Política de privacidad:",
    "smsOptIn.relatedDocsTermsLabel": "Términos y condiciones:",

    // ---- Página pública de reservas (mini-sitio) ----
    "public.hero.getQuote": "Obtén una cotización gratis",
    "public.hero.website": "Sitio web",
    "public.hero.reviews": (n) => `${n} reseñas de Google`,
    "public.about.title": "Sobre nosotros",
    "public.highlights.title": "Por qué elegirnos",
    "public.highlight.insured": "Asegurado",
    "public.highlight.locally_owned": "Negocio local",
    "public.highlight.supplies_included": "Insumos incluidos",
    "public.highlight.pet_friendly": "Acepta mascotas",
    "public.highlight.recurring_available": "Limpieza recurrente disponible",
    "public.highlight.residential_cleaning": "Limpieza residencial",
    "public.highlight.commercial_cleaning": "Limpieza comercial",
    "public.highlight.english_speaking": "Se habla inglés",
    "public.highlight.spanish_speaking": "Se habla español",
    "public.reviews.title": "Reseñas de Google",
    "public.reviews.basedOn": (n) => `Basado en ${n} reseñas de Google`,
    "public.reviews.viewProfile": "Ver perfil de Google",
    "public.cta.title": "¿Listo para un espacio más limpio?",
    "public.cta.subtitle": "Cuéntanos qué necesitas y obtén tu cotización gratis en solo unos minutos.",
    "public.gallery.title": "Nuestro trabajo",
    "public.services.title": "Servicios",
    "public.services.subtitle": "Servicios de limpieza profesionales adaptados a tus necesidades.",
    "public.gallery.photoAlt": (n, total) => `Foto de trabajo ${n} de ${total}`,
    "public.lightbox.close": "Cerrar",
    "public.lightbox.prev": "Foto anterior",
    "public.lightbox.next": "Foto siguiente",
    "public.lightbox.counter": (n, total) => `${n} / ${total}`,

    // ---- Configuración de la página de reservas (dueño) ----
    "bps.title": "Personaliza tu página de reservas",
    "bps.subtitle": "Crea una página profesional que tus clientes pueden visitar para conocer tu negocio y pedir una cotización.",
    "bps.preview": "Vista previa",
    "bps.sectionIntro": "Presentación del negocio",
    "bps.sectionIntroHelp": "Lo primero que ven los clientes en tu página.",
    "bps.businessName": "Nombre del negocio",
    "bps.tagline": "Eslogan",
    "bps.taglinePlaceholder": "Limpieza confiable y profesional para hogares en Atlanta.",
    "bps.taglineEs": "Eslogan — Español (opcional)",
    "bps.taglineEsPlaceholder": "Limpieza confiable y profesional para hogares y negocios.",
    "bps.aboutEs": "Sobre nosotros — Español (opcional)",
    "bps.aboutEsPlaceholder": "Cuéntales a tus clientes sobre tu negocio, en español.",
    "bps.serviceAreaEs": "Área de servicio — Español (opcional)",
    "bps.serviceAreaEsPlaceholder": "Atendiendo Atlanta y áreas cercanas dentro de 30 millas.",
    "bps.esFallbackHint": "Opcional. Se muestra cuando un visitante ve tu página en español. Si se deja en blanco, se muestra el texto en inglés.",
    "settings.weeklyEmailTitle": "Correo semanal de rendimiento",
    "settings.weeklyEmailHelp": "Recibe un resumen semanal de las visitas, solicitudes de cotización y conversión de tu página de reservas.",
    "settings.weeklyEmailSaved": "Preferencia guardada.",
    "settings.lifecycleEmailTitle": "Correos de consejos de configuración",
    "settings.lifecycleEmailHelp": "Correos ocasionales para empezar, con consejos para compartir tu página de reservas y enviar tu primera cotización.",
    "settings.fcTitle": "Listado de tu Negocio de Limpieza",
    "settings.fcHelp": "Haz que tu negocio sea visible para los clientes que buscan servicios de limpieza en tu área.",
    "settings.fcZipLabel": "Código postal del negocio",
    "settings.fcRadiusLabel": "Radio de servicio",
    "settings.fcRadiusPlaceholder": "Selecciona el radio",
    "settings.fcRadius20": "20 millas",
    "settings.fcRadius30": "30 millas",
    "settings.fcToggleLabel": "Mostrar mi negocio en los resultados de búsqueda",
    "settings.fcToggleHelp": "Los clientes que buscan en tu área de servicio pueden descubrir tu negocio cuando esta opción está activada. Puedes desactivarla en cualquier momento.",
    "settings.fcSaved": "Configuración de Find a Cleaner guardada.",
    "settings.fcZipInvalid": "Ingresa un código postal de EE. UU. válido de 5 dígitos.",
    "settings.fcNeedZipRadius": "Agrega un código postal válido y un radio de servicio para aparecer en Find a Cleaner.",
    "fc.title": "Encuentra un profesional de limpieza cerca de ti",
    "fc.subtitle": "Ingresa tu código postal para descubrir profesionales de limpieza de confianza en tu zona y solicita una cotización gratis en minutos.",
    "fc.zipLabel": "Código postal",
    "fc.zipPlaceholder": "Ingresa tu código postal",
    "fc.searchBtn": "Buscar",
    "fc.searching": "Buscando…",
    "fc.zipInvalid": "Ingresa un código postal de EE. UU. válido de 5 dígitos.",
    "fc.tooMany": "Demasiadas búsquedas en poco tiempo. Espera un momento e inténtalo de nuevo.",
    "fc.networkError": "Algo salió mal. Revisa tu conexión e inténtalo de nuevo.",
    "fc.emptyTitle": "Aún no hay profesionales en esta zona",
    "fc.emptyBody": "Aún no hay profesionales de limpieza de Tasklio disponibles en esta zona. Estamos haciendo crecer nuestra red. Prueba con otro código postal cercano o vuelve pronto.",
    "fc.resultsFor": (zip) => `Profesionales de limpieza cerca de ${zip}`,
    "fc.distance": (mi) => `Aprox. a ${mi} millas`,
    "fc.reviews": (n) => `(${n} reseñas)`,
    "fc.getQuote": "Obtén una cotización gratis",
    "fc.distanceNote": "Las distancias son aproximadas, basadas en la ubicación del código postal, no en la distancia en auto.",
    "lead.viaFindACleaner": "Desde Find a Cleaner",
    "nav.findCleaner": "Buscar limpieza",
    "landing.fcBandTitle": "¿Buscas un servicio de limpieza?",
    "landing.fcBandBody": "Encuentra negocios de limpieza que atienden tu código postal y solicita una cotización gratis.",
    "landing.fcBandCta": "Buscar limpieza",
    "dash.mkNewBadge": "Nuevo • Incluido con Tasklio",
    "dash.mkInviteTitle": "Haz que nuevos clientes te descubran",
    "dash.mkInviteBody": "Ayuda a los clientes de tu zona a encontrar tu negocio de limpieza. Define tu área de servicio y empieza a aparecer en las búsquedas locales.",
    "dash.mkFreeNote": "Gratis con tu suscripción",
    "dash.mkIncluded": "Incluido con tu suscripción de Tasklio.",
    "dash.mkEnableCta": "Activar mi listado",
    "dash.mkListedTitle": "Tu negocio está listado",
    "dash.mkListedBody": "Los clientes que buscan en Find a Cleaner cerca de tu área de servicio pueden descubrir tu negocio.",
    "dash.mkViewCta": "Ver el directorio",
    "dash.mkManageCta": "Administrar listado",
    "dash.mkInactiveTitle": "Listado de Find a Cleaner inactivo",
    "dash.mkInactiveBody": "Tu negocio no está visible actualmente en Find a Cleaner. Se requiere una suscripción o prueba activa para aparecer en el directorio.",
    "bps.about": "Sobre tu negocio",
    "bps.aboutPlaceholder": "Cuéntales a tus clientes sobre tu negocio, tu experiencia y qué hace diferente a tu servicio.",
    "bps.sectionArea": "Área de servicio",
    "bps.sectionAreaHelp": "Indica a los clientes dónde trabajas.",
    "bps.city": "Ciudad",
    "bps.state": "Estado",
    "bps.serviceArea": "Descripción del área de servicio",
    "bps.serviceAreaPlaceholder": "Sirviendo a Atlanta y alrededores dentro de 30 millas.",
    "bps.sectionContact": "Contacto público",
    "bps.sectionContactHelp": "Datos de contacto que se muestran a los clientes en tu página pública.",
    "bps.publicPhone": "Teléfono público",
    "bps.publicPhoneHelper": "Este número se muestra a los clientes en tu página pública. Es completamente independiente de tu número privado para notificaciones SMS en Configuración.",
    "bps.website": "URL del sitio web",
    "bps.sectionHighlights": "Puntos destacados",
    "bps.sectionHighlightsHelp": "Selecciona solo los puntos que realmente apliquen a tu negocio. Aparecen bajo \u201cPor qué elegirnos\u201d.",
    "bps.sectionGoogle": "Google Business",
    "bps.sectionGoogleHelp": "Se muestra como un resumen de tu calificación de Google. Ingresa solo valores que coincidan con tu perfil real de Google Business.",
    "bps.googleUrl": "URL del perfil de Google Business",
    "bps.googleRating": "Calificación de Google",
    "bps.googleReviewCount": "Número de reseñas de Google",
    "bps.googleHelper": "Si la calificación o el número de reseñas está vacío, la sección de Google no aparecerá en tu página.",
    "bps.saved": "Página de reservas actualizada",
    "bps.saveError": "No se pudo guardar tu página de reservas. Inténtalo de nuevo.",
    "bps.ratingInvalid": "La calificación de Google debe estar entre 0 y 5.",
    "bps.countInvalid": "El número de reseñas debe ser un número entero positivo.",
    "bps.backToSettings": "Volver a Configuración",
    "bps.sectionPhotos": "Logo y fotos de trabajo",
    "bps.sectionPhotosHelp": "Agrega tu logo y fotos de tu trabajo. Los cambios aquí se guardan al instante.",
    "bps.logo": "Logo del negocio",
    "bps.logoHelper": "Se muestra junto al nombre de tu negocio en tu página pública.",
    "bps.uploadLogo": "Subir logo",
    "bps.replaceLogo": "Reemplazar logo",
    "bps.removeLogo": "Quitar",
    "bps.gallery": "Fotos de trabajo",
    "bps.galleryHelper": "Agrega hasta 6 fotos de tu trabajo.",
    "bps.galleryTip": "Usa fotos claras y bien iluminadas. Las fotos horizontales funcionan mejor.",
    "bps.galleryCount": (n) => `${n} / 6 fotos`,
    "bps.galleryFull": "La galería está llena. Elimina una foto para agregar otra.",
    "bps.addPhoto": "Agregar foto",
    "bps.photoUploaded": "Foto agregada",
    "bps.photoRemoved": "Foto eliminada",
    "bps.logoUpdated": "Logo actualizado",
    "bps.logoRemoved": "Logo eliminado",
    "bps.uploadError": "Error al subir. Inténtalo de nuevo.",
    "bps.invalidImage": "Elige una imagen JPG, PNG o WebP.",
    "bps.imageTooLarge": "La imagen es demasiado grande. El tamaño máximo es 8 MB.",
    "bps.moveLeft": "Mover a la izquierda",
    "bps.moveRight": "Mover a la derecha",
    "bps.deletePhoto": "Eliminar foto",
    "bps.sectionServices": "Servicios que ofreces",
    "bps.sectionServicesHelp": "Elige los servicios de limpieza que los clientes pueden solicitar desde tu página de reservas.",
    "bps.servicesHint": "Solo los servicios marcados aparecen en tu formulario de cotización.",
    "bps.selectAll": "Seleccionar todos",
    "bps.servicesMin": "Selecciona al menos un servicio.",
    "bps.reviewLinkConnected": "Conectado desde Configuración",
    "bps.reviewLinkConnectedHelp": "Esta página de reservas usa el enlace de reseñas de Google ya guardado en tu Configuración de Tasklio.",
    "bps.reviewLinkMissing": "Aún no hay un enlace de reseñas de Google conectado.",
    "bps.reviewLinkMissingHelp": "Agrega tu enlace de reseñas de Google en Configuración para conectarlo a tu página de reservas.",
    "bps.editInSettings": "Editar en Configuración",
    "bps.addInSettings": "Agregar en Configuración",
    "bps.reviewLinkLabel": "Enlace de reseñas de Google",
    "dash.bookingPageCustomize": "Personalizar página",
    "dash.qrButton": "Código QR",
    "dash.qrTitle": "Código QR de tu página de reservas",
    "dash.qrHelp": "Los clientes pueden escanear este código para abrir tu página de reservas. Perfecto para volantes, tarjetas y vehículos.",
    "dash.qrDownload": "Descargar PNG",
    "dash.bpaTitle": "Rendimiento de tu página de reservas",
    "dash.bpaPeriod.today": "Hoy",
    "dash.bpaPeriod.7d": "7 días",
    "dash.bpaPeriod.30d": "30 días",
    "dash.bpaViews": "Visitas a la página",
    "dash.bpaStarts": "Cotizaciones iniciadas",
    "dash.bpaRequests": "Cotizaciones solicitadas",
    "dash.bpaConversion": "Tasa de conversión",
    "dash.bpaEmpty": "Aún no hay actividad en tu página de reservas.",
    "dash.bpaEmptyHint": "Comparte tu página de reservas para empezar a registrar visitas y solicitudes de cotización.",
    "settings.bookingPage": "Personaliza tu página de reservas",
    "settings.bookingPageHelp": "Agrega tu eslogan, sección sobre nosotros, puntos destacados y calificación de Google para que tu página pública destaque.",
    "settings.bookingPageHint": "Convierte tu página de reservas en un pequeño sitio web profesional.",
    "settings.bookingPageButton": "Personalizar página",
  },
};

export const SUPPORTED_LANGUAGES = ["en", "es"];

/**
 * Translate a key. Falls back to English, then to the key string itself.
 * If the dictionary value is a function, call it with `args` so callers can
 * write tr("dash.fromActiveLeads", "es", 4).
 */
export const tr = (key, lang = "en", ...args) => {
  const table = STRINGS[lang] || STRINGS.en;
  const fallback = STRINGS.en;
  const value = (key in table ? table[key] : fallback[key]) ?? key;
  return typeof value === "function" ? value(...args) : value;
};

/**
 * Heuristic: looks at a customer-submitted lead message and guesses if it
 * was written in Spanish. Used ONLY to seed the default reply-language
 * selector — the user can override with one click. Conservative on purpose:
 * we'd rather miss a Spanish lead than mis-default an English one.
 */
export const guessLanguageFromText = (text) => {
  if (!text || typeof text !== "string") return "en";
  const sample = text.toLowerCase().slice(0, 600);
  // Spanish-only diacritics or ¿/¡ are very strong signals.
  if (/[ñ¿¡áéíóúü]/.test(sample)) return "es";
  // Check for several common Spanish stopwords. Two or more = Spanish.
  const tokens = ["hola","gracias","necesito","quiero","limpieza","casa","habitaciones","cuanto","cuánto","puede","puedo","favor","por favor","cuando","cuándo","servicio","cotizar","cotización","mañana","hoy","baño","baños"];
  let hits = 0;
  for (const w of tokens) {
    const re = new RegExp(`\\b${w}\\b`, "i");
    if (re.test(sample)) {
      hits += 1;
      if (hits >= 2) return "es";
    }
  }
  return "en";
};
